import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowUpTrayIcon,
  AdjustmentsHorizontalIcon,
  UserPlusIcon,
  AcademicCapIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import { useStudentRefresh } from "../hooks/useStudentRefresh";
import Notification from "../components/Notification";
import Tooltip from "../components/Tooltip";
import FamilyDiscountDisplay from "../components/FamilyDiscountDisplay";
import {
  validateFile,
  parseFile,
  sanitizeValue,
  getFileType,
} from "../utils/fileParser";
import {
  applyOptimisticUpdate,
  removeStudentOptimistically,
  addStudentOptimistically,
  createOptimisticUpdate,
} from "../utils/optimisticUpdates";

import { getLocalYMDFromUTC, formatTimeTo12Hour } from "../utils/timezone";
import StudentPaymentMethodModal from '../components/students/StudentPaymentMethodModal';
import RecordPaymentModal from '../components/students/RecordPaymentModal';
import PaymentHistoryModal from '../components/students/PaymentHistoryModal';
import ProrationSummary from '../components/students/ProrationSummary';
import BalanceAdjustmentModal from '../components/students/BalanceAdjustmentModal';
import OneTimePaymentTypeModal from '../components/students/OneTimePaymentTypeModal';
import OneTimeCashPaymentModal from '../components/students/OneTimeCashPaymentModal';
import CurriculumProgressModal from '../components/CurriculumProgressModal';
import AttendanceHistoryModal from '../components/AttendanceHistoryModal';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import StripeBillingGate from '../components/StripeBillingGate';
import PlanUpgradeModal from '../components/PlanUpgradeModal';
import { useStudioPlan } from '../hooks/useStudioPlan';
import {
  UPGRADE_REQUIRED_OPTION_VALUE,
  AUTO_BILLING_FILTER_UPGRADE_LABEL,
  AUTO_BILLING_UPGRADE_OPTION_LABEL,
} from '../plans/lockedFeatureConstants';
import { BooleanCapabilityKey } from '../plans/types';

// ============================================
// SECURITY CONSTANTS AND UTILITIES
// ============================================

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  name: 100,
  email: 254,
  alternative_email: 254,
  phone: 20,
  emergency_contact_name: 100,
  emergency_contact_phone: 20,
  relationship_to_student: 50,
  notes: 1000,
  belt: 50,
  gender: 20,
  status: 20,
  staff_role: 50,
  family_id: 50,
  billing_mode: 20,
  search: 200,
};

const SUBMISSION_COOLDOWN = 1000; // 1 second between submissions

// Consolidate buttons are limited to these IDs while they're being tested.
// Keep in sync with backend/src/utils/consolidateAllowlist.ts, which enforces them.
const CONSOLIDATE_ALLOWED_STUDENT_IDS = [
  // Studio 7b8822e8-ec06-4a8f-a71d-d392d9f86e61
  '5d3f1c2e-ccbe-4c64-b47a-92b4122072b9',
  // Studio 166dcb5a-0ae1-4822-9c5b-223d8596aa23
  'c847f8dc-fd96-4efe-a4dc-db20b52f65e8',
  'e069c45f-750d-40ef-947f-bafec865d4a4',
];
const CONSOLIDATE_ALLOWED_FAMILY_IDS = [
  // Studio 166dcb5a-0ae1-4822-9c5b-223d8596aa23
  'family-mhl1akke-8qgy58',
  'family-mhcjbi4z-dht6dp',
  'family-mhcjhfbv-fc406n',
  'family-mhcl1u6b-aidnri',
  'family-mubtim7x-kc85h8',
  'family-mhcjfpus-cjhajp',
  'family-mhckcj2o-7vac9o',
  'family-mhcl15mb-pyxmv3',
  'family-mhclu4m5-jrz9cm',
  'family-ml5cfjcd-s9v5ba',
  'family-ml8n1oao-1zyf5i',
  'family-ml8q5uty-06k7h1',
  // Studio 4a9cedcb-6705-4c5c-8deb-15d7f52d2dd2
  'family-mr2bxtqi-71sibs',
  'family-mt6s5zft-riyx1g',
  'family-mtyry2u2-qgjdv9',
  'family-mup3w35x-h6jsx3',
  'family-mpu27w69-jkk397',
  'family-ms74o0ez-pn87ti',
];

function isConsolidateAllowed(allowedIds: string[], id?: string | null): boolean {
  return !!id && allowedIds.includes(id);
}

type PlanLimitErrorBody = {
  code?: string;
  capability?: string;
  limit?: number;
  current?: number;
  error?: string;
  message?: string;
};

function formatStudentLimitErrorMessage(
  limit: number,
  current: number,
  addCount: number,
  isFreePlan: boolean
): string {
  const remaining = Math.max(0, limit - current);
  const planLabel = isFreePlan ? 'free plan' : 'plan';

  if (addCount > 1) {
    if (remaining === 0) {
      return `Your ${planLabel} allows up to ${limit} students and you already have ${current}. This import has ${addCount} students, so none can be added. Remove students from your file or upgrade from Billing settings to import more.`;
    }
    return `Your ${planLabel} allows up to ${limit} students. You currently have ${current}, so you can add ${remaining} more. This import has ${addCount} students — import ${remaining} or fewer, or upgrade from Billing settings.`;
  }

  if (remaining === 0) {
    return `Your ${planLabel} allows up to ${limit} students and you already have ${current}. Upgrade from Billing settings to add more.`;
  }

  return `Your ${planLabel} allows up to ${limit} students. You currently have ${current} and can add ${remaining} more. Upgrade from Billing settings for a higher limit.`;
}

function isStudentPlanLimitError(data: PlanLimitErrorBody): boolean {
  return data.code === 'plan_limit_reached' && data.capability === 'maxStudents';
}

const EMAIL_INTEGRATION_REQUIRED_MESSAGE =
  'To send a payment link by email, connect your Gmail or Google Workspace account in Settings → Integrations.';

const SMS_INTEGRATION_REQUIRED_MESSAGE =
  'To send a payment link by text, set up your SMS integration in Settings → Integrations.';

async function checkGmailConfigured(authToken: string): Promise<boolean> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/status`,
    {
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );

  if (!response.ok) {
    return false;
  }

  const { isConfigured } = await response.json();
  return !!isConfigured;
}

async function checkSmsConfigured(
  authToken: string,
  studioId: string
): Promise<boolean> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config?studio_id=${studioId}`,
    {
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );

  if (!response.ok) {
    return false;
  }

  const config = await response.json();
  return (
    (config.provider === 'studio_managed' && !!config.fromnumber) ||
    (config.provider === 'own_twilio' && !!config.accountsid) ||
    (!config.provider && !!config.accountsid)
  );
}

function getPaymentLinkSendErrorMessage(
  errorData: {
    error?: string;
    message?: string;
    results?: Array<{ error?: string }>;
  },
  channel: 'email' | 'sms'
): string {
  const combinedError = [
    errorData.error,
    errorData.message,
    ...(errorData.results?.map((result) => result.error) ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    channel === 'email' &&
    (combinedError.includes('gmail not configured') ||
      combinedError.includes('reconnect your gmail'))
  ) {
    return EMAIL_INTEGRATION_REQUIRED_MESSAGE;
  }

  if (
    channel === 'sms' &&
    (combinedError.includes('twilio credentials not configured') ||
      combinedError.includes('twilio credentials not found') ||
      combinedError.includes('sms is not configured'))
  ) {
    return SMS_INTEGRATION_REQUIRED_MESSAGE;
  }

  return (
    errorData.error ||
    errorData.message ||
    (channel === 'email'
      ? 'Failed to send payment link email.'
      : 'Failed to send payment link SMS.')
  );
}

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For text fields, preserve spaces and only remove dangerous content
  if (fieldType === 'notes' || 
      fieldType === 'name' || 
      fieldType === 'emergency_contact_name' || 
      fieldType === 'relationship_to_student' ||
      fieldType === 'staff_role' ||
      fieldType === 'family_id' ||
      // Search keeps spaces as you type ("Alex " then "Alex Rivera").
      fieldType === 'search') {
    
    // Only remove dangerous patterns, preserve ALL spaces (including trailing)
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    
    // DON'T trim for text fields - preserve all spaces including trailing ones
    
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'phone':
    case 'emergency_contact_phone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    case 'name':
    case 'emergency_contact_name':
    case 'relationship_to_student':
    case 'staff_role':
    case 'family_id':
      // Allow letters, spaces, hyphens, apostrophes, periods for names
      // Check for dangerous patterns instead of restrictive allowed characters
      const nameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !nameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'notes':
      // Allow most characters for notes, just exclude dangerous ones
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'belt':
    case 'gender':
    case 'status':
    case 'billing_mode':
      // Allow letters, spaces, hyphens for these fields
      const fieldDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !fieldDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'search':
      // Allow letters, numbers, spaces, hyphens, apostrophes for search
      const searchDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !searchDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

/** Normalize membership_type_ids from student record */
function getMembershipTypeIdsFromStudent(student: {
  membership_type_ids?: string[] | string | null;
}): string[] {
  const ids = student?.membership_type_ids;
  if (Array.isArray(ids)) return ids.filter(Boolean);
  if (typeof ids === "string") {
    try {
      const parsed = JSON.parse(ids);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Use membership_type_ids from students table as source of truth for active membership. */
function hasActiveMembershipFromIds(student: { membership_type_ids?: string[] | string | null }): boolean {
  return getMembershipTypeIdsFromStudent(student).length > 0;
}

/** Student has this membership type in membership_type_ids (active assignment). */
function studentHasActiveMembershipType(
  student: { membership_type_ids?: string[] | string | null },
  membershipTypeId: string
): boolean {
  return getMembershipTypeIdsFromStudent(student).includes(membershipTypeId);
}

/** Curriculum order from belts table (`order`); unknown / missing belts last. */
function getBeltCurriculumRank(
  student: Student,
  belts: { name: string; order?: number }[]
): number {
  if (!belts?.length) return 0;
  if (!student.belt) return 1_000_000;
  const found = belts.find((x) => x.name === student.belt);
  if (found != null && typeof found.order === "number") {
    return found.order;
  }
  return 500_000;
}

const BELT_SORT_CURRICULUM = "curriculum";
const BELT_SORT_CURRICULUM_DESC = "curriculum_desc";
const BELT_SORT_NONE = "none";

function isBeltSortByRank(value: string): boolean {
  return value === BELT_SORT_CURRICULUM || value === BELT_SORT_CURRICULUM_DESC;
}

/** Legacy A–Z belt compare (no longer in UI; kept so stale bundles / HMR cannot throw). */
function compareBeltAlphabetical(
  a: Student,
  b: Student,
  dir: "asc" | "desc"
): number {
  const an = (a.belt || "\uffff").toLowerCase();
  const bn = (b.belt || "\uffff").toLowerCase();
  const c = an < bn ? -1 : an > bn ? 1 : 0;
  return dir === "asc" ? c : -c;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`;
};

// Date input formatter function
const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert YYYY-MM-DD to MM/DD/YYYY for display
const convertDateToDisplayFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  // If already in MM/DD/YYYY format, return as is
  if (dateString.includes('/')) {
    return dateString;
  }
  
  // Convert from YYYY-MM-DD to MM/DD/YYYY using direct string parsing
  try {
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      
      // Validate the parts are numbers
      if (isNaN(Number(year)) || isNaN(Number(month)) || isNaN(Number(day))) {
        return '';
      }
      
      return `${month}/${day}/${year}`;
    }
  } catch {
    return '';
  }
  
  return '';
};

// Convert MM/DD/YYYY to YYYY-MM-DD for storage
const convertDateToStorageFormat = (dateString: string | null | undefined): string | null => {
  if (!dateString || !dateString.trim()) return null;
  
  // If already in YYYY-MM-DD format, return as is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }
  
  // Convert from MM/DD/YYYY to YYYY-MM-DD using direct string parsing
  try {
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      
      // Basic validation - check if parts are numbers and in valid ranges
      const monthNum = Number(month);
      const dayNum = Number(day);
      const yearNum = Number(year);
      
      if (isNaN(monthNum) || isNaN(dayNum) || isNaN(yearNum)) {
        return null;
      }
      
      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || yearNum < 1900 || yearNum > 2100) {
        return null;
      }
      
      return `${year}-${month}-${day}`;
    }
  } catch {
    // If conversion fails, return null
  }
  
  return null;
};

// Max base64 length for student_image (allows ~256px+ JPEG at reasonable quality in Postgres text/json)
const MAX_STUDENT_IMAGE_BASE64_LENGTH = 150_000;

// Image compression before crop UI: downscale large uploads, mild JPEG to limit size without mushy finals
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      const maxSize = 900; // Enough resolution for crop → final avatar without double blur
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressedBase64 = canvas.toDataURL('image/jpeg', 0.88);
      URL.revokeObjectURL(objectUrl);
      resolve(compressedBase64);
    };

    // Without this the promise never settles when the browser cannot decode the file,
    // so the upload hangs with no crop modal and no error. HEIC outside Safari and
    // photos beyond a device's canvas memory limit both land here.
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image could not be decoded'));
    };

    img.src = objectUrl;
  });
};

// Function to create a circular cropped image with zoom and pan
const createCircularCrop = (imageSrc: string, cropX: number, cropY: number, cropSize: number, zoom: number): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    
    img.onload = () => {
      

      
      // Output pixels: large enough for lists/modals without upscaling blur; cropSize comes from caller
      const finalSize = Math.min(512, Math.max(96, cropSize));
      canvas.width = finalSize;
      canvas.height = finalSize;
      
      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(finalSize / 2, finalSize / 2, finalSize / 2, 0, 2 * Math.PI);
      ctx.clip();
      
      // The crop area in the UI is 320x320 pixels
      const uiCropAreaSize = 320;
      
      // Calculate the scale factors between the UI crop area and the actual image
      const scaleX = img.width / uiCropAreaSize;
      const scaleY = img.height / uiCropAreaSize;
      

      
      // The drag position (cropX, cropY) represents the offset of the image within the crop area
      // We need to convert this to source coordinates in the original image
      // The drag offset is in the opposite direction of the source coordinates
      const sourceX = Math.max(0, -cropX * scaleX);
      const sourceY = Math.max(0, -cropY * scaleY);
      
      // When zooming, we need to adjust the source coordinates
      // The zoomed image is larger, so we need to scale the offset accordingly
      const zoomedSourceX = sourceX * zoom;
      const zoomedSourceY = sourceY * zoom;
      

      
      // Calculate the source size to maintain aspect ratio
      // We want to crop a square area from the image, so we use the smaller dimension
      const minDimension = Math.min(img.width, img.height);
      const sourceWidth = minDimension;
      const sourceHeight = minDimension;
      

      
      // Use the zoomed source coordinates for the final crop
      // Allow console overrides for debugging
      const finalSourceX = (window as any).debugSourceX !== undefined ? (window as any).debugSourceX : zoomedSourceX;
      const finalSourceY = (window as any).debugSourceY !== undefined ? (window as any).debugSourceY : zoomedSourceY;
      

      

      
      // Draw the cropped and circular image
      // Draw the cropped and circular image
      ctx.drawImage(
        img,
        finalSourceX / zoom, finalSourceY / zoom, sourceWidth, sourceHeight,
        0, 0, finalSize, finalSize
      );
      
      const circularBase64 = canvas.toDataURL('image/jpeg', 0.88);
      resolve(circularBase64);
    };
    
    img.src = imageSrc;
  });
};

// ============================================
// TYPES
// ============================================
interface FailedPaymentDetails {
  count: number;
}



interface Student {
  id: string;
  student_number?: number;
  name: string;
  email: string | null;
  alternative_email?: string | null;
  phone: string | null;
  age: number | null;
  birthdate: string | null;
  gender: string | null;
  belt: string | null;
  belt_size?: string | null;
  status: "Active" | "Inactive";
  join_date: string | null;
  last_checked_in: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  relationship_to_student: string | null;
  membership_type: string | null;
  notes: string | null;
  ai_drop_off_risk: number;
  studio_id: string;
  stripe_customer_id?: string;
  overdue_amount?: number;
  has_payment_method?: boolean;
  stripe_subscription_id?: string | string[];
  subscription_cancel_at?: string;
  subscription_status?: string;
  /** Upcoming "cancel at period end" memberships (from the students list). */
  membership_cancellations?: Array<{ membership_type_id: string; cancel_at: string }>;
  stripe_payment_method_id?: string;
  waiver_signed?: boolean;
  belt_last_changed?: string | null;
  student_image?: string | null;
  assigned_classes?: string[];
  membership_type_ids?: string[];
  family_id?: string | null;
  billing_mode?: string | null;
  billing_guardian_id?: string | null;
  manual_payment_expires?: string | null;
  subscription_details?: Array<{
    id: string;
    status: string;
    membership_type_id: string;
    membership_name: string;
    current_period_start: number | null;
    current_period_end: number | null;
    calculated_billing_period_end: number | null;
    cancel_at_period_end: boolean;
    cancel_at?: number | null;
    isCanceling?: boolean;
    /** Already taken off the Stripe subscription before renewal; paid through cancel_at. */
    removed_from_billing?: boolean;
  }>;
}

interface FamilyMemberSummary {
  id: string;
  name: string;
  billing_mode?: string | null;
  billing_guardian_id?: string | null;
  subscription_status?: string | null;
  membership_type_ids?: string[];
  created_at?: string;
}

interface ExportField {
  key: keyof Student;
  label: string;
  selected: boolean;
}

interface ImportField {
  key: keyof Student;
  label: string;
  csvColumn: string;
}

interface FormData {
  name: string;
  email: string | null;
  alternative_email?: string | null;
  phone: string | null;
  age: string | null;
  birthdate: string | null;
  gender: string | null;
  belt: string | null;
  belt_size?: string | null;
  status: "Active" | "Inactive";
  join_date: string;
  last_checked_in: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  relationship_to_student: string | null;
  membership_type: string | null;
  membership_type_ids?: string[];
  notes: string | null;
  ai_drop_off_risk: number;
  studio_id: string;
  waiver_signed?: boolean;
  staff_role?: string | null;
  student_image?: string | null;
  assigned_classes: string[];
  family_id?: string | null;
  billing_mode?: string | null;
  billing_guardian_id?: string | null;
  cancelImmediately?: boolean;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Whether a student is billed automatically through Stripe. Everything that is not
 * cash or manual is automatic, including family members billed through a primary.
 */
const isAutoBillingMode = (mode?: string | null): boolean =>
  mode !== "cash" && mode !== "manual";

/**
 * Whether a family member is their family's billing primary.
 *
 * `individual` and `auto` are two stored spellings of the same "pays for
 * themselves" state, and the backend accepts both everywhere it resolves a primary
 * (see wasFamilyBillingPrimary). Checking only one of them silently fails to find
 * primaries stored as the other.
 */
const isFamilyBillingPrimaryMember = (member: {
  billing_mode?: string | null;
  billing_guardian_id?: string | null;
}): boolean =>
  ["individual", "auto"].includes(member.billing_mode || "") &&
  !member.billing_guardian_id;

/**
 * Whether a family member occupies a family-discount position.
 *
 * Must stay in step with countsTowardFamilyDiscountPosition on the server, which
 * decides the discount actually charged. `trialing` and `past_due` count so that a
 * temporary card failure does not appear to reshuffle everyone's discount tier.
 */
const countsTowardFamilyDiscountPosition = (member: {
  membership_type_ids?: string[] | null;
  billing_mode?: string | null;
  subscription_status?: string | null;
}): boolean => {
  const hasMemberships =
    Array.isArray(member.membership_type_ids) &&
    member.membership_type_ids.length > 0;
  if (!hasMemberships) return false;

  const isCashOrManual =
    member.billing_mode === "cash" || member.billing_mode === "manual";

  return (
    isCashOrManual ||
    ["active", "trialing", "past_due"].includes(member.subscription_status || "")
  );
};

// Helper function to check if a student has subscription IDs
const hasSubscriptionId = (student: Student): boolean => {
  if (!student.stripe_subscription_id) return false;
  return Array.isArray(student.stripe_subscription_id) 
    ? student.stripe_subscription_id.length > 0 
    : Boolean(student.stripe_subscription_id);
};

// Helper function to apply optimistic updates with type conversion
const applyOptimisticUpdateWithTypes = (students: Student[], update: any): Student[] => {
  return students.map(student => 
    student.id === update.studentId 
      ? { ...student, ...update.updates }
      : student
  );
};

// Helper function to truncate email addresses (matches Dashboard High Risk Students)
/** Days since the last check-in; within this many days counts as attending. */
const RECENT_CHECK_IN_DAYS = 14;

const daysSince = (iso: string): number =>
  Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));

type MembershipStatusKey = "active" | "cancelling" | "paused" | "past_due" | "cash" | "none";

const MEMBERSHIP_STATUS_STYLES: Record<MembershipStatusKey, string> = {
  active: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400",
  cancelling: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300",
  paused: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300",
  past_due: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400",
  cash: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
  none: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300",
};

/** MM/DD/YY, e.g. 10/31/27 */
const formatShortNumericDate = (date: Date): string =>
  date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });

const formatShortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/**
 * Membership Status for the students table: billing state, not attendance.
 * `note` flags a membership that's ending while others continue.
 */
const getMembershipStatus = (
  student: Pick<Student, "billing_mode" | "subscription_status" | "membership_type_ids" | "subscription_cancel_at" | "membership_cancellations">
): { key: MembershipStatusKey; label: string; note?: string } => {
  const memberships = student.membership_type_ids || [];
  const now = Date.now();
  const upcoming = (student.membership_cancellations || []).filter(
    (c) => new Date(c.cancel_at).getTime() > now
  );
  const studentCancelAt =
    student.subscription_cancel_at && new Date(student.subscription_cancel_at).getTime() > now
      ? student.subscription_cancel_at
      : null;
  const cancelDates = [...upcoming.map((c) => c.cancel_at), ...(studentCancelAt ? [studentCancelAt] : [])].sort();

  if (student.billing_mode === "cash" || student.billing_mode === "manual") {
    return memberships.length ? { key: "cash", label: "Cash / Manual" } : { key: "none", label: "No membership" };
  }
  const status = (student.subscription_status || "").toLowerCase();
  if (status === "paused") return { key: "paused", label: "Paused" };
  if (["past_due", "unpaid", "incomplete", "declined"].includes(status)) {
    return { key: "past_due", label: "Past due" };
  }

  const cancellingIds = new Set(upcoming.map((c) => c.membership_type_id));
  const continuing = memberships.filter((id) => !cancellingIds.has(id));
  if (continuing.length === 0 && cancelDates.length > 0) {
    return { key: "cancelling", label: `Cancels ${formatShortDate(cancelDates[0])}` };
  }
  if (continuing.length > 0) {
    // Individual billing drops an ending membership from membership_type_ids,
    // so count the ending ones from the cancellations list.
    const ending = Math.max(upcoming.length, cancelDates.length ? 1 : 0);
    return {
      key: "active",
      label: "Active",
      ...(ending ? { note: `${ending} cancel${ending === 1 ? "s" : ""} ${formatShortDate(cancelDates[0])}` } : {}),
    };
  }
  return { key: "none", label: "No membership" };
};

const truncateEmail = (email: string | null | undefined, maxLength: number = 25): string => {
  if (!email) return "-";
  if (email.length <= maxLength) return email;
  return email.substring(0, maxLength - 3) + "...";
};

// ============================================
// CUSTOM HOOKS
// ============================================

// Consolidated data fetching hook
const useStudentData = ({
  page,
  limit,
  searchTerm,
  genderFilter,
  statusFilter,
  waiverFilter,
  sortField,
  sortDirection,
}: {
  page: number;
  limit: number;
  searchTerm: string;
  genderFilter: string;
  statusFilter: string;
  waiverFilter: string;
  sortField: keyof Student;
  sortDirection: "asc" | "desc";
}) => {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const [isLoading, setIsLoading] = useState(true);
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [canUseStripeBilling, setCanUseStripeBilling] = useState(true);
  const [hasPaymentIntegration, setHasPaymentIntegration] = useState(false);
  const [classes, setClasses] = useState<any[]>([]);
  const [paymentIntegrationLoading, setPaymentIntegrationLoading] =
    useState(true);
  const [studioId, setStudioId] = useState<string | null>(null);
  const [studioName, setStudioName] = useState<string>("");

  const [data, setData] = useState<{
    students: Student[];
    totalStudentsCount: number;
    belts: any[];
    memberships: any[];
    productDescriptions: any[];
    lastCheckedInMap: Record<string, string>;
    staffRoles: any[];
    classes: any[];
    studioTimezone: string;
  } | null>(null);

  const fetchAllData = useCallback(async () => {
    if (!studioUser?.studio_id || !getAuthToken()) return null;

    setPaymentIntegrationLoading(true);
    setIsLoading(true);

    try {
      // Build query parameters for students fetch
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchTerm,
        gender: genderFilter === "all" ? "" : genderFilter,
        status: statusFilter === "all" ? "" : statusFilter,
        waiver: waiverFilter === "all" ? "" : waiverFilter,
        sortField,
        sortDirection,
      });

      // Fetch all data in parallel
      const [
        studioResponse,
        studentsResponse,
        beltsResponse,
        membershipsResponse,
        productDescriptionsResponse,
        lastCheckedInResponse,
        staffRolesResponse,
        classesResponse,
      ] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser.studio_id}?${params}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioUser.studio_id}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/memberships/${studioUser.studio_id}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/product-descriptions/${studioUser.studio_id}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/last-checked-in/${studioUser.studio_id}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-roles/${studioUser.studio_id}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${studioUser.studio_id}`,
          {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }
        ),
      ]);

      // Process studio data
      const studioData = await studioResponse.json();
      setStudioId(studioData.id);
      setStudioName(studioData.name || "");
      const hasStripe = !!studioData.stripe_user_id;
      setIsStripeConnected(hasStripe);
      setCanUseStripeBilling(
        studioData.plan?.capabilities?.stripeBilling ?? true
      );
      setHasPaymentIntegration(hasStripe);

      // Process all responses
      const [
        studentsResult,
        beltsData,
        membershipsData,
        productDescriptionsData,
        lastCheckedInData,
        staffRolesData,
        classesData,
      ] = await Promise.all([
        studentsResponse.json(),
        beltsResponse.ok ? beltsResponse.json() : [],
        membershipsResponse.ok ? membershipsResponse.json() : [],
        productDescriptionsResponse.ok
          ? productDescriptionsResponse.json()
          : [],
        lastCheckedInResponse.ok ? lastCheckedInResponse.json() : {},
        staffRolesResponse.ok ? staffRolesResponse.json() : [],
        classesResponse.ok ? classesResponse.json() : [],
      ]);

      setPaymentIntegrationLoading(false);
      setIsLoading(false);

      const result = {
        students: studentsResult.data || [],
        totalStudentsCount: studentsResult.pagination?.total || 0,
        belts: beltsData || [],
        memberships: membershipsData || [],
        productDescriptions: productDescriptionsData || [],
        lastCheckedInMap: lastCheckedInData || {},
        staffRoles: staffRolesData || [],
        classes: classesData || [],
        studioTimezone: studioData.timezone || "America/New_York",
      };

      if (hasStripe && result.students.length > 0) {
        const autoBillingStudentIds = result.students
          .filter(
            (student: Student) =>
              student.billing_mode !== "cash" &&
              student.billing_mode !== "manual"
          )
          .map((student: Student) => student.id)
          .slice(0, 25);

        if (autoBillingStudentIds.length > 0) {
          void fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/sync-billing-batch`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getAuthToken()}`,
              },
              body: JSON.stringify({ student_ids: autoBillingStudentIds }),
            }
          )
            .then(async (syncResponse) => {
              if (!syncResponse.ok) return null;
              return fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser.studio_id}?${params}`,
                {
                  headers: { Authorization: `Bearer ${getAuthToken()}` },
                }
              );
            })
            .then(async (studentsRefreshResponse) => {
              if (!studentsRefreshResponse?.ok) return null;
              const refreshedStudentsResult =
                await studentsRefreshResponse.json();
              return {
                ...result,
                students: refreshedStudentsResult.data || result.students,
                totalStudentsCount:
                  refreshedStudentsResult.pagination?.total ||
                  result.totalStudentsCount,
              };
            })
            .then((refreshedResult) => {
              if (refreshedResult) {
                setData(refreshedResult);
              }
            })
            .catch((syncError) => {
              console.warn(
                "Background student billing sync failed:",
                syncError
              );
            });
        }
      }

      return result;
    } catch (error) {
      console.error("Error fetching data:", error);
      setPaymentIntegrationLoading(false);
      setIsLoading(false);
      return null;
    }
  }, [
    studioUser?.studio_id,
    getAuthToken,
    page,
    limit,
    searchTerm,
    genderFilter,
    statusFilter,
    waiverFilter,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchAllData().then(setData);
  }, [fetchAllData]);

  return {
    students: data?.students || [],
    belts: data?.belts || [],
    memberships: data?.memberships || [],
    productDescriptions: data?.productDescriptions || [],
    lastCheckedInMap: data?.lastCheckedInMap || {},
    staffRoles: data?.staffRoles || [],
    classes: data?.classes || [],
    isLoading,
    isStripeConnected,
    canUseStripeBilling,
    hasPaymentIntegration,
    paymentIntegrationLoading,
    totalStudentsCount: data?.totalStudentsCount || 0,
    studioId,
    studioName,
    studioTimezone: data?.studioTimezone || "America/New_York",
    refreshData: () => fetchAllData().then(setData),
  };
};

  // Form management hook
  const useStudentForm = (
    editingStudent?: Student | null,
    memberships?: any[],
    staffRoles?: any[],
    classes?: any[],
    membershipLoading?: boolean
  ) => {
    const { studioUser } = useStudioUser();
    const { getAuthToken } = useAuth();

  // Keep track of which students we've already checked to prevent repeated calls
  const checkedStaffRolesRef = useRef<Set<string>>(new Set());

  // Function to check if student is already a staff member
  const checkStudentStaffRole = useCallback(async (studentId: string): Promise<string | null> => {
    // Don't check if we've already checked this student
    if (checkedStaffRolesRef.current.has(studentId)) {
      return null;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        return null;
      }

      // Mark as checked before making the call to prevent duplicate requests
      checkedStaffRolesRef.current.add(studentId);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/student/${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const staffData = await response.json();
        return staffData?.role || null;
      }
      return null;
    } catch (error) {
      console.warn("Failed to check student staff role:", error);
      // Remove from checked set if there was an error, so we can retry later
      checkedStaffRolesRef.current.delete(studentId);
      return null;
    }
  }, [getAuthToken]);

  const getInitialFormData = useCallback((): FormData => {
    // If memberships are still loading, return a basic form structure without membership data
    if (membershipLoading) {
      return {
        name: editingStudent?.name || "",
        email: editingStudent?.email || null,
        alternative_email: editingStudent?.alternative_email || null,
        phone: editingStudent?.phone || null,
        age: editingStudent?.age?.toString() || null,
        birthdate: editingStudent?.birthdate ? convertDateToDisplayFormat(editingStudent.birthdate) : "",
        gender: editingStudent?.gender || null,
        belt: editingStudent?.belt || null,
        belt_size: (editingStudent as any)?.belt_size || null,
        status: editingStudent?.status || "Active",
        join_date: editingStudent?.join_date || new Date().toISOString().split("T")[0],
        last_checked_in: editingStudent?.last_checked_in || null,
        emergency_contact_name: editingStudent?.emergency_contact_name || null,
        emergency_contact_phone: editingStudent?.emergency_contact_phone || null,
        relationship_to_student: editingStudent?.relationship_to_student || null,
        membership_type: null,
        membership_type_ids: [],
        notes: editingStudent?.notes || null,
        ai_drop_off_risk: editingStudent?.ai_drop_off_risk || 0,
        studio_id: studioUser?.studio_id || "",
        waiver_signed: editingStudent?.waiver_signed || false,
        staff_role: null,
        student_image: editingStudent?.student_image || null,
        assigned_classes: editingStudent?.assigned_classes || [],
        family_id: editingStudent?.family_id || null,
        billing_mode: editingStudent?.billing_mode || null,
        billing_guardian_id: editingStudent?.billing_guardian_id || null,
        cancelImmediately: false,
      };
    }

    if (editingStudent && memberships) {

      // Use stored membership_type_ids directly, fallback to subscription details, then parsing names
      let selectedMembershipIds: string[] = [];
      
      if (editingStudent.membership_type_ids && Array.isArray(editingStudent.membership_type_ids) && editingStudent.membership_type_ids.length > 0) {
        selectedMembershipIds = [...editingStudent.membership_type_ids]; // Create a copy
      } else if (editingStudent.subscription_details && editingStudent.subscription_details.length > 0) {
        // Use subscription details to determine active memberships
        selectedMembershipIds = editingStudent.subscription_details
          .filter(sub => sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due' || sub.status === 'canceling' || sub.status === 'paused' || sub.isCanceling)
          .map(sub => sub.membership_type_id)
          .filter(Boolean);
      } else if (editingStudent.membership_type) {
        // Fallback: parse names for legacy data
        const names = editingStudent.membership_type
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean);
        const byName = new Map(
          memberships.map((m: any) => [String(m.name).trim(), m.id])
        );
        selectedMembershipIds = names
          .map((name) => byName.get(name))
          .filter((id): id is string => Boolean(id));
      }


      const firstMembershipId = selectedMembershipIds[0] || null;

      const initialData: FormData = {
        name: editingStudent.name,
        email: editingStudent.email,
        alternative_email: editingStudent.alternative_email || null,
        phone: editingStudent.phone,
        age: editingStudent.age?.toString() || null,
        birthdate: editingStudent.birthdate ? convertDateToDisplayFormat(editingStudent.birthdate) : null,
        gender: editingStudent.gender,
        belt: editingStudent.belt,
        belt_size: (editingStudent as any)?.belt_size || null,
        status: editingStudent.status,
        join_date:
          editingStudent.join_date || new Date().toISOString().split("T")[0],
        last_checked_in: editingStudent.last_checked_in,
        emergency_contact_name: editingStudent.emergency_contact_name,
        emergency_contact_phone: editingStudent.emergency_contact_phone,
        relationship_to_student: editingStudent.relationship_to_student,
        membership_type: firstMembershipId,
        membership_type_ids: selectedMembershipIds,
        notes: editingStudent.notes,
        ai_drop_off_risk: editingStudent.ai_drop_off_risk,
        studio_id: studioUser?.studio_id || "",
        waiver_signed: editingStudent.waiver_signed,
        staff_role: null, // Will be populated asynchronously
        student_image: editingStudent.student_image,
        assigned_classes: editingStudent.assigned_classes || [],
        family_id: editingStudent.family_id || null,
        billing_mode: editingStudent.billing_mode || 'individual',
        billing_guardian_id: editingStudent.billing_guardian_id || null,
      };

      return initialData;
    }

    return {
      name: "",
      email: null,
      alternative_email: null,
      phone: null,
      age: null,
      birthdate: null,
      gender: null,
      belt: null,
      belt_size: null,
      status: "Active",
      join_date: new Date().toISOString().split("T")[0],
      last_checked_in: null,
      emergency_contact_name: null,
      emergency_contact_phone: null,
      relationship_to_student: null,
      membership_type: null,
      membership_type_ids: [],
      notes: null,
      ai_drop_off_risk: 0,
      studio_id: studioUser?.studio_id || "",
      waiver_signed: false,
      staff_role: null,
      student_image: null,
      assigned_classes: [],
      family_id: null,
      billing_mode: 'individual',
      billing_guardian_id: null,
    };
  }, [editingStudent, memberships, studioUser]);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: null,
    alternative_email: null,
    phone: null,
    age: null,
    birthdate: null,
    gender: null,
    belt: null,
    belt_size: null,
    status: "Active",
    join_date: new Date().toISOString().split("T")[0],
    last_checked_in: null,
    emergency_contact_name: null,
    emergency_contact_phone: null,
    relationship_to_student: null,
    membership_type: null,
    membership_type_ids: [],
    notes: null,
    ai_drop_off_risk: 0,
    studio_id: studioUser?.studio_id || "",
    waiver_signed: false,
    staff_role: null,
    student_image: null,
    assigned_classes: [],
    family_id: null,
    billing_mode: 'individual',
    billing_guardian_id: null,
  });
  const [isUpdatingFromSave, setIsUpdatingFromSave] = useState(false);
  const [familyIdDirty, setFamilyIdDirty] = useState(false);

  const markFamilyIdDirty = useCallback(() => {
    setFamilyIdDirty(true);
  }, []);

  const clearFamilyIdDirty = useCallback(() => {
    setFamilyIdDirty(false);
  }, []);



  // Track when we've initialized the form to prevent re-initialization
  const [hasInitialized, setHasInitialized] = useState(false);
  // Track when user is actively making membership changes
  const [userMakingMembershipChanges, setUserMakingMembershipChanges] = useState(false);
  // Preserve an explicit billing-mode selection while background student data loads.
  const [billingModeDirty, setBillingModeDirty] = useState(false);
  // Track family discount percentage for cash/manual billing students
  const [familyDiscountPercent, setFamilyDiscountPercent] = useState<number>(0);
  
  // Track previous editingStudent ID to detect when a new student is loaded
  const prevEditingStudentIdRef = useRef<string | null>(null);

  // Which student formData currently holds. editingStudent is set a render before the
  // form is repopulated below, and anything reading a field from each would otherwise
  // compare the incoming student against the outgoing student's form values.
  const [formStudentId, setFormStudentId] = useState<string | null>(null);
  
  useEffect(() => {
    // Check if this is a new student (ID changed from previous)
    const isNewStudent = editingStudent?.id && editingStudent.id !== prevEditingStudentIdRef.current;
    
    // Only update form data when:
    // 1. EditingStudent exists AND not updating from save AND (not initialized OR new student)
    if (editingStudent && !isUpdatingFromSave && (!hasInitialized || isNewStudent)) {
      const initialData = getInitialFormData();
      setFormData(initialData);
      setFormStudentId(editingStudent.id ?? null);
      setHasInitialized(true);

      if (isNewStudent) {
        setBillingModeDirty(false);
      }
      
      // Update the ref to track this student
      if (editingStudent.id) {
        prevEditingStudentIdRef.current = editingStudent.id;
        
        // Check if student is already a staff member and update staff_role (only once per student)
        checkStudentStaffRole(editingStudent.id).then(staffRole => {
          // Always update the staff_role, even if it's null (to clear it)
          setFormData(prev => ({ ...prev, staff_role: staffRole }));
        }).catch(error => {
          console.warn("Failed to check student staff role:", error);
          // Clear staff_role on error
          setFormData(prev => ({ ...prev, staff_role: null }));
        });
      }
    }
    // Reset the flag only after we've processed the data (not on every render)
    if (isUpdatingFromSave) {
      setIsUpdatingFromSave(false);
    }
    // Reset initialization flag only when editing student changes to null (modal closed)
    if (!editingStudent) {
      setHasInitialized(false);
      setUserMakingMembershipChanges(false);
      setBillingModeDirty(false);
      setFormStudentId(null);
      prevEditingStudentIdRef.current = null;
    }
  }, [editingStudent?.id, getInitialFormData, isUpdatingFromSave, hasInitialized, checkStudentStaffRole]);

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
  };

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value, type } = e.target;
      let newValue: string | boolean = value;
      
      if (type === "checkbox") {
        newValue = (e.target as HTMLInputElement).checked;
      } else if (name === "phone" || name === "emergency_contact_phone") {
        // Sanitize and format phone numbers
        const sanitized = sanitizeInput(value, name);
        newValue = formatPhoneNumber(sanitized);
        
        // Validate phone format
        if (newValue && !validateInput(newValue, name)) {
          logSecurityEvent('INVALID_PHONE_INPUT', { field: name, value: newValue });
        }
      } else if (name === "birthdate") {
        // Sanitize and format date
        const sanitized = sanitizeInput(value, name);
        newValue = formatDateInput(sanitized);
      } else if (typeof value === 'string') {
        // Sanitize all string inputs
        newValue = sanitizeInput(value, name);
        
        // Validate input if it's a known field type
        if (newValue && MAX_LENGTHS[name]) {
          if (!validateInput(newValue, name)) {
            logSecurityEvent('INVALID_INPUT', { field: name, value: newValue });
          }
          
          // Enforce length limits
          if (newValue.length > MAX_LENGTHS[name]) {
            newValue = newValue.substring(0, MAX_LENGTHS[name]);
          }
        }
      }

      if (name === "family_id") {
        setFamilyIdDirty(true);
      }

      setFormData((prev) => {
        const normalizedValue =
          name === "family_id" && typeof newValue === "string" && !newValue.trim()
            ? null
            : newValue;
        const updated = { ...prev, [name]: normalizedValue };
        return updated;
      });
    },
    []
  );

  const handleClassToggle = useCallback((classId: string) => {
    setFormData((prev) => {
      const isSelected = prev.assigned_classes.includes(classId);
      const updatedClasses = isSelected
        ? prev.assigned_classes.filter((id) => id !== classId)
        : [...prev.assigned_classes, classId];
      return { ...prev, assigned_classes: updatedClasses };
    });
  }, []);

  const resetForm = useCallback(() => {
    const initialData = getInitialFormData();
    if (!familyIdDirty && !billingModeDirty) {
      setFormData(initialData);
      setFamilyDiscountPercent(0);
      return;
    }

    setFormData((prev) => ({
      ...initialData,
      ...(familyIdDirty ? { family_id: prev.family_id ?? null } : {}),
      billing_mode: prev.billing_mode ?? initialData.billing_mode,
      billing_guardian_id: prev.billing_guardian_id ?? null,
    }));
  }, [getInitialFormData, familyIdDirty, billingModeDirty]);

  const calculateAge = (birthdate: string | undefined): number | null => {
    if (!birthdate) return null;
    
    // Convert MM/DD/YYYY to YYYY-MM-DD if needed for consistent parsing
    const dateString = birthdate.includes('/') 
      ? (convertDateToStorageFormat(birthdate) ?? birthdate)
      : birthdate;
    
    if (!dateString) return null;
    
    const today = new Date();
    const birthDate = new Date(dateString);
    if (isNaN(birthDate.getTime())) return null;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const clearCheckedStaffRoles = useCallback(() => {
    checkedStaffRolesRef.current.clear();
  }, []);

  return {
    formData,
    setFormData,
    formStudentId,
    setIsUpdatingFromSave,
    handleInputChange,
    handleClassToggle,
    resetForm,
    calculateAge,
    formatPhoneNumber,
    clearCheckedStaffRoles,
    userMakingMembershipChanges,
    setUserMakingMembershipChanges,
    setBillingModeDirty,
    familyDiscountPercent,
    setFamilyDiscountPercent,
    markFamilyIdDirty,
    clearFamilyIdDirty,
    familyIdDirty,
  };
};

// Import/Export utilities hook
const useImportExport = () => {
  const exportStudents = useCallback(
    (students: Student[], selectedFields: ExportField[]) => {
      const fields = selectedFields.filter((f) => f.selected);
      const headers = fields.map((f) => f.label);
      const data = students.map((student) =>
        fields.map((field) => student[field.key])
      );

      const csvContent = [
        headers.join(","),
        ...data.map((row) => row.join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `students_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.click();
    },
    []
  );
// Note test
  const downloadTemplate = useCallback(() => {
    const headers = [
      "Name",
      "Email",
      "Phone",
      // "Age", // Removed - age is calculated from Date of Birth
      "Date of Birth",
      "Gender",
      "Belt",
      "Join Date",
      "Status",
      "Retention Risk",
      "Emergency Contact Name",
      "Emergency Contact Phone",
      "Relationship to Student",
      "Membership Type",
      "Notes",
    ];
    const sampleData = [
      [
        "John Smith",
        "john@example.com",
        "(555) 123-4567",
        // "25", // Removed - age is calculated from Date of Birth
        "1998-05-20",
        "Male",
        "Blue",
        "2023-01-15",
        "Active",
        "0.15",
        "Jane Smith",
        "(555) 987-6543",
        "Mother",
        "Monthly",
        '"Regular attendee"',
      ],
    ];
    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "student_import_template.csv");
    link.click();
  }, []);

  return { exportStudents, downloadTemplate };
};

// ============================================
// COMPONENTS
// ============================================

// Student table row component

const StudentTableRow: React.FC<{
  student: Student;
  lastCheckedIn: string | undefined;
  cardExpiration?: { expirationDate: string; daysUntilExpiration: number } | undefined;
  onEdit: (student: Student) => void;
  onDelete: (student: Student) => void;
  formatPhoneNumber: (phone: string | null | undefined) => string;
  riskThresholds: { medium: number; high: number };
  showDropoffRisk?: boolean;
  isSelectMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (studentId: string) => void;
  onRecordPayment?: (student: Student) => void;
  onViewPaymentHistory?: (student: Student) => void;
  onOpenCurriculum?: (student: Student) => void;
  onOpenAttendanceHistory?: (student: Student) => void;
}> = ({
  student,
  lastCheckedIn,
  cardExpiration,
  onEdit,
  onDelete,
  formatPhoneNumber,
  riskThresholds,
  showDropoffRisk = true,
  isSelectMode = false,
  isSelected = false,
  onToggleSelection,
  onRecordPayment,
  onViewPaymentHistory,
  onOpenCurriculum,
  onOpenAttendanceHistory,
}) => {

  return (
    <tr className={isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
      <td className="whitespace-nowrap py-4 pl-0 text-sm font-medium text-gray-900 dark:text-white">
        <div className="flex items-center gap-3">
          {isSelectMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelection?.(student.id)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
          )}
          {student.student_image ? (
            <img
              src={student.student_image}
              alt={`${student.name} photo`}
              className="h-16 w-16 rounded-full object-cover border border-gray-300 dark:border-gray-600"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
              <UserIcon className="h-8 w-8 text-gray-500 dark:text-gray-400" />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span>{student.name}</span>
            </div>
            
            {/* Payment Expiration Warning for Credit Card - 60-day countdown, same styling as cash */}
            {cardExpiration && (
              (() => {
                const daysUntilExpiration = cardExpiration.daysUntilExpiration;
                const expiresDate = new Date(cardExpiration.expirationDate);
                if (daysUntilExpiration < 0) {
                  return (
                    <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                      ⚠️ Payment expired {Math.abs(daysUntilExpiration)} days ago
                    </span>
                  );
                } else if (daysUntilExpiration <= 60) {
                  return (
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      ⚠️ Payment expires in {daysUntilExpiration} days
                    </span>
                  );
                } else {
                  return (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Card valid until {formatShortNumericDate(expiresDate)}
                    </span>
                  );
                }
              })()
            )}
            
            {/* Payment Expiration Warning for Manual Billing */}
            {(student.billing_mode === 'manual' || student.billing_mode === 'cash') && student.manual_payment_expires && (
              (() => {
                const expiresDate = new Date(student.manual_payment_expires);
                const today = new Date();
                const daysUntilExpiration = Math.ceil((expiresDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                
                if (daysUntilExpiration < 0) {
                  return (
                    <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                      ⚠️ Payment expired {Math.abs(daysUntilExpiration)} days ago
                    </span>
                  );
                } else if (daysUntilExpiration <= 7) {
                  return (
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      ⚠️ Payment expires in {daysUntilExpiration} days
                    </span>
                  );
                } else {
                  return (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Cash valid until {formatShortNumericDate(expiresDate)}
                    </span>
                  );
                }
              })()
            )}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="h-4 w-4 text-gray-400" />
            <span title={student.email || ""}>{truncateEmail(student.email)}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <PhoneIcon className="h-4 w-4" />
            {formatPhoneNumber(student.phone)}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
        {(() => {
          if (!student.birthdate) return "-";
          const today = new Date();
          const birthDate = new Date(student.birthdate);
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age;
        })()}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        {lastCheckedIn ? (() => {
          const days = daysSince(lastCheckedIn);
          const recent = days <= RECENT_CHECK_IN_DAYS;
          return (
            <span
              className={`inline-flex flex-col rounded-md px-2 py-0.5 text-xs font-medium leading-5 ${
                recent
                  ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400"
                  : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400"
              }`}
              title={recent ? "Checked in within the last 2 weeks" : "No check-in for over 2 weeks"}
            >
              <span>{new Date(lastCheckedIn).toLocaleDateString()}</span>
              <span className="font-normal opacity-80">
                {days <= 0 ? "Today" : days === 1 ? "1 day ago" : `${days} days ago`}
              </span>
            </span>
          );
        })() : (
          <span className="inline-flex rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium leading-5 text-gray-600 dark:text-gray-300">
            Never
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        {(() => {
          const membership = getMembershipStatus(student);
          return (
            <div className="flex flex-col items-start gap-1">
              <span
                className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${MEMBERSHIP_STATUS_STYLES[membership.key]}`}
              >
                {membership.label}
              </span>
              {membership.note && (
                <span className="text-xs text-amber-700 dark:text-amber-300">{membership.note}</span>
              )}
            </div>
          );
        })()}
      </td>

      {/* Emergency Contact column removed */}
      {showDropoffRisk && (
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center">
          <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mr-2">
            <div
              className={`h-2.5 rounded-full ${(() => {
                const dynamicRisk = student.ai_drop_off_risk || 0;

                return dynamicRisk < riskThresholds.medium
                  ? "bg-green-500"
                  : dynamicRisk < riskThresholds.high
                  ? "bg-yellow-500"
                  : "bg-red-500";
              })()}`}
              style={{
                width: `${(() => {
                  const dynamicRisk = student.ai_drop_off_risk || 0;
                  
                  return dynamicRisk * 100;
                })()}%`,
              }}
            />
          </div>
          <span className="text-xs">
            {(() => {
              const dynamicRisk = student.ai_drop_off_risk || 0;
              
              return (dynamicRisk * 100).toFixed(0);
            })()}
            %
          </span>
        </div>
      </td>
      )}
      <td className="whitespace-nowrap px-3 py-4 text-sm font-medium">
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => onEdit(student)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={() => onOpenCurriculum?.(student)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
            title="Curriculum Progress"
          >
            <AcademicCapIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={() => onOpenAttendanceHistory?.(student)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
            title="Attendance History"
          >
            <ClockIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
          </button>

          {/* Record cash payment — only for manual/cash billing students */}
          {(student.billing_mode === 'manual' || student.billing_mode === 'cash') && (
            <button
              type="button"
              onClick={() => onRecordPayment?.(student)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
              title="Record Cash Membership Payment"
            >
              <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}

          {/* Payment history — visible for all students (shows cash + Stripe) */}
          <button
            type="button"
            onClick={() => onViewPaymentHistory?.(student)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
            title="View Payment History"
          >
            <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => onDelete(student)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
            title="Delete"
          >
            <TrashIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Student filters component
const StudentFilters: React.FC<{
  genderFilter: string;
  statusFilter: string;
  waiverFilter: string;
  classFilter: string;
  billingModeFilter: string;
  canUseStripeBilling: boolean;
  sortField: keyof Student;
  sortDirection: "asc" | "desc";
  classes: any[];
  onGenderFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onStatusFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onWaiverFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onClassFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBillingModeFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSort: (field: keyof Student) => void;
  disabled?: boolean;
}> = ({
  genderFilter,
  statusFilter,
  waiverFilter,
  classFilter,
  billingModeFilter,
  canUseStripeBilling,
  sortField,
  sortDirection,
  classes,
  onGenderFilterChange,
  onStatusFilterChange,
  onWaiverFilterChange,
  onClassFilterChange,
  onBillingModeFilterChange,
  onSort,
  disabled = false,
}) => {
  return (
    <div className="flex items-center justify-start w-full min-w-0">
      <div className="flex flex-wrap gap-2 xl:gap-2">
        <select
          value={genderFilter}
          onChange={onGenderFilterChange}
          className="block rounded-md border-0 py-1.5 pl-3 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          disabled={disabled}
        >
          <option value="all">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
        <select
          value={statusFilter}
          onChange={onStatusFilterChange}
          className="block rounded-md border-0 py-1.5 pl-3 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          disabled={disabled}
        >
          <option value="all">Membership Status</option>
          <option value="active">Active</option>
          <option value="cancelling">Cancelling</option>
          <option value="paused">Paused</option>
          <option value="past_due">Past due</option>
          <option value="cash">Cash / Manual</option>
          <option value="none">No membership</option>
          <option value="not_seen">Not seen in 2+ weeks</option>
          <option value="never">Never checked in</option>
        </select>
        <select
          value={waiverFilter}
          onChange={onWaiverFilterChange}
          className="block rounded-md border-0 py-1.5 pl-3 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          disabled={disabled}
        >
          <option value="all">Waiver Statuses</option>
          <option value="signed">Waiver Signed</option>
          <option value="not_signed">No Waiver Signed</option>
        </select>
        <select
          value={billingModeFilter}
          onChange={onBillingModeFilterChange}
          className="block rounded-md border-0 py-1.5 pl-3 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          disabled={disabled}
        >
          <option value="all">Billing Types</option>
          {canUseStripeBilling ? (
            <option value="auto">Auto Billing</option>
          ) : (
            <option value={UPGRADE_REQUIRED_OPTION_VALUE} disabled>
              {AUTO_BILLING_FILTER_UPGRADE_LABEL}
            </option>
          )}
          <option value="cash">Cash/Manual</option>
        </select>
        <select
          value={classFilter}
          onChange={onClassFilterChange}
          className="block rounded-md border-0 py-1.5 pl-3 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          disabled={disabled}
        >
          <option value="all">All Classes</option>
          <option value="not_assigned">Not Assigned Yet</option>
          {(() => {
            // Function to get weekday order (Monday = 1, Sunday = 7)
            const getWeekdayOrder = (dayName: string): number => {
              const dayMap: Record<string, number> = {
                'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
                'Friday': 5, 'Saturday': 6, 'Sunday': 7
              };
              return dayMap[dayName] || 8;
            };

            // Function to convert time to minutes for sorting
            const timeToMinutes = (timeStr: string | null | undefined): number => {
              if (!timeStr) return 0;
              const [hours, minutes] = timeStr.split(':').map(Number);
              return hours * 60 + minutes;
            };

            // Function to format time for display
            const formatTime = (timeStr: string | null | undefined): string => {
              if (!timeStr) return '';
              const [hours, minutes] = timeStr.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
              return `${displayHour}:${minutes} ${ampm}`;
            };

            // Sort classes by weekday and time
            const sortedClasses = [...classes].sort((a, b) => {
              // Get the first day for each class for sorting
              const aDay = a.days && a.days.length > 0 ? a.days[0] : '';
              const bDay = b.days && b.days.length > 0 ? b.days[0] : '';
              
              const aWeekday = getWeekdayOrder(aDay);
              const bWeekday = getWeekdayOrder(bDay);
              
              if (aWeekday !== bWeekday) {
                return aWeekday - bWeekday;
              }
              
              // If same day, sort by start time
              const aTime = timeToMinutes(a.start_time);
              const bTime = timeToMinutes(b.start_time);
              return aTime - bTime;
            });

            return sortedClasses.map((classItem) => {
              // Build the display text
              let displayText = classItem.class_name;
              
              if (classItem.days && classItem.days.length > 0) {
                displayText += ` (${classItem.days.join(", ")})`;
              }

              return (
                <option key={classItem.id} value={classItem.id}>
                  {displayText}
                </option>
              );
            });
          })()}
        </select>
        <button
          type="button"
          onClick={() => onSort("name")}
          disabled={disabled}
          className={`px-3 py-2 text-sm rounded-md font-semibold border border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
            sortField === "name"
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className="pointer-events-none">
            {sortField === "name" && sortDirection === "desc" ? "Z-A" : "A-Z"}{" "}
            {sortField === "name" && (sortDirection === "asc" ? "↑" : "↓")}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onSort("join_date")}
          disabled={disabled}
          className={`px-3 py-2 text-sm rounded-md font-semibold border border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
            sortField === "join_date"
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className="pointer-events-none">
            Join Date{" "}
            {sortField === "join_date" && (sortDirection === "asc" ? "↑" : "↓")}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onSort("last_checked_in")}
          disabled={disabled}
          className={`px-3 py-2 text-sm rounded-md font-semibold border border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
            sortField === "last_checked_in"
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className="pointer-events-none">
            Last Check-in{" "}
            {sortField === "last_checked_in" && (sortDirection === "asc" ? "↑" : "↓")}
          </span>
        </button>
      </div>
    </div>
  );
};

const RetentionRiskTooltipContent = ({
  onOpenSettings,
}: {
  onOpenSettings: () => void;
}) => (
  <div className="space-y-3">
    <p className="text-gray-300">AI estimates drop-off risk from:</p>
    <ul className="space-y-1.5 text-white">
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Attendance</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Membership status</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Class assignments</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Notes</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>How long they’ve been with you</span>
      </li>
    </ul>
    <p className="text-gray-300">
      Over time, adjust the red High Risk threshold so it matches your studio.
    </p>
    <button
      type="button"
      onClick={onOpenSettings}
      className="w-full inline-flex items-center justify-center rounded-md bg-indigo-500 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2 focus:ring-offset-gray-800"
    >
      Open Retention Risk Settings
    </button>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
export default function StudentManagement() {
  const { user, getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const { hasCapability } = useStudioPlan();
  const showDropoffRisk = hasCapability('aiDropoffRisk');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Helper function to format billing period
  const formatBillingPeriod = (periodEnd: number | null | undefined, isCanceling: boolean = false): string => {
    if (!periodEnd) {
      return "No billing date";
    }
    
    const date = new Date(periodEnd * 1000);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid billing date";
    }
    
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Format the date nicely (omit year when same calendar year to keep chips compact)
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
    });
    
    // If this is a cancellation, show appropriate label
    if (isCanceling) {
      if (diffDays === 0) {
        return `Cancels ${formattedDate}`;
      } else if (diffDays === 1) {
        return `Cancels ${formattedDate}`;
      } else if (diffDays < 0) {
        return `Canceled ${formattedDate}`;
      } else {
        return `Cancels ${formattedDate}`;
      }
    }
    
    // Regular billing period display
    if (diffDays === 0) {
      return `Renews ${formattedDate}`;
    } else if (diffDays === 1) {
      return `Renews ${formattedDate}`;
    } else if (diffDays < 0) {
      return `Overdue ${formattedDate}`;
    } else if (diffDays <= 7) {
      return `Renews ${formattedDate}`;
    } else {
      return `Renews ${formattedDate}`;
    }
  };

  // Check for URL parameter to filter students with expiring payment methods
  const [showExpiringPaymentsOnly, setShowExpiringPaymentsOnly] = useState(false);
  const [expiringPaymentStudents, setExpiringPaymentStudents] = useState<string[]>([]);
  const [cardExpirationsByStudent, setCardExpirationsByStudent] = useState<Record<string, { expirationDate: string; daysUntilExpiration: number }>>({});

  // Fetch CSRF token on component mount
  useEffect(() => {
    fetchCsrfToken();
  }, []);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const filter = urlParams.get('filter');
    
    if (filter === 'expiring-payments') {
      setShowExpiringPaymentsOnly(true);
      // Fetch students with expiring payment methods
      fetchExpiringPaymentStudents();
    }
  }, [location.search]);

  const fetchExpiringPaymentStudents = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/check-expiring-student-payment-methods`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const studentIds = data.expiringMethods?.map((method: any) => method.studentId) || [];
        setExpiringPaymentStudents(studentIds);
        setCardExpirationsByStudent(data.cardExpirations ?? {});
      }
    } catch (error) {
      // console.error('Error fetching expiring payment students:', error);
    }
  };

  // State management
  const [searchInput, setSearchInput] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [waiverFilter, setWaiverFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [billingModeFilter, setBillingModeFilter] = useState("all");
  const [upgradeModalCapability, setUpgradeModalCapability] =
    useState<BooleanCapabilityKey | null>(null);
  const [membershipFilter, setMembershipFilter] = useState<string>("all");
  const [birthdayMonthFilter, setBirthdayMonthFilter] = useState("all");
  const [beltSortMode, setBeltSortMode] = useState<string>(BELT_SORT_NONE);
  const [sortField, setSortField] = useState<keyof Student>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15); // Set to 15 to show pagination with 23 students

  // State for optimistic updates
  const [optimisticUpdates, setOptimisticUpdates] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [recentlyAddedStudents, setRecentlyAddedStudents] = useState<Set<string>>(new Set());
  
  // Add loading state for customer portal button
  const [portalLoading, setPortalLoading] = useState(false);

  // Use consolidated data hook with pagination params
  const {
    students: allStudents,
    belts,
    memberships,
    productDescriptions,
    lastCheckedInMap,
    staffRoles,
    classes,
    isLoading,
    isStripeConnected,
    canUseStripeBilling,
    hasPaymentIntegration,
    paymentIntegrationLoading,
    totalStudentsCount,
    studioId,
    studioName,
    studioTimezone,
    refreshData,
  } = useStudentData({
    page: 1, // Always fetch page 1 for client-side filtering
    limit: 1000, // Fetch more students for client-side filtering
    searchTerm: "", // No server-side search
    genderFilter: "all", // We'll filter client-side
    statusFilter: "all", // We'll filter client-side
    waiverFilter: "all", // We'll filter client-side
    sortField: "name", // We'll sort client-side
    sortDirection: "asc", // We'll sort client-side
  });

  useEffect(() => {
    if (!canUseStripeBilling && billingModeFilter === "auto") {
      setBillingModeFilter("all");
    }
  }, [canUseStripeBilling, billingModeFilter]);

  // Load card expirations for table countdown when Stripe is connected
  useEffect(() => {
    if (isStripeConnected) {
      fetchExpiringPaymentStudents();
    }
  }, [isStripeConnected]);

  // Membership loading state should be tied to the main data loading
  const membershipLoading = isLoading;

  // Universal refresh system
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes
    
    setIsRefreshing(true);
    try {
      await refreshData();
      // Clear optimistic updates after successful refresh
      setOptimisticUpdates([]);
      if (isStripeConnected) {
        fetchExpiringPaymentStudents();
      }
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshData, isRefreshing, isStripeConnected]);

  const {
    refreshStudent,
    refreshCreated,
    refreshDeleted,
    refreshBulkImport,
    refreshPaymentStatus,
    refreshMembership,
    refreshAIRisk,
    refreshStatus,
    refreshFamily,
  } = useStudentRefresh({
    onRefresh: handleRefresh,
    debounceMs: 50, // Reduced debounce for faster response
  });

  // Client-side filtering and pagination with optimistic updates
  const filteredStudents = useMemo(() => {
    let filtered = allStudents;

    // Apply optimistic updates
    optimisticUpdates.forEach(update => {
      filtered = applyOptimisticUpdateWithTypes(filtered, update);
    });

    // Apply search filter
    if (searchInput.trim()) {
      // Ignore leading/trailing spaces when matching, so a space typed between
      // words doesn't briefly hide results.
      const searchLower = searchInput.trim().toLowerCase();
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchLower) ||
        (student.email && student.email.toLowerCase().includes(searchLower)) ||
        (student.alternative_email && student.alternative_email.toLowerCase().includes(searchLower)) ||
        (student.phone && student.phone.toLowerCase().includes(searchLower))
      );
    }

    // Apply other filters (these can stay server-side if needed)
    if (genderFilter !== 'all') {
      filtered = filtered.filter(student => student.gender === genderFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(student => {
        const lastCheckedIn = lastCheckedInMap[student.id];
        if (statusFilter === 'never') return !lastCheckedIn;
        if (statusFilter === 'not_seen') {
          return !!lastCheckedIn && daysSince(lastCheckedIn) > RECENT_CHECK_IN_DAYS;
        }
        const membership = getMembershipStatus(student);
        if (statusFilter === 'cancelling') {
          // Includes students with one membership ending while others continue.
          return membership.key === 'cancelling' || !!membership.note;
        }
        return membership.key === statusFilter;
      });
    }
    if (waiverFilter !== 'all') {
      if (waiverFilter === 'signed') {
        filtered = filtered.filter(student => student.waiver_signed);
      } else {
        filtered = filtered.filter(student => !student.waiver_signed);
      }
    }
    
    // Apply class filter
    if (classFilter !== 'all') {
      if (classFilter === 'not_assigned') {
        filtered = filtered.filter(student => 
          !student.assigned_classes || student.assigned_classes.length === 0
        );
      } else {
        filtered = filtered.filter(student => 
          student.assigned_classes && student.assigned_classes.includes(classFilter)
        );
      }
    }

    // Apply billing mode filter
    if (billingModeFilter !== 'all') {
      if (billingModeFilter === 'cash') {
        filtered = filtered.filter(student => !isAutoBillingMode(student.billing_mode));
      } else if (billingModeFilter === 'auto') {
        filtered = filtered.filter(student => isAutoBillingMode(student.billing_mode));
      }
    }

    // Apply membership filter using membership_type_ids as source of truth (one or more = has membership)
    if (membershipFilter !== "all") {
      if (membershipFilter === "with_membership") {
        filtered = filtered.filter(hasActiveMembershipFromIds);
      } else if (membershipFilter === "without_membership") {
        filtered = filtered.filter((s) => !hasActiveMembershipFromIds(s));
      } else {
        // Specific membership type id (from studio memberships list, including types created in settings)
        filtered = filtered.filter((s) =>
          studentHasActiveMembershipType(s, membershipFilter)
        );
      }
    }

    // Filter to a single belt (by id from Settings → Belts)
    if (
      beltSortMode !== BELT_SORT_NONE &&
      !isBeltSortByRank(beltSortMode)
    ) {
      const beltMeta = belts.find((b) => b.id === beltSortMode);
      if (beltMeta) {
        filtered = filtered.filter((s) => s.belt === beltMeta.name);
      }
    }

    // Apply birthday month filter
    if (birthdayMonthFilter !== 'all') {
      const selectedMonth = parseInt(birthdayMonthFilter);
      filtered = filtered.filter(student => {
        if (!student.birthdate) return false;
        const birthdate = new Date(student.birthdate);
        // getMonth() returns 0-11, so we add 1 to match our filter values (1-12)
        return birthdate.getMonth() + 1 === selectedMonth;
      });
    }

    // Apply sorting (curriculum / reverse curriculum by rank; then column sort)
    filtered.sort((a, b) => {
      if (beltSortMode === BELT_SORT_CURRICULUM) {
        const beltCmp =
          getBeltCurriculumRank(a, belts) - getBeltCurriculumRank(b, belts);
        if (beltCmp !== 0) return beltCmp;
      } else if (beltSortMode === BELT_SORT_CURRICULUM_DESC) {
        const beltCmp =
          getBeltCurriculumRank(b, belts) - getBeltCurriculumRank(a, belts);
        if (beltCmp !== 0) return beltCmp;
      }

      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [
    allStudents,
    optimisticUpdates,
    searchInput,
    genderFilter,
    statusFilter,
    waiverFilter,
    classFilter,
    billingModeFilter,
    membershipFilter,
    birthdayMonthFilter,
    beltSortMode,
    belts,
    sortField,
    sortDirection,
    lastCheckedInMap,
  ]);

  // Client-side pagination
  const totalFilteredCount = filteredStudents.length;
  const totalPages = Math.ceil(totalFilteredCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const students = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  // NOTE: Automatic status sync disabled to prevent unnecessary refreshes during user interactions
  // Status sync can be re-enabled if needed, but should be done manually or on a schedule



  // Update handlers for pagination
  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };



  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sanitize search input
    const sanitizedValue = sanitizeInput(e.target.value, 'search');
    
    // Validate search input
    if (sanitizedValue && !validateInput(sanitizedValue, 'search')) {
      logSecurityEvent('INVALID_SEARCH_INPUT', { value: sanitizedValue });
      return;
    }
    
    setSearchInput(sanitizedValue);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleGenderFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setGenderFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleWaiverFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setWaiverFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleClassFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setClassFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleBillingModeFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (e.target.value === UPGRADE_REQUIRED_OPTION_VALUE) {
      setUpgradeModalCapability('stripeBilling');
      return;
    }
    setBillingModeFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleMembershipFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setMembershipFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleBirthdayMonthFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setBirthdayMonthFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleBeltSortChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setBeltSortMode(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field: keyof Student) => {
    setBeltSortMode(BELT_SORT_NONE);
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const [riskThresholds, setRiskThresholds] = useState({
    medium: 0.2,
    high: 0.4,
  });

  // DEBUG: Log risk thresholds on component mount
  useEffect(() => {
    
  }, [riskThresholds]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isRiskSettingsModalOpen, setIsRiskSettingsModalOpen] = useState(false);
  const [existingFamilies, setExistingFamilies] = useState<Array<{
    family_id: string;
    student_count: number;
    students: Array<{ id: string; name: string }>;
  }>>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMemberSummary[]>([]);
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
  const [familyDataLoading, setFamilyDataLoading] = useState(false);
  const [familyConsolidatedBillingSummary, setFamilyConsolidatedBillingSummary] = useState<{
    subscription_id: string | null;
    subscription_status: string | null;
    line_items: Array<{
      student_id: string;
      student_name: string;
      membership_type_id: string;
      membership_name: string;
      unit_amount_cents: number;
      discount_cents: number;
      net_amount_cents: number;
      cancel_at_period_end?: boolean;
      cancel_at?: number | null;
      is_canceling?: boolean;
    }>;
    upcoming_total_cents: number | null;
    monthly_recurring_total_cents?: number;
    upcoming_includes_prorations?: boolean;
    current_period_end?: number | null;
    currency: string;
    billing_primary_id?: string | null;
    billing_primary_name?: string | null;
    billing_primary_has_payment_method?: boolean;
    subscription_transfer_pending?: boolean;
    paused_members?: Array<{ student_id: string; student_name: string }>;
  } | null>(null);
  const [familyConsolidatedBillingEnabled, setFamilyConsolidatedBillingEnabled] =
    useState(false);
  const [familyBillingSummaryLoading, setFamilyBillingSummaryLoading] = useState(false);
  const familyBillingSummaryRequestRef = useRef(0);
  const [familyBillingMigrationLoading, setFamilyBillingMigrationLoading] =
    useState(false);
  const [individualBillingConsolidateLoading, setIndividualBillingConsolidateLoading] =
    useState(false);
  const [familyIdValidation, setFamilyIdValidation] = useState<{
    isValid: boolean;
    message: string;
    isChecking: boolean;
  }>({ isValid: true, message: '', isChecking: false });
  const familyIdValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  const [showInlinePaymentForm, setShowInlinePaymentForm] = useState<
    | null
    | "add"
    | "one-time"
    | {
        amount: string;
        description: string;
        isRetry: boolean;
        originalPaymentId: string;
      }
  >(null);
  const [showPaymentTypeSelection, setShowPaymentTypeSelection] = useState(false);
  const [showOneTimeCashPaymentModal, setShowOneTimeCashPaymentModal] = useState(false);
  const [oneTimeCashPaymentStudent, setOneTimeCashPaymentStudent] = useState<Student | null>(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pausingStudent, setPausingStudent] = useState<Student | null>(null);
  const [isPausing, setIsPausing] = useState(false);
  const [showFamilyBillingAlertModal, setShowFamilyBillingAlertModal] = useState(false);
  const [billingPrimaryHasPaymentMethodLive, setBillingPrimaryHasPaymentMethodLive] =
    useState<boolean | null>(null);
  const [dismissedFamilyBillingAlertForStudentId, setDismissedFamilyBillingAlertForStudentId] =
    useState<string | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showRemoveFromFamilyModal, setShowRemoveFromFamilyModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBalanceAdjustmentModal, setShowBalanceAdjustmentModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStudent, setPaymentStudent] = useState<Student | null>(null);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [paymentHistoryStudent, setPaymentHistoryStudent] = useState<Student | null>(null);
  const [showCurriculumModal, setShowCurriculumModal] = useState(false);
  const [curriculumStudent, setCurriculumStudent] = useState<Student | null>(null);
  const [showAttendanceHistoryModal, setShowAttendanceHistoryModal] = useState(false);
  const [attendanceHistoryStudent, setAttendanceHistoryStudent] = useState<Student | null>(null);

  // Selection states
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [familyDiscountRefreshTrigger, setFamilyDiscountRefreshTrigger] = useState(0);
  const [newStudentId, setNewStudentId] = useState<string | null>(null);
  const [lastPaymentMethodId, setLastPaymentMethodId] = useState<string | null>(
    null
  );

  // Import states
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<number | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportingFromStripe, setIsImportingFromStripe] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Proration states
  const [prorationData, setProrationData] = useState<{
    amount: number;
    currency: string;
    proration_date: number;
  } | null>(null);
  const [isCalculatingProration, setIsCalculatingProration] = useState(false);
  
  // Credit balance state
  const [creditBalance, setCreditBalance] = useState<{
    balance: number;
    currency: string;
    has_credit: boolean;
    raw_balance: number;
  } | null>(null);
  const [isLoadingCreditBalance, setIsLoadingCreditBalance] = useState(false);

  // Mirrors the student being edited so async billing lookups can tell whether their
  // response still belongs on screen. The editingStudent captured in a callback is
  // whatever it was when the request started, which is the stale value we need to
  // detect.
  const editingStudentIdRef = useRef<string | null>(null);
  useEffect(() => {
    editingStudentIdRef.current = editingStudent?.id ?? null;
  }, [editingStudent?.id]);
  
  // Multi-membership proration state
  const [multiProrationData, setMultiProrationData] = useState<{
    prorations: any[];
    total_amount: number;
    existing_credit: number;
    net_amount: number;
    currency: string;
    calculation_date: number;
  } | null>(null);
  const [isCalculatingMultiProration, setIsCalculatingMultiProration] = useState(false);

  // Failed payments state
  const [failedPayments, setFailedPayments] =
    useState<FailedPaymentDetails | null>(null);
  const [loadingFailedPayments, setLoadingFailedPayments] = useState(false);
  const [loadingSubscriptionSync, setLoadingSubscriptionSync] = useState(false);
  const [uncancelingMembershipId, setUncancelingMembershipId] = useState<string | null>(null);

  const [hasShownRetryNotification, setHasShownRetryNotification] =
    useState(false);

  // Last payment date for display in edit card (manual + Stripe)
  const [lastPaymentDateForStudent, setLastPaymentDateForStudent] = useState<string | null>(null);
  const [lastPaymentDateFetched, setLastPaymentDateFetched] = useState(false);

  // Form management
  // Import/Export utilities
  const { exportStudents, downloadTemplate } = useImportExport();

  const {
    formData,
    setFormData,
    formStudentId,
    setIsUpdatingFromSave,
    handleInputChange,
    handleClassToggle,
    resetForm,
    calculateAge,
    formatPhoneNumber,
    clearCheckedStaffRoles,
    userMakingMembershipChanges,
    setUserMakingMembershipChanges,
    setBillingModeDirty,
    familyDiscountPercent,
    setFamilyDiscountPercent,
    markFamilyIdDirty,
    clearFamilyIdDirty,
    familyIdDirty,
  } = useStudentForm(editingStudent, memberships, staffRoles, classes, membershipLoading);

  const isCashManualStudentBilling =
    formData.billing_mode === 'cash' || formData.billing_mode === 'manual';
  const isLocalMembershipBilling =
    !canUseStripeBilling || isCashManualStudentBilling;
  const hasSelectedMembership =
    !!formData.membership_type ||
    (formData.membership_type_ids != null && formData.membership_type_ids.length > 0);
  const needsStripeForMemberships =
    canUseStripeBilling && !isCashManualStudentBilling && !isStripeConnected;

  useEffect(() => {
    if (!canUseStripeBilling && isModalOpen) {
      const mode = formData.billing_mode;
      if (mode !== 'cash' && mode !== 'manual') {
        setFormData((prev) => ({ ...prev, billing_mode: 'cash' }));
      }
    }
  }, [canUseStripeBilling, isModalOpen, formData.billing_mode, setFormData]);

  const handleBillingModeChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (e.target.value === UPGRADE_REQUIRED_OPTION_VALUE) {
      setUpgradeModalCapability('stripeBilling');
      return;
    }
    const newMode = e.target.value;
    setBillingModeDirty(true);
    const wasManual =
      formData.billing_mode === 'cash' || formData.billing_mode === 'manual';
    const isStripeBilling = newMode !== 'cash' && newMode !== 'manual';
    if (wasManual && isStripeBilling) {
      setFormData((prev) => ({
        ...prev,
        billing_mode: newMode,
        billing_guardian_id: null,
      }));
      return;
    }
    handleInputChange(e);
  };

  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    title: "",
    type: "success" as "success" | "error" | "general",
  });

  // Image upload state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState<string>("");
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  // Export fields configuration
  const [exportFields, setExportFields] = useState<ExportField[]>([
    { key: "name", label: "Name", selected: true },
    { key: "email", label: "Email", selected: true },
    { key: "alternative_email", label: "Alternative Email", selected: false },
    { key: "phone", label: "Phone", selected: true },
    { key: "age", label: "Age", selected: true },
    { key: "gender", label: "Gender", selected: true },
    { key: "belt", label: "Belt", selected: true },
    { key: "status", label: "Status", selected: true },
    { key: "join_date", label: "Join Date", selected: true },
    { key: "last_checked_in", label: "Last Check In", selected: true },
    {
      key: "emergency_contact_name",
      label: "Emergency Contact Name",
      selected: true,
    },
    {
      key: "emergency_contact_phone",
      label: "Emergency Contact Phone",
      selected: true,
    },
    {
      key: "relationship_to_student",
      label: "Relationship to Student",
      selected: true,
    },
    { key: "membership_type", label: "Membership Type", selected: true },
    { key: "notes", label: "Notes", selected: true },
  ]);

  const [importFields, setImportFields] = useState<ImportField[]>([
    { key: "name", label: "Name", csvColumn: "" },
    { key: "email", label: "Email", csvColumn: "" },
    { key: "alternative_email", label: "Alternative Email", csvColumn: "" },
    { key: "phone", label: "Phone", csvColumn: "" },
    // { key: "age", label: "Age", csvColumn: "" }, // Removed - age is calculated from birthdate
    { key: "birthdate", label: "Date of Birth", csvColumn: "" },
    { key: "gender", label: "Gender", csvColumn: "" },
    { key: "belt", label: "Belt", csvColumn: "" },
    { key: "status", label: "Status", csvColumn: "" },
    { key: "join_date", label: "Join Date", csvColumn: "" },
    {
      key: "emergency_contact_name",
      label: "Emergency Contact Name",
      csvColumn: "",
    },
    {
      key: "emergency_contact_phone",
      label: "Emergency Contact Phone",
      csvColumn: "",
    },
    {
      key: "relationship_to_student",
      label: "Relationship to Student",
      csvColumn: "",
    },
    { key: "membership_type", label: "Membership Type", csvColumn: "" },
    { key: "notes", label: "Notes", csvColumn: "" },
  ]);

  // Add after other state declarations
  const [stripeHasPaymentMethod, setStripeHasPaymentMethod] = useState<boolean | null>(null);

  // Helper for payment method check (use everywhere instead of just editingStudent?.has_payment_method)
  const effectiveFamilyId = familyIdDirty
    ? (formData.family_id || "").trim() || null
    : (formData.family_id || editingStudent?.family_id || "").trim() || null;

  const isFamilyBilledMember =
    (formData.billing_mode === "family" && !!formData.billing_guardian_id) ||
    (editingStudent?.billing_mode === "family" &&
      !!editingStudent?.billing_guardian_id);

  const isStripeFamilyBillingPrimary = (
    mode?: string | null,
    billingGuardianId?: string | null
  ) =>
    (mode === "individual" || mode === "auto") && !billingGuardianId;

  const familyBillingPrimaryFromMembers = familyMembers.find((member) =>
    isStripeFamilyBillingPrimary(member.billing_mode, member.billing_guardian_id)
  );

  const existingFamilyBillingPrimary = familyMembers.find(
    (member) =>
      isStripeFamilyBillingPrimary(
        member.billing_mode,
        member.billing_guardian_id
      ) && member.id !== editingStudent?.id
  );

  const isEditingStudentBillingPrimary =
    !!editingStudent?.id &&
    isStripeFamilyBillingPrimary(
      formData.billing_mode || editingStudent.billing_mode,
      formData.billing_guardian_id ?? editingStudent.billing_guardian_id
    );

  const familyBillingPrimaryStudentId =
    familyConsolidatedBillingSummary?.billing_primary_id ||
    familyBillingPrimaryFromMembers?.id ||
    (isEditingStudentBillingPrimary ? editingStudent?.id ?? null : null) ||
    null;

  const familyBillingPrimaryName = familyBillingPrimaryStudentId
    ? familyConsolidatedBillingSummary?.billing_primary_name ||
      familyMembers.find((member) => member.id === familyBillingPrimaryStudentId)
        ?.name ||
      (familyBillingPrimaryStudentId === editingStudent?.id
        ? editingStudent?.name
        : null) ||
      null
    : null;

  const isFamilyBillingPrimaryStudent =
    !!editingStudent?.id &&
    !!familyBillingPrimaryStudentId &&
    editingStudent.id === familyBillingPrimaryStudentId;

  const isConsolidatedFamilyBilledNonPrimary =
    familyConsolidatedBillingEnabled &&
    !!effectiveFamilyId &&
    !isFamilyBillingPrimaryStudent &&
    formData.billing_mode !== "cash" &&
    formData.billing_mode !== "manual" &&
    familyConsolidatedBillingSummary?.billing_primary_has_payment_method !==
      false;

  const restrictOneTimePaymentToPrimaryCard =
    !!familyBillingPrimaryStudentId &&
    !isFamilyBillingPrimaryStudent &&
    (isFamilyBilledMember || familyConsolidatedBillingEnabled);

  const restrictBalanceAdjustmentToPrimary =
    familyConsolidatedBillingEnabled &&
    !!editingStudent?.family_id &&
    !!familyBillingPrimaryStudentId &&
    !isFamilyBillingPrimaryStudent;

  const isStripeFamilyBillingMode = (mode?: string | null) =>
    mode !== 'cash' && mode !== 'manual';

  const familyHasAutoBillingMember =
    familyMembers.length > 0
      ? familyMembers.some((member) =>
          isStripeFamilyBillingMode(member.billing_mode)
        )
      : isStripeFamilyBillingMode(editingStudent?.billing_mode);

  const billingPrimaryIdForPaymentCheck =
    familyConsolidatedBillingSummary?.billing_primary_id ||
    familyBillingPrimaryStudentId;

  const primaryMissingPaymentMethod =
    familyConsolidatedBillingSummary?.billing_primary_has_payment_method ===
      false &&
    (billingPrimaryIdForPaymentCheck
      ? billingPrimaryHasPaymentMethodLive === false
      : true);

  const familyBillingNeedsPaymentMethodAttention =
    familyConsolidatedBillingEnabled &&
    !!editingStudent?.family_id &&
    familyHasAutoBillingMember &&
    !familyBillingSummaryLoading &&
    primaryMissingPaymentMethod;

  const familyBillingPrimaryDisplayName =
    familyConsolidatedBillingSummary?.billing_primary_name ||
    familyBillingPrimaryName ||
    null;

  const familyConsolidatedMidCycleImmediateCharge =
    familyConsolidatedBillingEnabled && !!effectiveFamilyId;

  const hasMidCycleMembershipProration =
    !!prorationData ||
    (multiProrationData?.prorations?.some(
      (item) => item.action === 'add' && item.type === 'charge'
    ) ??
      false);

  const membershipProrationBehavior: string | undefined =
    hasMidCycleMembershipProration ? 'always_invoice' : undefined;

  const isViewingFamilyBillingPrimary =
    !!editingStudent?.id &&
    (familyConsolidatedBillingSummary?.billing_primary_id === editingStudent.id ||
      isFamilyBillingPrimaryStudent);

  const willBecomeFamilyBillingPrimary =
    !existingFamilyBillingPrimary &&
    (isFamilyBillingPrimaryStudent ||
      familyConsolidatedBillingSummary?.billing_primary_id === editingStudent?.id ||
      (!!effectiveFamilyId &&
        familyConsolidatedBillingSummary?.billing_primary_id == null &&
        familyMembers.filter((member) => member.id !== editingStudent?.id).length ===
          0));

  const billsThroughFamilyPrimary =
    (isFamilyBilledMember && !isViewingFamilyBillingPrimary) ||
    isConsolidatedFamilyBilledNonPrimary ||
    (familyConsolidatedBillingEnabled &&
      !!effectiveFamilyId &&
      !willBecomeFamilyBillingPrimary &&
      !familyBillingSummaryLoading &&
      familyConsolidatedBillingSummary != null &&
      familyConsolidatedBillingSummary.billing_primary_has_payment_method !== false);

  const consolidatedFamilyBillingBlocked =
    familyConsolidatedBillingEnabled &&
    !!effectiveFamilyId &&
    familyHasAutoBillingMember &&
    (primaryMissingPaymentMethod ||
      familyConsolidatedBillingSummary?.subscription_transfer_pending === true);

  const hasFamilyBillingPastDue =
    editingStudent?.subscription_status === "past_due" &&
    (isFamilyBilledMember ||
      (familyConsolidatedBillingEnabled &&
        !formData.billing_guardian_id &&
        !!effectiveFamilyId));

  const hasPaymentMethod = (() => {
    if (billsThroughFamilyPrimary || isConsolidatedFamilyBilledNonPrimary) {
      return true;
    }

    if (
      familyConsolidatedBillingEnabled &&
      effectiveFamilyId &&
      !familyBillingSummaryLoading &&
      familyConsolidatedBillingSummary != null &&
      willBecomeFamilyBillingPrimary &&
      familyConsolidatedBillingSummary.billing_primary_has_payment_method
    ) {
      return true;
    }

    return !!lastPaymentMethodId || stripeHasPaymentMethod === true;
  })();

  const membershipRequiresPaymentMethod =
    hasSelectedMembership &&
    !isLocalMembershipBilling &&
    !hasPaymentMethod &&
    !(!!effectiveFamilyId && familyConsolidatedBillingEnabled && familyBillingSummaryLoading);

  const familyMembershipChangesBlocked =
    consolidatedFamilyBillingBlocked && !isViewingFamilyBillingPrimary;

  // Mirrors getPausedMembershipAdditionError on the server: while paused, a student
  // can drop memberships but not take on new ones, which would ride the paused
  // subscription for free or start billing while the rest is paused.
  const membershipAdditionsPaused =
    !!editingStudent?.id &&
    !isLocalMembershipBilling &&
    editingStudent.subscription_status === "paused";

  // Mirrors the server rule in resolveMembershipChangeTargets: a membership that is
  // being replaced ends immediately, while one removed with nothing taking its place
  // stays active until the period already paid for runs out.
  const pendingMembershipChange = useMemo(() => {
    const selectedIds = formData.membership_type_ids || [];
    const isCashManual =
      formData.billing_mode === "cash" || formData.billing_mode === "manual";
    const hasLoadedSubscriptionData =
      !!editingStudent?.subscription_details &&
      editingStudent.subscription_details.length > 0;

    const previouslySelectedIds = (memberships || [])
      .filter((membership) => {
        if (isCashManual) {
          return !!editingStudent?.membership_type_ids?.includes(membership.id);
        }

        const subscriptionDetail = editingStudent?.subscription_details?.find(
          (sub) => sub.membership_type_id === membership.id
        );
        const hasActiveStripeSubscription =
          !!subscriptionDetail &&
          (subscriptionDetail.status === "active" ||
            subscriptionDetail.status === "canceling" ||
            subscriptionDetail.isCanceling);

        return (
          (hasLoadedSubscriptionData && hasActiveStripeSubscription) ||
          (editingStudent?.subscription_status === "paused" &&
            !!editingStudent?.membership_type_ids?.includes(membership.id)) ||
          (selectedIds.includes(membership.id) &&
            !!editingStudent?.membership_type_ids?.includes(membership.id))
        );
      })
      .map((membership) => membership.id);

    const additions = selectedIds.filter(
      (id: string) => !previouslySelectedIds.includes(id)
    );
    const removals = previouslySelectedIds.filter(
      (id) => !selectedIds.includes(id)
    );
    const isOneForOneSwap = additions.length === 1 && removals.length === 1;

    return {
      previouslySelectedIds,
      additions,
      removals,
      isOneForOneSwap,
      removalsAreImmediate:
        (!isOneForOneSwap && additions.length > 0) ||
        formData.cancelImmediately === true,
    };
  }, [
    memberships,
    formData.membership_type_ids,
    formData.billing_mode,
    formData.cancelImmediately,
    editingStudent,
  ]);

  // Handle URL parameters for editing - only on initial load
  useEffect(() => {
    // Check for student_id in the URL and open modal if needed
    const urlParams = new URLSearchParams(window.location.search);
    const studentId = urlParams.get("student_id");

    // Only trigger on initial load when no modal is open and no student is being edited
    if (studentId && students && !isModalOpen && !editingStudent) {
      const student = students.find((s) => s.id === studentId);
      if (student) {
        setEditingStudent(student);
        setIsModalOpen(true);
        setFamilyDiscountRefreshTrigger(prev => prev + 1);
      }
    }
  }, [students, isModalOpen]);

  // Reinitialize form when memberships finish loading
  useEffect(() => {
    if (!membershipLoading && editingStudent && memberships && memberships.length > 0) {
      // Only reset form if user hasn't made membership changes
      if (!userMakingMembershipChanges) {
        // Reset form with fully loaded membership data only if user hasn't made changes
        resetForm();
      }
    }
  }, [membershipLoading, editingStudent, memberships, resetForm, userMakingMembershipChanges]);

  // Reinitialize form when subscription details or synced membership IDs change
  useEffect(() => {
    if (editingStudent?.subscription_details && memberships && memberships.length > 0) {
      // Only reset form if user hasn't made membership changes
      if (!userMakingMembershipChanges) {
        // Reset form with subscription details loaded
        resetForm();
      }
    }
  }, [
    editingStudent?.subscription_details,
    editingStudent?.membership_type_ids,
    memberships,
    resetForm,
    userMakingMembershipChanges,
  ]);

  // Fetch last payment date when edit modal opens (manual + Stripe)
  useEffect(() => {
    if (!editingStudent?.id) {
      setLastPaymentDateForStudent(null);
      setLastPaymentDateFetched(false);
      return;
    }
    setLastPaymentDateFetched(false);
    const token = getAuthToken();
    if (!token) return;
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${editingStudent.id}/last-payment-date`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((res) => (res.ok ? res.json() : { lastPaymentDate: null }))
      .then((data) => {
        setLastPaymentDateForStudent(data?.lastPaymentDate ?? null);
        setLastPaymentDateFetched(true);
      })
      .catch(() => {
        setLastPaymentDateForStudent(null);
        setLastPaymentDateFetched(true);
      });
  }, [editingStudent?.id, getAuthToken]);

  // Fetch failed payments for a student
  const fetchFamilyDiscount = useCallback(
    async (familyId: string, studentId: string) => {
      if (!familyId || !studentId || !studioUser?.studio_id) return;
      
      try {
        const authToken = getAuthToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/calculate/${familyId}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              studioId: studioUser.studio_id
            })
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          const studentDiscount = data.discounts?.find((d: any) => d.student_id === studentId);
          if (studentDiscount && studentDiscount.discount_type === 'percentage') {
            setFamilyDiscountPercent(studentDiscount.discount_value);
          } else {
            setFamilyDiscountPercent(0);
          }
        } else {
          setFamilyDiscountPercent(0);
        }
      } catch (error) {
        console.error('Error fetching family discount:', error);
        setFamilyDiscountPercent(0);
      }
    },
    [getAuthToken, studioUser]
  );

  const fetchFailedPayments = useCallback(
    async (studentId: string) => {
      if (!studentId) return;

      setLoadingFailedPayments(true);
      try {
        const authToken = getAuthToken();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${studentId}/failed-payments`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Only store the count since we just need to know if there are failed payments
          setFailedPayments({ count: data.count || 0 });
        } else {
          setFailedPayments(null);
        }
      } catch (error) {
        setFailedPayments(null);
      } finally {
        setLoadingFailedPayments(false);
      }
    },
    [getAuthToken]
  );



  // Handle successful payment (including retries)
  const handlePaymentSuccess = useCallback(
    async (paymentMethodId: string) => {


      setLastPaymentMethodId(paymentMethodId);

      // Since we're using customer portal for retries, just handle regular payment success
      // Add a small delay to allow Stripe to process payment status changes
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Refresh failed payments and students data
      // Always refresh student data after payment method is added to ensure payment method status is current
      if (editingStudent?.id) {
        // Always refresh both failed payments and student data to ensure payment method status is updated
        await Promise.all([
          fetchFailedPayments(editingStudent.id),
          refreshData(),
        ]);
        
        // Update the editing student with fresh data to reflect the new payment method
        // This ensures the modal has the most current payment method status
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${editingStudent.id}`,
            { headers: { Authorization: `Bearer ${getAuthToken()}` } }
          );
          if (response.ok) {
            const updatedStudent = await response.json();
            setEditingStudent(updatedStudent);
          }
        } catch (error) {
          console.error('Error fetching updated student data:', error);
        }
      }

      setNotification({
        show: true,
        message:
          showInlinePaymentForm === "add"
            ? "Payment method updated successfully!"
            : "Payment processed successfully!",
        title: "Success",
        type: "success",
      });

      // Close the inline form and clear retry state
      setShowInlinePaymentForm(null);
    },
    [
      editingStudent,
      fetchFailedPayments,
      refreshData,
      showInlinePaymentForm,
      formData,
    ]
  );

  // Handlers
  const handleEditClick = useCallback(
    (student: Student) => {
      const requestedStudentId = student.id;
      const isStaleEditRequest = () =>
        editingStudentIdRef.current !== requestedStudentId;

      // Invalidate async work started for the previously opened student immediately.
      editingStudentIdRef.current = requestedStudentId;

      // Seed the form immediately; the fresh response below safely replaces this same student.
      setEditingStudent(student);

      // Open modal first for immediate feedback
      setIsModalOpen(true);

      // Drop the previous student's billing panels so nothing carries over while this
      // student's own values are still loading.
      setCreditBalance(null);
      setMultiProrationData(null);
      setProrationData(null);
      
      // Set image preview if student has an image
      if (student.student_image) {
        setImagePreview(student.student_image);
      } else {
        setImagePreview("");
      }
      
      // Always fetch fresh student data from server to ensure we have latest membership_type_ids
      const fetchFreshStudentData = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser?.studio_id}`,
            {
              cache: "no-store",
              headers: {
                Authorization: `Bearer ${getAuthToken()}`,
              },
            }
          );

          if (isStaleEditRequest()) return;
          
          let studentToUse = student; // Default to passed student
          
          if (response.ok) {
            const result = await response.json();
            if (isStaleEditRequest()) return;
            const freshStudent = result.data?.find((s: Student) => s.id === student.id);
            if (freshStudent) {
              studentToUse = freshStudent;
              setEditingStudent(freshStudent);
            } else {
              // Fallback to passed student if not found in fresh data
              setEditingStudent(student);
            }
          } else {
            // Fallback to passed student if fetch fails
            setEditingStudent(student);
          }
          
          // Now fetch Stripe-related data ONLY for auto-billing students
          const isCashBilling = studentToUse.billing_mode === 'cash' || studentToUse.billing_mode === 'manual';
          
          if (!isCashBilling) {
            // Fetch failed payments for auto-billing students only
            fetchFailedPayments(studentToUse.id);
            
            // Fetch credit balance for auto-billing students only
            fetchCreditBalance(studentToUse.id);

            // Auto-sync subscription status from Stripe if Stripe is connected
            if (isStripeConnected && studentToUse.id) {
              setLoadingSubscriptionSync(true);
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${studentToUse.id}/sync-subscription-status`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${getAuthToken()}`,
                },
              })
              .then(response => {
                if (response.ok) {
                  return response.json();
                }
                throw new Error("Failed to sync subscription status");
              })
              .then(result => {
                if (isStaleEditRequest()) return;

                const syncedMembershipIds =
                  Array.isArray(result.active_membership_ids) &&
                  result.active_membership_ids.length > 0
                    ? result.active_membership_ids
                    : null;

                // Update the editing student with synced data
                setEditingStudent(prev => prev?.id === requestedStudentId ? {
                  ...prev,
                  subscription_status: (() => {
                    const nextStatus = result.status ?? prev.subscription_status;
                    if (
                      prev.subscription_status === 'paused' &&
                      nextStatus !== 'paused' &&
                      nextStatus !== 'active'
                    ) {
                      return 'paused';
                    }
                    return nextStatus;
                  })(),
                  membership_type_ids:
                    syncedMembershipIds ?? prev.membership_type_ids ?? [],
                  membership_type:
                    result.membership_type ?? prev.membership_type,
                  subscription_details: result.subscription_details || prev.subscription_details || [],
                } : prev);

                if (syncedMembershipIds && !userMakingMembershipChanges) {
                  setFormData((prev) => ({
                    ...prev,
                    membership_type_ids: syncedMembershipIds,
                    membership_type: syncedMembershipIds[0] || prev.membership_type,
                  }));
                }
              })
              .catch(error => {
                console.error("Failed to auto-sync subscription status:", error);
                // Don't show error notification for auto-sync, just log it
              })
              .finally(() => {
                if (!isStaleEditRequest()) {
                  setLoadingSubscriptionSync(false);
                }
              });
            }
          }
          
          // Fetch family discount for auto-billed students (cash/manual uses live preview)
          if (studentToUse.family_id) {
            const isCashManualStudent =
              studentToUse.billing_mode === 'cash' ||
              studentToUse.billing_mode === 'manual';
            if (canUseStripeBilling && !isCashManualStudent) {
              fetchFamilyDiscount(studentToUse.family_id, studentToUse.id);
            }
          } else {
            setFamilyDiscountPercent(0);
          }

          // Sync waiver_signed from waiver_submissions (matched by student email)
          if (studentToUse.id && studentToUse.email?.trim()) {
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studentToUse.id}/sync-waiver-status`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${getAuthToken()}`,
                },
              }
            )
              .then((syncResponse) =>
                syncResponse.ok ? syncResponse.json() : null
              )
              .then((syncResult) => {
                if (isStaleEditRequest()) return;
                if (syncResult?.waiver_signed) {
                  setEditingStudent((prev) =>
                    prev ? { ...prev, waiver_signed: true } : null
                  );
                  setFormData((prev) => ({ ...prev, waiver_signed: true }));
                  if (syncResult.updated) {
                    refreshData();
                  }
                }
              })
              .catch((syncError) => {
                console.error('Failed to sync waiver status:', syncError);
              });
          }
        } catch (error) {
          console.error('[Edit] Error fetching fresh student data:', error);
          // Fallback to passed student if error
          if (!isStaleEditRequest()) {
            setEditingStudent(student);
          }
        }
      };
      
      // Fetch fresh data first, then open modal
      fetchFreshStudentData();
      
      // Set modal open and image preview (will happen quickly while data fetches)
      setIsModalOpen(true);
      
      // Set image preview if student has an image
      if (student.student_image) {
        setImagePreview(student.student_image);
      } else {
        setImagePreview("");
      }
    },
    [fetchFailedPayments, fetchFamilyDiscount, isStripeConnected, canUseStripeBilling, getAuthToken, studioUser, setFormData, refreshData]
  );

  const handleModalClose = useCallback(() => {
    editingStudentIdRef.current = null;
    setIsModalOpen(false);
    setEditingStudent(null);
    setImageFile(null);
    setImagePreview("");
    setShowInlinePaymentForm(null);
    setLastPaymentMethodId(null);
    setCreditBalance(null);
    setProrationData(null);
    setIsCalculatingProration(false);
    setFailedPayments(null);
    setLastPaymentDateForStudent(null);
    setLastPaymentDateFetched(false);
    // Clear checked staff roles so they can be re-checked if needed
    clearCheckedStaffRoles();
    setLoadingFailedPayments(false);
    // Reset membership changes flag
    setUserMakingMembershipChanges(false);

    setHasShownRetryNotification(false);
    setLoadingSubscriptionSync(false);
    setPortalLoading(false); // Reset portal loading state
    setShowFamilyBillingAlertModal(false);
    setShowRemoveFromFamilyModal(false);
    clearFamilyIdDirty();
    setDismissedFamilyBillingAlertForStudentId(null);

    // Clear family ID validation message
    setFamilyIdValidation({ isValid: true, message: '', isChecking: false });

    resetForm();

    // Clear URL parameter to prevent modal from reopening
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("student_id")) {
      urlParams.delete("student_id");
      navigate({ search: urlParams.toString() }, { replace: true });
    }
  }, [resetForm, navigate, clearCheckedStaffRoles, clearFamilyIdDirty]);

  // Dismissing the modal does not mean nothing changed: several controls inside it
  // (payment method, balance adjustment, subscription sync) commit on their own, so the
  // list can be out of date even when Update Student was never pressed. The save path
  // refreshes itself, so it keeps calling handleModalClose directly.
  const handleModalDismiss = useCallback(() => {
    handleModalClose();
    void handleRefresh();
  }, [handleModalClose, handleRefresh]);

  // Payment recording handlers
  const handleRecordPayment = useCallback((student: Student) => {
    setPaymentStudent(student);
    setShowPaymentModal(true);
  }, []);

  const handleViewPaymentHistory = useCallback((student: Student) => {
    setPaymentHistoryStudent(student);
    setShowPaymentHistoryModal(true);
  }, []);

  const handleOpenCurriculum = useCallback((student: Student) => {
    setCurriculumStudent(student);
    setShowCurriculumModal(true);
  }, []);

  const handleOpenAttendanceHistory = useCallback((student: Student) => {
    setAttendanceHistoryStudent(student);
    setShowAttendanceHistoryModal(true);
  }, []);

  const handlePaymentModalClose = useCallback(() => {
    const studentId = paymentStudent?.id;
    setShowPaymentModal(false);
    setPaymentStudent(null);
    // Refresh student data to show updated payment status
    if (studentId) {
      refreshPaymentStatus(studentId);
    }
  }, [paymentStudent, refreshPaymentStatus]);

  const handlePaymentHistoryModalClose = useCallback(() => {
    setShowPaymentHistoryModal(false);
    setPaymentHistoryStudent(null);
  }, []);

  // Multi-select handlers
  const toggleSelectMode = useCallback(() => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) {
      setSelectedStudents(new Set());
    }
  }, [isSelectMode]);

  const toggleStudentSelection = useCallback((studentId: string) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  }, []);

  const selectAllStudents = useCallback(() => {
    setSelectedStudents(new Set(students.map(student => student.id)));
  }, [students]);

  const clearSelection = useCallback(() => {
    setSelectedStudents(new Set());
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedStudents.size === 0) return;

    const studentsToDelete = students.filter(student => selectedStudents.has(student.id));
    const hasActiveMemberships = studentsToDelete.some(student => 
      hasSubscriptionId(student) && student.subscription_status !== "canceled"
    );

    if (hasActiveMemberships) {
      setNotification({
        show: true,
        message: "Cannot delete students with active memberships. Please cancel their memberships first.",
        title: "Cannot Delete",
        type: "error",
      });
      return;
    }

    setShowBulkDeleteModal(true);
  }, [selectedStudents, students]);

  const confirmBulkDelete = useCallback(async () => {
    if (selectedStudents.size === 0) return;

    // Security logging for bulk delete operation
    logSecurityEvent('BULK_DELETE_ATTEMPT', { 
      studentCount: selectedStudents.size,
      studentIds: Array.from(selectedStudents)
    });

    setIsBulkDeleting(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const studentsToDelete = students.filter(student => selectedStudents.has(student.id));

      // Delete students one by one
      const deletePromises = studentsToDelete.map(student =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${student.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        })
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.ok).length;
      const failed = results.length - successful;

      // Clear selection and exit select mode
      setSelectedStudents(new Set());
      setIsSelectMode(false);
      setShowBulkDeleteModal(false);

      // Refresh data
      handleRefresh();

      if (failed === 0) {
        setNotification({
          show: true,
          message: `Successfully deleted ${successful} student${successful !== 1 ? 's' : ''}`,
          title: "Bulk Delete Complete",
          type: "success",
        });
      } else if (successful === 0) {
        setNotification({
          show: true,
          message: `Failed to delete ${failed} student${failed !== 1 ? 's' : ''}`,
          title: "Bulk Delete Failed",
          type: "error",
        });
      } else {
        setNotification({
          show: true,
          message: `Deleted ${successful} student${successful !== 1 ? 's' : ''}, failed to delete ${failed} student${failed !== 1 ? 's' : ''}`,
          title: "Bulk Delete Partial",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "An error occurred during bulk deletion",
        title: "Error",
        type: "error",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedStudents, students, getAuthToken, handleRefresh]);

  // Fetch credit balance for a student
  const fetchCreditBalance = useCallback(
    async (studentId: string) => {
      if (!studentId || !getAuthToken()) {
        setCreditBalance(null);
        return;
      }

      // A response that arrives after the user moved to another student would
      // otherwise show one student's balance on another's record.
      const isStale = () => editingStudentIdRef.current !== studentId;

      setIsLoadingCreditBalance(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/customers/${studentId}/credit-balance`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (isStale()) {
            return;
          }
          setCreditBalance(data);
        } else if (!isStale()) {
          setCreditBalance(null);
        }
      } catch (error) {
        console.error("Error fetching credit balance:", error);
        if (!isStale()) {
          setCreditBalance(null);
        }
      } finally {
        setIsLoadingCreditBalance(false);
      }
    },
    [getAuthToken]
  );

  // Calculate multi-membership proration
  const calculateMultiProration = useCallback(
    async (addedIds: string[], removedIds: string[], cancelImmediately: boolean = false) => {
      if (!editingStudent || !getAuthToken()) {
        setMultiProrationData(null);
        return;
      }

      // Only calculate if there are actual changes
      if (addedIds.length === 0 && removedIds.length === 0) {
        setMultiProrationData(null);
        return;
      }

      // Same reason as the credit balance: a preview that lands after the user moved
      // on would otherwise be shown against the wrong student.
      const requestedStudentId = editingStudent.id;
      const isStale = () => editingStudentIdRef.current !== requestedStudentId;

      setIsCalculatingMultiProration(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${requestedStudentId}/multi-proration`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              added_membership_ids: addedIds,
              removed_membership_ids: removedIds,
              cancel_immediately: cancelImmediately,
              family_id: formData.family_id || null, // Pass family_id from form for discount calculation
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (isStale()) {
            return;
          }
          setMultiProrationData(data);
        } else if (!isStale()) {
          setMultiProrationData(null);
        }
      } catch (error) {
        console.error("Error calculating multi-proration:", error);
        if (!isStale()) {
          setMultiProrationData(null);
        }
      } finally {
        setIsCalculatingMultiProration(false);
      }
    },
    [editingStudent, getAuthToken, formData.family_id]
  );

  // Watch for membership checkbox changes and calculate proration in real-time
  useEffect(() => {
    if (!editingStudent || !formData.membership_type_ids) {
      setMultiProrationData(null);
      return;
    }

    // Skip proration calculation for cash/manual billing students
    if (formData.billing_mode === 'cash' || formData.billing_mode === 'manual') {
      setMultiProrationData(null);
      return;
    }

    // The comparison below takes the baseline from editingStudent and the selection
    // from formData, so it is only meaningful once both describe the same student.
    // On the render where a new student has loaded but the form still holds the
    // previous one's values, this would read their memberships as additions.
    if (formStudentId !== editingStudent.id) {
      setMultiProrationData(null);
      return;
    }

    const switchedFromManualToAuto =
      (editingStudent.billing_mode === 'cash' ||
        editingStudent.billing_mode === 'manual') &&
      formData.billing_mode !== 'cash' &&
      formData.billing_mode !== 'manual';
    const previousIds = switchedFromManualToAuto
      ? []
      : editingStudent.membership_type_ids || [];
    const currentIds = formData.membership_type_ids || [];

    // Find added and removed memberships
    const addedIds = currentIds.filter(id => !previousIds.includes(id));
    const removedIds = previousIds.filter(id => !currentIds.includes(id));

    // Calculate proration if there are changes
    if (addedIds.length > 0 || removedIds.length > 0) {
      // Pass the cancelImmediately checkbox state
      const cancelImmediately = formData.cancelImmediately || false;
      calculateMultiProration(addedIds, removedIds, cancelImmediately);
    } else {
      setMultiProrationData(null);
    }
  }, [formData.membership_type_ids, formData.cancelImmediately, formData.billing_mode, editingStudent, formStudentId, calculateMultiProration]);

  // Calculate proration when membership type changes
  const calculateProration = useCallback(
    async (newMembershipTypeId: string) => {
      if (
        !editingStudent || !hasSubscriptionId(editingStudent) ||
        !getAuthToken() ||
        !newMembershipTypeId
      ) {
        setProrationData(null);
        return;
      }

      setIsCalculatingProration(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${editingStudent.id}/proration`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              new_membership_type_id: newMembershipTypeId,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setProrationData(data.proration);
        } else {
          setProrationData(null);
        }
      } catch (error) {
        setProrationData(null);
      } finally {
        setIsCalculatingProration(false);
      }
    },
    [editingStudent, getAuthToken]
  );

  // Handle membership type change with proration calculation
  const handleMembershipTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      // Allow membership assignment for cash/manual billing students without Stripe
      const isCashBilling = formData.billing_mode === 'cash' || formData.billing_mode === 'manual';
      
      // Prevent membership assignment if Stripe is not connected (only for auto-billing students)
      if (!isStripeConnected && !isCashBilling) {
        setNotification({
          show: true,
          message: "Please connect your Stripe account in Settings → Integrations to enable membership assignments.",
          title: "Stripe Required",
          type: "error",
        });
        return;
      }

      const newMembershipTypeId = e.target.value;
      handleInputChange(e);

      // Calculate proration if changing from one membership to another (only for auto-billing)
      if (!isCashBilling && editingStudent && hasSubscriptionId(editingStudent) && newMembershipTypeId) {
        calculateProration(newMembershipTypeId);
      } else {
        setProrationData(null);
      }
    },
    [handleInputChange, editingStudent, calculateProration, isStripeConnected, formData.billing_mode]
  );

  // Image upload handlers
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Phone photos are HEIC. iOS converts them to JPEG when picked through Photos, but a
    // raw .heic reaches us when chosen through Files, and some browsers report no type at
    // all for those. Accept them and let the decode below decide.
    const isHeic =
      /image\/(heic|heif)/i.test(file.type) ||
      (!file.type && /\.(heic|heif)$/i.test(file.name));

    if (!isHeic && !file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      setNotification({
        show: true,
        message: "Please select a JPEG, PNG, WEBP, or HEIC file only",
        title: "Invalid File Type",
        type: "error",
      });
      return;
    }

    // Note: File size limit removed - all images will be automatically compressed

    setImageFile(file);
    
    try {
      // Compress and convert to base64
      const compressedBase64 = await compressImage(file);
      setCropImage(compressedBase64);
      setShowCropModal(true);
    } catch (error) {
      setNotification({
        show: true,
        message: isHeic
          ? "This browser can't open HEIC photos. On an iPhone, pick the photo from your Photo Library instead of Files and it will convert automatically, or save it as a JPEG first."
          : "Failed to process image. Please try again.",
        title: "Image Processing Error",
        type: "error",
      });
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData(prev => ({
      ...prev,
      student_image: null,
    }));
    setShowCropModal(false);
    setCropImage("");
    setCropPosition({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent, keepModalOpen = false, suppressSuccessNotification = false, suppressSavingState = false) => {
      e.preventDefault();

      // Track if subscription creation fails
      let subscriptionCreationFailed = false;
      let familyJoinBillingErrors: string[] = [];

      // Prevent submission while memberships are loading
      if (membershipLoading) {
        setNotification({
          show: true,
          message: "Please wait for memberships to finish loading before saving.",
          title: "Memberships Loading",
          type: "error",
        });
        return;
      }

      // Client-side rate limiting
      const now = Date.now();
      if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
        setNotification({
          show: true,
          message: "Please wait before submitting again",
          title: "Rate Limited",
          type: "error",
        });
        return;
      }

      // CSRF token validation (strict in production, flexible in development)
      if (!csrfToken) {
        logSecurityEvent('CSRF_TOKEN_MISSING', { user: user?.id });
        if (process.env.NODE_ENV === 'production') {
          setNotification({
            show: true,
            message: "Security token missing. Please refresh the page and try again.",
            title: "Security Error",
            type: "error",
          });
          return;
        } else {
          // In development, log the issue but allow submission to continue
          console.warn('CSRF token missing in development mode - proceeding with submission');
        }
      }

      if (!user?.id || !getAuthToken() || !studioUser?.studio_id) {
        setNotification({
          show: true,
          message: "Missing required data. Please try again.",
          title: "Error",
          type: "error",
        });
        return;
      }

      setLastSubmissionTime(now);

      // Security logging for form submission
      logSecurityEvent('FORM_SUBMISSION_ATTEMPT', { 
        isNewStudent: !editingStudent,
        studentId: editingStudent?.id,
        hasMembership: !!formData.membership_type,
        hasImage: !!formData.student_image
      });

      // Frontend validation: email required for billing/notifications (paid) or studio communications (free)
      if (!formData.email || String(formData.email).trim() === "") {
        setNotification({
          show: true,
          message: canUseStripeBilling
            ? "An email address is required to set up billing in Stripe and to receive receipts and payment notifications."
            : "An email address is required for each student so you can send waivers, messages, and important studio updates.",
          title: "Email required",
          type: "error",
        });
        return;
      }

      const altEmailTrimmed = formData.alternative_email
        ? String(formData.alternative_email).trim()
        : "";
      if (
        altEmailTrimmed &&
        (!validateInput(altEmailTrimmed, "email") ||
          altEmailTrimmed.toLowerCase() === String(formData.email).trim().toLowerCase())
      ) {
        setNotification({
          show: true,
          message: altEmailTrimmed.toLowerCase() === String(formData.email).trim().toLowerCase()
            ? "Alternative email must be different from the primary email."
            : "Please enter a valid alternative email address.",
          title: "Invalid alternative email",
          type: "error",
        });
        return;
      }

      // Frontend validation: gender required
      if (!formData.gender || String(formData.gender).trim() === "") {
        setNotification({
          show: true,
          message: "Please select a gender before saving the student.",
          title: "Gender required",
          type: "error",
        });
        return;
      }

      // Check Stripe for payment method only if this is a new student getting their first membership
      // or if this is an existing student without a current membership getting one
      let hasPaymentMethod = false;
      const isNewStudent = !editingStudent;
      const hadMembership = editingStudent?.membership_type || (editingStudent?.membership_type_ids && editingStudent.membership_type_ids.length > 0);
      const willHaveMembership = formData.membership_type || (formData.membership_type_ids && formData.membership_type_ids.length > 0);
      
      // Only verify payment method if:
      // 1. New student getting a membership, OR
      // 2. Existing student without membership getting their first membership
      // 3. NOT on cash/manual billing mode
      const isCashBilling =
        !canUseStripeBilling ||
        formData.billing_mode === 'cash' ||
        formData.billing_mode === 'manual';
      
      if (willHaveMembership && (isNewStudent || (!hadMembership && editingStudent)) && !isCashBilling) {
        try {
          if (!editingStudent?.id) {
            throw new Error('Student ID is required for payment method verification');
          }

          // Check if student has a family ID assigned or bills through the primary
          const hasFamilyId = formData.family_id && formData.family_id.trim() !== '';
          const billsThroughPrimary =
            formData.billing_mode === 'family' && !!formData.billing_guardian_id;

          if (hasFamilyId && familyConsolidatedBillingEnabled) {
            const familyId = formData.family_id!.trim();
            const summaryRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/stripe/families/${encodeURIComponent(familyId)}/consolidated-billing-summary`,
              {
                cache: "no-store",
                headers: { Authorization: `Bearer ${getAuthToken()}` },
              }
            );
            const summary = summaryRes.ok ? await summaryRes.json() : null;

            if (summary?.subscription_transfer_pending) {
              setNotification({
                show: true,
                message:
                  "Family billing setup is still in progress. The billing primary must complete payment setup before memberships can change.",
                title: "Family Billing Setup Required",
                type: "error",
              });
              return;
            }

            const willBePrimary =
              formData.billing_mode !== 'family' || !formData.billing_guardian_id;

            if (summary?.billing_primary_has_payment_method === false) {
              if (willBePrimary) {
                const pmRes = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/payment-methods`,
                  { headers: { Authorization: `Bearer ${getAuthToken()}` } }
                );
                const pmData = await pmRes.json();
                hasPaymentMethod = !!pmData.hasPaymentMethod;
              } else {
                hasPaymentMethod = false;
              }
            } else {
              hasPaymentMethod = true;
            }

            if (!hasPaymentMethod) {
              setNotification({
                show: true,
                message:
                  "The family billing primary must have a payment method on file before memberships can be activated.",
                title: "Payment Method Required",
                type: "error",
              });
              return;
            }
          } else if (hasFamilyId && (billsThroughPrimary || !hadMembership)) {
            hasPaymentMethod = true;
          } else if (hasFamilyId) {
            hasPaymentMethod = true;
          } else {
            // For individual billing, check the student's own payment method
            const pmRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/payment-methods`,
              { headers: { Authorization: `Bearer ${getAuthToken()}` } }
            );
            const pmData = await pmRes.json();
            hasPaymentMethod = !!pmData.hasPaymentMethod;
            
            if (!hasPaymentMethod) {
              setNotification({
                show: true,
                message:
                  "A payment method is required to activate a membership. Please add a payment method first.",
                title: "Payment Method Required",
                type: "error",
              });
              return;
            }
          }
        } catch (err) {
          console.error('Payment method verification error:', err);
          setNotification({
            show: true,
            message: "Failed to verify payment method. Please try again.",
            title: "Error",
            type: "error",
          });
          return;
        }
      } else if (isCashBilling) {
        // For cash/manual billing, skip payment method check entirely
        hasPaymentMethod = true;
        console.log('[Cash Billing] Skipping payment method verification for cash/manual billing student');
      } else if (!isCashBilling) {
        // For existing students with memberships, verify payment method via API call
        // to ensure we have the most current status (ONLY for auto-billing)
        const normalizedFamilyIdForSave =
          formData.family_id && String(formData.family_id).trim()
            ? String(formData.family_id).trim()
            : editingStudent?.family_id || null;
        const saveBillsThroughFamilyPrimary =
          (formData.billing_mode === "family" &&
            !!formData.billing_guardian_id) ||
          (familyConsolidatedBillingEnabled &&
            !!normalizedFamilyIdForSave &&
            formData.billing_mode !== "cash" &&
            formData.billing_mode !== "manual" &&
            (() => {
              const primaryId =
                formData.billing_guardian_id ||
                familyConsolidatedBillingSummary?.billing_primary_id ||
                familyMembers.find(isFamilyBillingPrimaryMember)?.id ||
                null;
              return (
                !!primaryId &&
                primaryId !== editingStudent?.id &&
                familyConsolidatedBillingSummary?.billing_primary_has_payment_method !==
                  false
              );
            })());

        if (saveBillsThroughFamilyPrimary) {
          hasPaymentMethod = true;
        } else {
        try {
          if (editingStudent?.id) {
            const pmRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/payment-methods`,
              { headers: { Authorization: `Bearer ${getAuthToken()}` } }
            );
            const pmData = await pmRes.json();
            hasPaymentMethod = !!pmData.hasPaymentMethod;
          }
        } catch (err) {
          console.error('Payment method verification error (fallback):', err);
          hasPaymentMethod = false;
        }
        }
      }

      if (!suppressSavingState) {
        setIsSaving(true);
      }
      setIsAddingStudent(true);
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const url = editingStudent
          ? `${apiUrl}/api/supabase/students/${editingStudent.id}`
          : `${apiUrl}/api/supabase/students`;

        // Convert birthdate from MM/DD/YYYY to YYYY-MM-DD for storage
        const birthdateForStorage = convertDateToStorageFormat(formData.birthdate);
        const calculatedAge = birthdateForStorage
          ? calculateAge(birthdateForStorage)
          : null;

        const {
          overdue_amount,
          has_payment_method,
          stripe_payment_method_id,
          stripe_customer_id,
          stripe_subscription_id,
          subscription_cancel_at,
          subscription_status,
          is_emergency_contact,
          is_payment_manager,
          cancelImmediately,
          ...studentDataToSave
        } = formData as any;

        // Remove membership_type if it's null to avoid overwriting existing membership
        if (studentDataToSave.membership_type === null) {
          delete studentDataToSave.membership_type;
        }

        // Check if student_image is too large for API / payload
        if (studentDataToSave.student_image && studentDataToSave.student_image.length > MAX_STUDENT_IMAGE_BASE64_LENGTH) {
          setNotification({
            show: true,
            message: "Student image is too large. Please try cropping a smaller area or use a different image.",
            title: "Image Too Large",
            type: "error",
          });
          if (!suppressSavingState) {
            setIsSaving(false);
          }
          return;
        }

        const normalizedFamilyId =
          formData.family_id && String(formData.family_id).trim()
            ? String(formData.family_id).trim()
            : null;

        const selectedMembershipTypeIds = formData.membership_type_ids || [];

        const requestBody = {
          ...studentDataToSave,
          alternative_email: altEmailTrimmed || null,
          birthdate: birthdateForStorage,
          age: calculatedAge,
          studio_id: studioUser.studio_id,
          waiver_signed: formData.waiver_signed,
          staff_role: formData.staff_role,
          assigned_classes: formData.assigned_classes,
          family_id: normalizedFamilyId,
          cancelImmediately: formData.cancelImmediately || false,
          desired_membership_type_ids: selectedMembershipTypeIds,
          // The server decides which cancelled memberships stay active through the
          // period already paid for, so just send what was selected.
          membership_type_ids: selectedMembershipTypeIds,
          relationship_to_student: formData.relationship_to_student,
          billing_mode: !canUseStripeBilling ? 'cash' : studentDataToSave.billing_mode,
        };







        const response = await fetch(url, {
          method: editingStudent ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          if (response.status === 403) {
            const errorData: PlanLimitErrorBody = await response.json().catch(() => ({}));
            const message =
              isStudentPlanLimitError(errorData) &&
              typeof errorData.limit === 'number' &&
              typeof errorData.current === 'number'
                ? formatStudentLimitErrorMessage(
                    errorData.limit,
                    errorData.current,
                    1,
                    !canUseStripeBilling
                  )
                : errorData.error ||
                  "You have reached your plan's student limit. Please upgrade your plan to add more students.";
            setNotification({
              show: true,
              message,
              title: "Student Limit Reached",
              type: "error",
            });
            if (!suppressSavingState) {
              setIsSaving(false);
            }
            return;
          }
          
          let errorMessage = "Failed to save student";
          try {
            const errorData = await response.json();
            console.error('Backend error response:', errorData);
            errorMessage = errorData.message || errorData.error || errorData.details || "Failed to save student";
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
          
          throw new Error(errorMessage);
        }

        const savedResponse = await response.json();
        const savedStudent = Array.isArray(savedResponse)
          ? savedResponse[0]
          : savedResponse;
        if (!savedStudent?.id) {
          throw new Error("The student was saved, but the server did not return the student record.");
        }
        const familyJoinBilling = savedStudent.family_join_billing as
          | {
              processed?: boolean;
              errors?: string[];
            }
          | null
          | undefined;
        if (familyJoinBilling?.errors?.length) {
          familyJoinBillingErrors = familyJoinBilling.errors;
        }
        const familyConsolidatedBillingActivatedOnSave =
          familyJoinBilling?.processed === true;
        const billingSync = savedStudent.billing_sync as
          | {
              status?: string;
              active_count?: number;
              active_membership_ids?: string[];
              membership_type?: string | null;
              subscription_details?: Student['subscription_details'];
            }
          | null
          | undefined;
        const { billing_sync: _billingSync, family_join_billing: _familyJoinBilling, ...savedStudentWithoutSync } =
          savedStudent;
        const studentWithBilling = billingSync
          ? {
              ...savedStudentWithoutSync,
              subscription_status:
                billingSync.status ?? savedStudentWithoutSync.subscription_status,
              membership_type_ids:
                billingSync.active_membership_ids ??
                savedStudentWithoutSync.membership_type_ids,
              membership_type:
                billingSync.membership_type ??
                savedStudentWithoutSync.membership_type,
              subscription_details:
                billingSync.subscription_details ??
                savedStudentWithoutSync.subscription_details,
            }
          : savedStudentWithoutSync;

        // Set flag to prevent useEffect from overriding form data
        setIsUpdatingFromSave(true);

        // Update editingStudent with the saved data from backend (backend computed the names)
        setEditingStudent(studentWithBilling);
        setFormData((prev) => ({
          ...prev,
          billing_mode: studentWithBilling.billing_mode ?? prev.billing_mode,
          billing_guardian_id:
            studentWithBilling.billing_guardian_id ?? prev.billing_guardian_id,
        }));

        if (
          (familyConsolidatedBillingActivatedOnSave ||
            familyJoinBillingErrors.length > 0) &&
          studentWithBilling.family_id
        ) {
          const familyIdToRefresh = studentWithBilling.family_id;
          const requestId = ++familyBillingSummaryRequestRef.current;
          setFamilyBillingSummaryLoading(true);
          void fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stripe/families/${encodeURIComponent(familyIdToRefresh)}/consolidated-billing-summary`,
            {
              cache: "no-store",
              headers: { Authorization: `Bearer ${getAuthToken()}` },
            }
          )
            .then(async (summaryRes) => {
              if (!summaryRes.ok) {
                if (familyBillingSummaryRequestRef.current === requestId) {
                  setFamilyConsolidatedBillingSummary(null);
                  setFamilyConsolidatedBillingEnabled(false);
                }
                return;
              }
              const summary = await summaryRes.json();
              if (familyBillingSummaryRequestRef.current !== requestId) {
                return;
              }
              setFamilyConsolidatedBillingEnabled(
                !!summary.consolidated_billing_enabled
              );
              setFamilyConsolidatedBillingSummary(
                summary.consolidated_billing_enabled !== false ? summary : null
              );
            })
            .catch((error) => {
              console.error(
                "Error refreshing family consolidated billing summary after save:",
                error
              );
            })
            .finally(() => {
              if (familyBillingSummaryRequestRef.current === requestId) {
                setFamilyBillingSummaryLoading(false);
              }
            });
        }

        // Add optimistic update for new students to show them immediately
        if (!editingStudent) {
          const optimisticUpdate = createOptimisticUpdate(savedStudent.id, savedStudent);
          setOptimisticUpdates(prev => [...prev, optimisticUpdate]);
          
          // Mark this student as recently added to prevent status sync
          setRecentlyAddedStudents(prev => new Set(Array.from(prev).concat([savedStudent.id])));
          
          // Clear the recently added flag after a delay
          setTimeout(() => {
            setRecentlyAddedStudents(prev => {
              const newSet = new Set(prev);
              newSet.delete(savedStudent.id);
              return newSet;
            });
          }, 5000); // 5 seconds should be enough
        }

        // Update form data with the saved student's data from backend
        setFormData((prev: FormData) => {
          const updated = { 
            ...prev, 
            ...studentWithBilling,
            family_id: studentWithBilling.family_id ?? null,
            billing_guardian_id: studentWithBilling.billing_guardian_id ?? null,
            // Trust the backend's computed membership data
            membership_type: studentWithBilling.membership_type,
            membership_type_ids: studentWithBilling.membership_type_ids || [],
            // Clear staff_role if it was removed
            staff_role: studentWithBilling.staff_role || null,
          };

          return updated;
        });
        const studentId = editingStudent ? editingStudent.id : studentWithBilling.id;
        const membershipTypeId = formData.membership_type;
        const membershipTypeIds = formData.membership_type_ids || (membershipTypeId ? [membershipTypeId] : []);

        // Handle membership subscription changes (additions, updates, and cancellations)
        if (studentId) {
          const previousMembershipIds = editingStudent?.membership_type_ids || [];
          const newMembershipIds = formData.membership_type_ids || [];
          
          // Check if billing mode changed from cash/manual to auto
          const previousBillingMode = editingStudent?.billing_mode;
          const billingModeChangedToAuto = 
            (previousBillingMode === 'cash' || previousBillingMode === 'manual') &&
            (formData.billing_mode !== 'cash' && formData.billing_mode !== 'manual');

          const isCashBilling =
            !canUseStripeBilling ||
            formData.billing_mode === 'cash' ||
            formData.billing_mode === 'manual';
          const billsThroughPrimary =
            formData.billing_mode === 'family' && !!formData.billing_guardian_id;
          const hasFamilyId = !!(formData.family_id && String(formData.family_id).trim());
          const studentBeforeSave = editingStudent || studentWithBilling;
          const needsIndividualSubscriptionSetup =
            !isCashBilling &&
            !!studentBeforeSave &&
            studentWithBilling.subscription_status !== 'paused' &&
            newMembershipIds.length > 0 &&
            !hasSubscriptionId(studentWithBilling) &&
            !hasFamilyId &&
            !billsThroughPrimary;
          
          // Find memberships being added (new selections)
          // IMPORTANT: If switching from cash to auto billing, treat ALL memberships as new (need Stripe subscriptions)
          const addedMemberships = billingModeChangedToAuto 
            ? newMembershipIds // All memberships need Stripe subscriptions
            : newMembershipIds.filter(id => !previousMembershipIds.includes(id));
          
          // Find memberships being removed (unchecked)
          const removedMemberships = previousMembershipIds.filter(id => !newMembershipIds.includes(id));
          
          // IMPORTANT: Handle cancellations FIRST so credits are available for new subscriptions.
          // Backend PUT already cancels removed memberships before Stripe sync for auto-billing.
          const backendHandlesStripeMembershipChanges =
            !isCashBilling && canUseStripeBilling && isStripeConnected;

          if (
            removedMemberships.length > 0 &&
            !isCashBilling &&
            !backendHandlesStripeMembershipChanges &&
            editingStudent &&
            hasSubscriptionId(editingStudent)
          ) {
            try {
              // Cancel each removed membership BEFORE creating new ones (Stripe subscriptions only)
              for (const membershipId of removedMemberships) {
                const cancelResponse = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${studentId}/cancel`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${getAuthToken()}`,
                    },
                    body: JSON.stringify({
                      membership_type_id: membershipId,
                      immediate: formData.cancelImmediately || false, // Use user's preference
                    }),
                  }
                );

                if (!cancelResponse.ok) {
                  const cancelError = await cancelResponse.json();
                  console.warn(`Failed to cancel membership ${membershipId}:`, cancelError);
                  // Continue with other cancellations even if one fails
                }
              }
              console.log('[Membership Update] Completed cancellations, credits should now be available');
            } catch (err) {
              console.error('Error canceling memberships:', err);
              // Don't throw here - continue to try adding new memberships
            }
          } else if (removedMemberships.length > 0 && isCashBilling) {
            console.log('[Cash Billing] Skipping Stripe cancellation - memberships will be updated in database only');
          }
          
          // Check if billing mode changed to cash/manual and there are existing memberships
          const billingModeChangedToCash = 
            (previousBillingMode !== 'cash' && previousBillingMode !== 'manual') &&
            (formData.billing_mode === 'cash' || formData.billing_mode === 'manual');
          
          // Handle membership additions/updates (AFTER cancellations)
          // OR if billing mode changed to cash and there are memberships to persist
          // OR if billing mode changed to auto and there are memberships that need Stripe subscriptions
          const billingAlreadyProvisioned =
            !!billingSync &&
            (billingSync.status === 'active' || billingSync.status === 'canceling') &&
            addedMemberships.length > 0 &&
            addedMemberships.every((membershipId) =>
              (billingSync.active_membership_ids || []).includes(membershipId)
            );

          if (
            (addedMemberships.length > 0 ||
              (billingModeChangedToCash && newMembershipIds.length > 0) ||
              (billingModeChangedToAuto && newMembershipIds.length > 0) ||
              needsIndividualSubscriptionSetup) &&
            !billingAlreadyProvisioned &&
            !familyConsolidatedBillingActivatedOnSave
          ) {
            if (isCashBilling) {
              // For cash/manual billing, update memberships via Stripe endpoint only when
              // Connect is available (paid). On free tier, Supabase student save already persisted membership_type_ids.
              if (!canUseStripeBilling || !isStripeConnected) {
                console.log('[Cash Billing] Skipping Stripe subscription call — local billing only');
              } else {
              try {
                console.log('[Cash Billing] Updating memberships without Stripe subscription creation');
                const subResponse = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${getAuthToken()}`,
                    },
                    body: JSON.stringify({
                      student_id: studentId,
                      membership_type_ids: newMembershipIds,
                    }),
                  }
                );

                if (!subResponse.ok) {
                  const subError = await subResponse.json();
                  throw new Error(
                    subError.error || "Failed to update membership"
                  );
                }

                const subData = await subResponse.json();
                console.log('[Cash Billing] Membership updated:', subData);
              } catch (err) {
                subscriptionCreationFailed = true;
                setNotification({
                  show: true,
                  message:
                    err instanceof Error
                      ? err.message
                      : "Failed to update membership",
                  title: "Error",
                  type: "error",
                });
              }
              }
            } else if (hasPaymentMethod) {
              // Auto-billing: Create Stripe subscriptions
              try {
                // Log if this is a billing mode change from cash to auto
                if (billingModeChangedToAuto) {
                  console.log('[Billing Mode Change] Switching from cash/manual to auto billing - creating Stripe subscriptions for all memberships:', addedMemberships);
                }
                if (needsIndividualSubscriptionSetup) {
                  console.log('[Individual Billing Setup] Creating Stripe subscriptions for memberships after leaving family billing:', newMembershipIds);
                }
                
                // For students with existing subscriptions, create subscriptions only for the NEW memberships
                // NOTE: If switching from cash to auto or setting up individual billing, use all memberships
                if (
                  studentBeforeSave &&
                  hasSubscriptionId(studentWithBilling) &&
                  previousMembershipIds.length > 0 &&
                  !billingModeChangedToAuto &&
                  !needsIndividualSubscriptionSetup
                ) {
                  // Create subscriptions only for the newly added memberships
                  const subResponse = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getAuthToken()}`,
                      },
                      body: JSON.stringify({
                        student_id: studentId,
                        membership_type_ids: addedMemberships, // Only the NEW memberships
                        use_existing_payment_method: true,
                        additive_mode: true, // Don't cancel existing subscriptions
                        proration_behavior: pendingMembershipChange.isOneForOneSwap
                          ? 'none'
                          : membershipProrationBehavior,
                      }),
                    }
                  );

                  if (!subResponse.ok) {
                    const subError = await subResponse.json();
                    // Handle Stripe-specific errors
                    if (subResponse.status === 400 && subError.type === 'stripe_error') {
                      throw new Error(subError.error || "Payment failed");
                    }
                    throw new Error(
                      subError.error || "Failed to create new membership subscriptions"
                    );
                  }

                  // Check if subscription was created but payment failed (incomplete status)
                  const subData = await subResponse.json();
                  if (subData.status === 'incomplete') {
                    throw new Error("Payment failed. Please update your payment method to activate the membership.");
                  }
                } else {
                  // Create initial subscriptions for student with no existing subscriptions
                  const subResponse = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getAuthToken()}`,
                      },
                      body: JSON.stringify({
                        student_id: studentId,
                        membership_type_ids: newMembershipIds, // All memberships for first-time setup
                        use_existing_payment_method: true,
                        proration_behavior: membershipProrationBehavior,
                      }),
                    }
                  );

                  if (!subResponse.ok) {
                    const subError = await subResponse.json();
                    // Handle Stripe-specific errors
                    if (subResponse.status === 400 && subError.type === 'stripe_error') {
                      throw new Error(subError.error || "Payment failed");
                    }
                    throw new Error(
                      subError.error || "Failed to create subscription"
                    );
                  }

                  // Check if subscription was created but payment failed (incomplete status)
                  const subData = await subResponse.json();
                  if (subData.status === 'incomplete') {
                    throw new Error("Payment failed. Please update your payment method to activate the membership.");
                  }
                }
              } catch (err) {
                subscriptionCreationFailed = true;
                setNotification({
                  show: true,
                  message:
                    err instanceof Error
                      ? err.message
                      : "Failed to update subscription",
                  title: "Error",
                  type: "error",
                });
              }
            } else if (!familyConsolidatedBillingActivatedOnSave) {
              if (!editingStudent) {
                const newStudent = {
                  ...savedStudent,
                  id: studentId,
                };
                setEditingStudent(newStudent);
                setNewStudentId(studentId);

                setNotification({
                  show: true,
                  message:
                    "Student added successfully. Please add a payment method or change billing mode to cash/manual.",
                  title: "Success",
                  type: "success",
                });

                console.log(
                  "🔄 Early return for new student with memberships but no payment method"
                );

                return;
              }

              subscriptionCreationFailed = true;
              setNotification({
                show: true,
                message:
                  "A payment method is required to activate auto billing for this student.",
                title: "Payment Method Required",
                type: "error",
              });
            }
          } else if (!editingStudent) {
            // For new students without memberships, also exit early to prevent double refresh
            console.log('🔄 Early return for new student without memberships');
            setNotification({
              show: true,
              message: "Student added successfully",
              title: "Success",
              type: "success",
            });
            
            return; // Exit early, don't close modal or refresh data yet
          }
        }

        // Show success notification
        const staffRole = staffRoles.find(role => role.id === formData.staff_role);
        const hasStaffRole = formData.staff_role && formData.staff_role.trim() !== '';
        
        // Check for membership changes to include in notification
        const previousMembershipIds = editingStudent?.membership_type_ids || [];
        const newMembershipIds = formData.membership_type_ids || [];
        const addedMemberships = newMembershipIds.filter(id => !previousMembershipIds.includes(id));
        const removedMemberships = previousMembershipIds.filter(id => !newMembershipIds.includes(id));
        
        // Check for family changes
        const previousFamilyId = editingStudent?.family_id;
        const newFamilyId = formData.family_id;
        const familyChanged = previousFamilyId !== newFamilyId;
        
        let successMessage = editingStudent ? "Student updated successfully" : "Student added successfully";
        
        if (addedMemberships.length > 0 || removedMemberships.length > 0) {
          const changes = [];
          if (pendingMembershipChange.isOneForOneSwap) {
            changes.push('changed membership (no charge today)');
          } else {
            if (addedMemberships.length > 0) {
              changes.push(`added ${addedMemberships.length} membership(s)`);
            }
            if (removedMemberships.length > 0) {
              const cancellationType = pendingMembershipChange.removalsAreImmediate
                ? 'immediately'
                : 'at period end';
              changes.push(`canceled ${removedMemberships.length} membership(s) ${cancellationType}`);
            }
          }
          successMessage += ` - ${changes.join(', ')}`;
        }
        
        // Add family change notification
        if (familyChanged) {
          if (newFamilyId) {
            successMessage += ` - joined family ${newFamilyId}`;
          } else if (previousFamilyId) {
            successMessage += ` - removed from family ${previousFamilyId}`;
          }
          // successMessage += " (family discounts updated automatically if applicable)";
        }
        
        // Only show success notification if not suppressed and billing steps succeeded
        const familyJoinBillingFailed = familyJoinBillingErrors.length > 0;
        if (
          !suppressSuccessNotification &&
          !subscriptionCreationFailed &&
          !familyJoinBillingFailed
        ) {
          setNotification({
            show: true,
            message: successMessage,
            title: "Success",
            type: "success",
          });
        } else if (familyJoinBillingFailed) {
          if (editingStudent?.id) {
            setDismissedFamilyBillingAlertForStudentId(editingStudent.id);
          }
          setNotification({
            show: true,
            message: familyJoinBillingErrors.join(" "),
            title: "Family Billing Setup",
            type: "error",
          });
        }

        // Refresh family discount display if family changed
        if (familyChanged) {
          clearFamilyIdDirty();
          setFamilyDiscountRefreshTrigger(prev => prev + 1);
          // Note: refreshFamily call removed to prevent double refresh
          // The handleRefresh() below will refresh all data including family info
        }

        if (
          studentId &&
          (addedMemberships.length > 0 || removedMemberships.length > 0) &&
          formData.billing_mode !== 'cash' &&
          formData.billing_mode !== 'manual'
        ) {
          fetchCreditBalance(studentId);
        }
        
        // Refresh the student list immediately after successful update
        // Use immediate refresh for both new students and edits for consistent UX
        setIsRefreshing(true);
        try {
          await refreshData();
          setOptimisticUpdates([]);
        } catch (error) {
          console.error('Immediate refresh failed:', error);
        } finally {
          setIsRefreshing(false);
        }
        
        // Close the modal after showing the notification (unless keepModalOpen is true)
        if (!keepModalOpen && !familyJoinBillingFailed) {
          handleModalClose();
        }
          } catch (error) {
      logSecurityEvent('FORM_SUBMISSION_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studentId: editingStudent?.id 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error ? error.message : "Failed to save student",
        title: "Error",
        type: "error",
      });
    } finally {
        if (!suppressSavingState) {
          setIsSaving(false);
        }
        setIsAddingStudent(false);
      }
    },
    [
      user,
      getAuthToken,
      studioUser,
      formData,
      editingStudent,
      lastPaymentMethodId,
      calculateAge,
      memberships,
      belts,
      staffRoles,
      refreshData,
      fetchCreditBalance,
      handleModalClose,
      setIsUpdatingFromSave,
      hasPaymentMethod,
      canUseStripeBilling,
      isStripeConnected,
    ]
  );

  const confirmDelete = useCallback(async () => {
    if (!studentToDelete || !getAuthToken()) return;

    let errorTitle = "Student Not Removed";

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studentToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        if (response.status === 502 && studentToDelete.family_id) {
          errorTitle = "Remove From Family First";
          throw new Error(
            `${studentToDelete.name} still belongs to family "${studentToDelete.family_id}", so their memberships are billed together with the rest of that family and can't be cleaned up on their own. Open ${studentToDelete.name}'s profile, clear the Family ID field, save the student, and then delete them.`
          );
        }

        throw new Error(
          errorData?.error ||
            "We couldn't remove this student. Please try again."
        );
      }

      // Optimistically remove the student from the list
      const optimisticUpdate = createOptimisticUpdate(studentToDelete.id, { 
        // Mark as deleted for optimistic removal
        id: studentToDelete.id + '_deleted' 
      });
      setOptimisticUpdates(prev => [...prev, optimisticUpdate]);
      
      // Use universal refresh system
      refreshDeleted(studentToDelete.id);
      
      setStudentToDelete(null);
      setNotification({
        show: true,
        message: "Student removed successfully",
        title: "Success",
        type: "success",
      });
    } catch (err) {
      setNotification({
        show: true,
        message:
          err instanceof Error
            ? err.message
            : "We couldn't remove this student. Please try again.",
        title: errorTitle,
        type: "error",
      });
    }
  }, [studentToDelete, getAuthToken, refreshData]);

  const handleExport = useCallback(() => {
    exportStudents(students, exportFields);
    setIsExportModalOpen(false);
  }, [students, exportFields, exportStudents]);

  const toggleExportField = useCallback((index: number) => {
    setExportFields((prev) =>
      prev.map((field, i) =>
        i === index ? { ...field, selected: !field.selected } : field
      )
    );
  }, []);

  const handleImportFieldChange = useCallback(
    (index: number, value: string) => {
      setImportFields((prev) =>
        prev.map((field, i) =>
          i === index ? { ...field, csvColumn: value } : field
        )
      );
    },
    []
  );

  // File validation is now handled by the utility function

  // CSV injection prevention is now handled by the utility function

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          const validation = validateFile(file);
          if (!validation.isValid) {
            throw new Error(validation.error || "Invalid file");
          }

          setImportFile(file);
          setImportError(null);
          setImportSuccess(null);

          // Parse the file to extract headers
          const parsedData = await parseFile(file);

          if (parsedData.errors.length > 0) {
            setImportError(parsedData.errors.join(", "));
            return;
          }

          if (parsedData.headers.length === 0) {
            setImportError("No headers found in file");
            return;
          }

          // Auto-map columns
          setImportFields((prevFields) =>
            prevFields.map((field) => {
              const match = parsedData.headers.find(
                (header) =>
                  header.replace(/\s+/g, "").toLowerCase() ===
                  field.label.replace(/\s+/g, "").toLowerCase()
              );
              return {
                ...field,
                csvColumn: match || "",
              };
            })
          );
        } catch (error) {
          setImportError(
            error instanceof Error ? error.message : "Invalid file"
          );
          setImportFile(null);
        }
      }
    },
    []
  );

  const handleImportSubmit = useCallback(
    async (rowsData: any[]) => {
      if (!getAuthToken() || !studioUser?.studio_id) {
        setNotification({
          show: true,
          message: "Authentication required",
          title: "Error",
          type: "error",
        });
        return;
      }

      setIsImporting(true);
      try {
        const studentsToImport = rowsData.map((row) => {
          const student: any = { studio_id: studioUser.studio_id };

          importFields.forEach((field) => {
            if (field.csvColumn && row[field.csvColumn] !== undefined) {
              let value = row[field.csvColumn];

              // Handle special formatting for certain fields
              if (field.key === "phone" && value) {
                value = formatPhoneNumber(value.toString());
              } else if (field.key === "age" && value) {
                const ageNum = parseInt(value);
                value = isNaN(ageNum) ? null : ageNum;
              } else if (
                (field.key === "birthdate" ||
                  field.key === "join_date" ||
                  field.key === "last_checked_in") &&
                value
              ) {
                // Convert date strings to proper format
                const date = new Date(value);
                value = isNaN(date.getTime())
                  ? null
                  : date.toISOString().split("T")[0];
              } else if (field.key === "ai_drop_off_risk" && value) {
                const riskNum = parseFloat(value);
                value = isNaN(riskNum) ? 0 : Math.max(0, Math.min(1, riskNum));
              }

              student[field.key] = value || null;
            }
          });

          // Set defaults for required fields
          if (!student.status) student.status = "Active";
          if (!student.ai_drop_off_risk) student.ai_drop_off_risk = 0;
          if (!student.join_date)
            student.join_date = new Date().toISOString().split("T")[0];

          return student;
        });

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/bulk`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({ students: studentsToImport }),
          }
        );

        if (!response.ok) {
          const errorData: PlanLimitErrorBody = await response.json().catch(() => ({}));

          if (
            response.status === 403 &&
            isStudentPlanLimitError(errorData) &&
            typeof errorData.limit === 'number' &&
            typeof errorData.current === 'number'
          ) {
            setNotification({
              show: true,
              message: formatStudentLimitErrorMessage(
                errorData.limit,
                errorData.current,
                studentsToImport.length,
                !canUseStripeBilling
              ),
              title: "Student Limit Reached",
              type: "error",
            });
            setImportError(null);
            return;
          }

          throw new Error(
            errorData.error || errorData.message || "Failed to import students"
          );
        }

        const result = await response.json();
        
        // Use universal refresh system for bulk import
        refreshBulkImport();

        setNotification({
          show: true,
          message:
            result.message || `Successfully imported ${result.count} students`,
          title: "Import Complete",
          type: "success",
        });

        setIsImportModalOpen(false);
        setImportFile(null);
      } catch (error) {
        setNotification({
          show: true,
          message:
            error instanceof Error
              ? error.message
              : "Failed to import students",
          title: "Import Error",
          type: "error",
        });
      } finally {
        setIsImporting(false);
      }
    },
    [importFile, studioUser, importFields, getAuthToken, refreshData, csrfToken, canUseStripeBilling]
  );



  const handleUpdatePaymentMethod = useCallback(
    async (student: Student) => {
      if (
        student.billing_mode === "family" &&
        student.billing_guardian_id
      ) {
        setNotification({
          show: true,
          message:
            "Payment links are only available for the family billing primary.",
          title: "Billing Primary Required",
          type: "error",
        });
        return;
      }

      if (!getAuthToken()) {
        setNotification({
          show: true,
          message: "Authentication required",
          title: "Error",
          type: "error",
        });
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payment/link`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              student_id: student.id,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to create payment portal session"
          );
        }

        const { url } = await response.json();

        // Open the portal in a new window/tab
        window.open(url, "_blank", "noopener,noreferrer");

        setNotification({
          show: true,
          message: `Payment portal opened for ${student.name}. They can update their payment method there.`,
          title: "Payment Portal Opened",
          type: "success",
        });
      } catch (error) {
        setNotification({
          show: true,
          message:
            error instanceof Error
              ? error.message
              : "Failed to open payment portal",
          title: "Error",
          type: "error",
        });
      }
    },
    [getAuthToken]
  );

  const handleGenerateCustomerPortalLink = useCallback(
    async (student: Student) => {
      if (
        student.billing_mode === "family" &&
        student.billing_guardian_id
      ) {
        setNotification({
          show: true,
          message:
            "Payment links are only available for the family billing primary.",
          title: "Billing Primary Required",
          type: "error",
        });
        return;
      }

      if (!getAuthToken()) {
        setNotification({
          show: true,
          message: "Authentication required",
          title: "Error",
          type: "error",
        });
        return;
      }

      setPortalLoading(true);

      // Check Stripe connection status with fallback verification
      if (!isStripeConnected) {
        // Try to verify Stripe connection status from backend as a fallback
        try {
          const verifyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stripe/verify-connection`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${getAuthToken()}`,
              },
            }
          );
          
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            if (verifyData.isConnected) {
              // Connection verified, continue with portal generation
            } else {
              setNotification({
                show: true,
                message: "Please connect your account to Stripe to enable payment processing.",
                title: "Stripe Required",
                type: "error",
              });
              return;
            }
          } else {
            // If verification fails, show a more helpful error message
            setNotification({
              show: true,
              message: "Unable to verify Stripe connection. Please refresh the page and try again, or contact support if the issue persists.",
              title: "Connection Check Failed",
              type: "error",
            });
            return;
          }
        } catch (error) {
          // If verification endpoint doesn't exist or fails, try the portal request anyway
          // The backend will handle the Stripe connection check
          console.log("Stripe verification failed, attempting portal request anyway...");
        }
      }

      try {
        const requestBody: any = {
          student_id: student.id,
          return_url: `${window.location.origin}/app/students`,
        };

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/student-portal-session`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          // Check for Stripe's missing portal configuration error
          if (
            errorData.details &&
            errorData.details.includes(
              "No configuration provided and your live mode default configuration has not been created"
            )
          ) {
            throw new Error(
              "Your Stripe account is not fully configured to use the Customer Portal. Ask Sifu how to enable it, or save Customer Portal settings in Stripe Live mode."
            );
          }
          // Check for Stripe connection issues
          if (errorData.error && errorData.error.includes("Stripe")) {
            throw new Error(
              "Unable to connect to Stripe. Please refresh the page and try again, or contact support if the issue persists."
            );
          }
          throw new Error(
            errorData.error || "Failed to create payment portal session"
          );
        }

        const { url } = await response.json();

        // Open the customer portal in a new tab
        if (url && typeof url === 'string' && url.startsWith('https://')) {
          window.open(url, '_blank');
          
          setNotification({
            show: true,
            message: `Customer portal for ${student.name} has been opened in a new tab.`,
            title: "Portal Opened",
            type: "success",
          });
        } else {
          throw new Error("Invalid portal URL received");
        }
      } catch (error) {
        setNotification({
          show: true,
          message:
            error instanceof Error
              ? error.message
              : "Failed to generate customer portal link",
          title: "Error",
          type: "error",
        });
      } finally {
        setPortalLoading(false);
      }
    },
    [getAuthToken, isStripeConnected]
  );





  const fetchExistingFamilies = useCallback(async () => {
    if (!getAuthToken() || !studioUser?.studio_id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/families/${studioUser.studio_id}`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExistingFamilies(data.families || []);
      }
    } catch (error) {
      console.error('Failed to fetch existing families:', error);
    }
  }, [getAuthToken, studioUser?.studio_id]);

  const handleFamilyIdChange = (newFamilyId: string) => {
    // Direct update - no confirmation required
    handleInputChange({
      target: {
        name: "family_id",
        value: newFamilyId.trim() ? newFamilyId : null,
      },
    } as any);
    
    // Trigger validation for the new family ID
    validateFamilyId(newFamilyId);
    
    // Trigger discount calculation
    if (newFamilyId) {
      // For existing students who already have this family_id saved in DB, fetch their actual discount
      if (editingStudent?.id && editingStudent.family_id === newFamilyId) {
        fetchFamilyDiscount(newFamilyId, editingStudent.id);
      } 
      // For new students OR students changing/adding family_id, calculate discount based on position
      else {
        fetchFamilyMembers(newFamilyId);
      }
    } else {
      setFamilyDiscountPercent(0);
      setFamilyMembers([]);
    }
  };

  const handleRemoveFromFamily = useCallback(() => {
    const currentFamilyId =
      (formData.family_id && formData.family_id.trim()) ||
      editingStudent?.family_id;
    if (!currentFamilyId) {
      return;
    }

    setShowRemoveFromFamilyModal(true);
  }, [formData.family_id, editingStudent?.family_id]);

  const confirmRemoveFromFamily = useCallback(() => {
    markFamilyIdDirty();
    setFormData((prev) => ({
      ...prev,
      family_id: null,
      ...(prev.billing_mode === "family" || prev.billing_guardian_id
        ? { billing_mode: "individual", billing_guardian_id: null }
        : {}),
    }));
    setFamilyIdValidation({
      isValid: true,
      message: "Student will be removed from family when you save.",
      isChecking: false,
    });
    setFamilyDiscountPercent(0);
    setFamilyMembers([]);
    setShowFamilyDropdown(false);
    setShowRemoveFromFamilyModal(false);
  }, [markFamilyIdDirty, setFormData, setFamilyDiscountPercent]);

  // Validate Family ID uniqueness
  const validateFamilyId = useCallback(async (familyId: string) => {
    if (!familyId || familyId.trim() === '') {
      setFamilyIdValidation({ isValid: true, message: '', isChecking: false });
      return;
    }

    // Don't validate if this is the same family ID the student already has
    if (editingStudent?.family_id === familyId) {
      setFamilyIdValidation({ isValid: true, message: '', isChecking: false });
      return;
    }

    // Check if this family ID exists in our existing families list (from dropdown)
    const existingFamily = existingFamilies.find(family => family.family_id === familyId);
    if (existingFamily) {
      setFamilyIdValidation({ 
        isValid: true, 
        message: `⚠️ Family ID already exists with ${existingFamily.student_count} student(s). Student will join this family.`, 
        isChecking: false 
      });
      return;
    }

    setFamilyIdValidation({ isValid: true, message: '', isChecking: true });

    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/families/validate-id`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            family_id: familyId,
            studio_id: studioUser.studio_id,
            current_student_id: editingStudent?.id || null
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (result.isAvailable) {
          setFamilyIdValidation({ 
            isValid: true, 
            message: '✓ Family ID is available and can be used', 
            isChecking: false 
          });
        } else {
          // Family ID exists - this could be intentional (joining existing family)
          // NOTE: Backend should return accurate studentCount for existing families
          setFamilyIdValidation({ 
            isValid: true, // Mark as valid since they can join existing family
            message: `⚠️ Family ID already exists with ${result.studentCount || 0} student(s). Student will join this family.`, 
            isChecking: false 
          });
        }
      } else {
        setFamilyIdValidation({ 
          isValid: false, 
          message: result.error || 'Failed to validate Family ID', 
          isChecking: false 
        });
      }
    } catch (error) {
      setFamilyIdValidation({ 
        isValid: false, 
        message: 'Error validating Family ID', 
        isChecking: false 
      });
    }
  }, [getAuthToken, studioUser?.studio_id, editingStudent?.id, editingStudent?.family_id, existingFamilies]);



  const calculateDiscountForNewStudent = useCallback(async (familyMembers: any[]) => {
    if (!studioUser?.studio_id) {
      return;
    }
    
    try {
      const activeMembers = familyMembers.filter(
        countsTowardFamilyDiscountPosition
      );
      
      // New student would be at position = activeMembers.length + 1
      const newStudentPosition = activeMembers.length + 1;
      
      // Only apply discount if position > 1
      if (newStudentPosition <= 1) {
        setFamilyDiscountPercent(0);
        return;
      }
      
      // Fetch discount rules for this studio
      const authToken = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/rules/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      
      if (response.ok) {
        const rules = await response.json();
        const applicableRule = rules.find((rule: any) => 
          rule.student_position === newStudentPosition && 
          rule.is_active === true
        );
        
        
        if (applicableRule && applicableRule.discount_type === 'percentage') {
          setFamilyDiscountPercent(applicableRule.discount_value);
        } else {
          setFamilyDiscountPercent(0);
        }
      } else {
        setFamilyDiscountPercent(0);
      }
    } catch (error) {
      console.error('[Family Discount Debug] Error calculating discount for new student:', error);
      setFamilyDiscountPercent(0);
    }
  }, [getAuthToken, studioUser?.studio_id, setFamilyDiscountPercent]);

  const refreshFamilyDiscountPreview = useCallback(async () => {
    if (!studioUser?.studio_id || !editingStudent?.id || !effectiveFamilyId) {
      return;
    }

    const pendingMembershipIds = (formData.membership_type_ids || []).filter(Boolean);
    if (pendingMembershipIds.length === 0) {
      setFamilyDiscountPercent(0);
      return;
    }

    if (familyMembers.length === 0) {
      return;
    }

    const membersForPosition: FamilyMemberSummary[] = familyMembers.map((member) =>
      member.id === editingStudent.id
        ? {
            ...member,
            membership_type_ids: pendingMembershipIds,
            billing_mode: formData.billing_mode ?? member.billing_mode,
          }
        : member
    );

    const activeMembers = membersForPosition
      .filter(countsTowardFamilyDiscountPosition)
      .sort(
        (a, b) =>
          new Date(a.created_at || 0).getTime() -
          new Date(b.created_at || 0).getTime()
      );

    const studentPosition =
      activeMembers.findIndex((member) => member.id === editingStudent.id) + 1;

    if (studentPosition <= 1) {
      setFamilyDiscountPercent(0);
      return;
    }

    try {
      const authToken = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/rules/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const rules = await response.json();
        const applicableRule = rules.find(
          (rule: any) =>
            rule.student_position === studentPosition && rule.is_active === true
        );

        if (applicableRule && applicableRule.discount_type === 'percentage') {
          setFamilyDiscountPercent(applicableRule.discount_value);
        } else {
          setFamilyDiscountPercent(0);
        }
      } else {
        setFamilyDiscountPercent(0);
      }
    } catch (error) {
      console.error('Error previewing family discount:', error);
      setFamilyDiscountPercent(0);
    }
  }, [
    studioUser?.studio_id,
    editingStudent?.id,
    effectiveFamilyId,
    formData.membership_type_ids,
    formData.billing_mode,
    familyMembers,
    getAuthToken,
    setFamilyDiscountPercent,
  ]);

  const fetchFamilyMembers = useCallback(async (familyId: string) => {
    
    if (!getAuthToken() || !studioUser?.studio_id) {
      return;
    }

    setFamilyDataLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser.studio_id}?family_id=${familyId}`,
        {
          cache: "no-store",
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        const members: FamilyMemberSummary[] = data.data
          .filter((student: any) => {
            // Only include students that are actually part of this family
            return student.family_id === familyId;
          })
          .map((student: any) => ({
            id: student.id,
            name: student.name,
            billing_mode: student.billing_mode,
            billing_guardian_id: student.billing_guardian_id,
            subscription_status: student.subscription_status,
            membership_type_ids: student.membership_type_ids,
            created_at: student.created_at
          }));
        
        setFamilyMembers(members);
        
        // Calculate discount for:
        // 1. New students (no ID yet)
        // 2. Existing students changing families (family_id doesn't match current)
        const isNewStudent = !editingStudent?.id;
        const isChangingFamily = editingStudent?.id && editingStudent.family_id !== familyId;
        
        
        if (isNewStudent || isChangingFamily) {
          calculateDiscountForNewStudent(members);
        } else {
        }
      } else {
      }
    } catch (error) {
      console.error('[Family Discount Debug] Error fetching family members:', error);
    } finally {
      setFamilyDataLoading(false);
    }
  }, [getAuthToken, studioUser?.studio_id, editingStudent?.id, editingStudent?.family_id, calculateDiscountForNewStudent]);

  const fetchFamilyConsolidatedBillingSummary = useCallback(async (familyId: string) => {
    const requestId = ++familyBillingSummaryRequestRef.current;
    const isLatestRequest = () =>
      familyBillingSummaryRequestRef.current === requestId;

    if (!getAuthToken() || !canUseStripeBilling) {
      if (isLatestRequest()) {
        setFamilyConsolidatedBillingSummary(null);
        setFamilyConsolidatedBillingEnabled(false);
        setFamilyBillingSummaryLoading(false);
      }
      return;
    }

    setFamilyBillingSummaryLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/families/${encodeURIComponent(familyId)}/consolidated-billing-summary`,
        {
          cache: "no-store",
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );

      if (response.ok) {
        const summary = await response.json();
        if (!isLatestRequest()) return;
        setFamilyConsolidatedBillingEnabled(
          !!summary.consolidated_billing_enabled
        );
        setFamilyConsolidatedBillingSummary(
          summary.consolidated_billing_enabled !== false ? summary : null
        );
      } else if (isLatestRequest()) {
        setFamilyConsolidatedBillingSummary(null);
        setFamilyConsolidatedBillingEnabled(false);
      }
    } catch (error) {
      console.error('Error fetching family consolidated billing summary:', error);
      if (isLatestRequest()) {
        setFamilyConsolidatedBillingSummary(null);
        setFamilyConsolidatedBillingEnabled(false);
      }
    } finally {
      if (isLatestRequest()) {
        setFamilyBillingSummaryLoading(false);
      }
    }
  }, [getAuthToken, canUseStripeBilling]);

  // Fetch existing families when modal opens
  useEffect(() => {
    if (isModalOpen) {
      fetchExistingFamilies();
    }
  }, [isModalOpen, fetchExistingFamilies]);

  // Fetch family members when a family ID is selected (saved or in form)
  useEffect(() => {
    if (isModalOpen && effectiveFamilyId) {
      fetchFamilyMembers(effectiveFamilyId);
      fetchFamilyConsolidatedBillingSummary(effectiveFamilyId);
    } else {
      familyBillingSummaryRequestRef.current += 1;
      setFamilyMembers([]);
      setFamilyConsolidatedBillingSummary(null);
      setFamilyConsolidatedBillingEnabled(false);
      setFamilyBillingSummaryLoading(false);
    }
  }, [isModalOpen, effectiveFamilyId, fetchFamilyMembers, fetchFamilyConsolidatedBillingSummary]);

  useEffect(() => {
    if (
      !familyConsolidatedBillingEnabled ||
      !billingPrimaryIdForPaymentCheck ||
      familyConsolidatedBillingSummary?.billing_primary_has_payment_method !== false
    ) {
      setBillingPrimaryHasPaymentMethodLive(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${billingPrimaryIdForPaymentCheck}/payment-methods`,
          { headers: { Authorization: `Bearer ${getAuthToken()}` } }
        );
        const data = response.ok ? await response.json() : null;
        if (!cancelled) {
          setBillingPrimaryHasPaymentMethodLive(!!data?.hasPaymentMethod);
        }
      } catch {
        if (!cancelled) {
          setBillingPrimaryHasPaymentMethodLive(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    familyConsolidatedBillingEnabled,
    billingPrimaryIdForPaymentCheck,
    familyConsolidatedBillingSummary?.billing_primary_has_payment_method,
    getAuthToken,
  ]);

  useEffect(() => {
    if (
      !isModalOpen ||
      !isCashManualStudentBilling ||
      !effectiveFamilyId ||
      !editingStudent?.id
    ) {
      return;
    }
    void refreshFamilyDiscountPreview();
  }, [
    isModalOpen,
    isCashManualStudentBilling,
    effectiveFamilyId,
    editingStudent?.id,
    formData.membership_type_ids,
    formData.billing_mode,
    familyMembers,
    refreshFamilyDiscountPreview,
  ]);

  useEffect(() => {
    if (
      isModalOpen &&
      editingStudent?.id &&
      familyBillingNeedsPaymentMethodAttention &&
      dismissedFamilyBillingAlertForStudentId !== editingStudent.id
    ) {
      setShowFamilyBillingAlertModal(true);
    } else if (!familyBillingNeedsPaymentMethodAttention) {
      setShowFamilyBillingAlertModal(false);
    }
  }, [
    isModalOpen,
    editingStudent?.id,
    familyBillingNeedsPaymentMethodAttention,
    dismissedFamilyBillingAlertForStudentId,
  ]);

  const dismissFamilyBillingAlertModal = useCallback(() => {
    if (editingStudent?.id) {
      setDismissedFamilyBillingAlertForStudentId(editingStudent.id);
    }
    setShowFamilyBillingAlertModal(false);
  }, [editingStudent?.id]);

  const runFamilyBillingMigration = useCallback(
    async () => {
      const familyId = editingStudent?.family_id || formData.family_id;
      if (!familyId || !getAuthToken()) {
        return;
      }

      setFamilyBillingMigrationLoading(true);
      try {
        const previewResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/families/${encodeURIComponent(familyId)}/consolidate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({ dry_run: true }),
          }
        );
        const preview = await previewResponse.json();
        if (!previewResponse.ok) {
          throw new Error(preview.error || 'Could not preview family billing migration');
        }

        if (preview.already_consolidated) {
          setNotification({
            show: true,
            title: 'Already consolidated',
            message: preview.message,
            type: 'success',
          });
          return;
        }

        const planSummary =
          preview.plan?.length > 0
            ? preview.plan
                .map(
                  (item: {
                    student_name: string | null;
                    membership_name: string;
                    estimated_credit_cents: number;
                    billing_alignment_charge_cents?: number;
                  }) =>
                    `${item.student_name || 'Student'}: ${item.membership_name}${
                      item.estimated_credit_cents > 0
                        ? ` (credit $${(item.estimated_credit_cents / 100).toFixed(2)})`
                        : ''
                    }${
                      (item.billing_alignment_charge_cents || 0) > 0
                        ? ` (billing-cycle alignment $${(
                            (item.billing_alignment_charge_cents || 0) / 100
                          ).toFixed(2)})`
                        : ''
                    }`
                )
                .join('\n')
            : 'No individual subscriptions found to migrate.';

        const billingAlignmentChargeCents = Number(
          preview.billing_alignment_charge_cents || 0
        );
        const billingAlignmentCurrency =
          preview.billing_alignment_currency || 'usd';
        const formattedBillingAlignmentCharge = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: billingAlignmentCurrency.toUpperCase(),
        }).format(billingAlignmentChargeCents / 100);
        const billingPrimaryName = editingStudent?.name || 'the billing primary';
        const consolidatedInvoiceTiming = preview.subscription_id
          ? 'next consolidated family invoice'
          : 'first consolidated family invoice';
        const billingAlignmentSummary =
          `One-time billing-cycle alignment charge to ${billingPrimaryName}: ${formattedBillingAlignmentCharge}.` +
          (billingAlignmentChargeCents > 0
            ? ` This will be included on ${billingPrimaryName}'s ${consolidatedInvoiceTiming}.`
            : ' No billing-cycle alignment charge is due.');

        const confirmed = window.confirm(
          `${preview.message}\n\n${planSummary}\n\n${billingAlignmentSummary}\n\nThis cancels individual Stripe subscriptions and moves the memberships onto one consolidated family bill on the billing primary. Continue?`
        );
        if (!confirmed) {
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/families/${encodeURIComponent(familyId)}/consolidate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              dry_run: false,
              expected_billing_alignment_charge_cents:
                billingAlignmentChargeCents,
            }),
          }
        );
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || 'Family billing migration failed');
        }

        await fetchFamilyConsolidatedBillingSummary(familyId);

        if (editingStudent?.id && isStripeConnected) {
          const syncResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/sync-subscription-status`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getAuthToken()}`,
              },
            }
          );
          if (syncResponse.ok) {
            const syncResult = await syncResponse.json();
            setEditingStudent((prev) =>
              prev
                ? {
                    ...prev,
                    subscription_status:
                      syncResult.status ?? prev.subscription_status,
                    membership_type_ids:
                      syncResult.active_membership_ids || prev.membership_type_ids,
                    membership_type:
                      syncResult.membership_type ?? prev.membership_type,
                    subscription_details:
                      syncResult.subscription_details || prev.subscription_details,
                  }
                : null
            );
          }
        }

        setNotification({
          show: true,
          title: result.already_consolidated
            ? 'Already consolidated'
            : 'Family billing consolidated',
          message: result.message,
          type: 'success',
        });
      } catch (error) {
        setNotification({
          show: true,
          title: 'Consolidation failed',
          message:
            error instanceof Error
              ? error.message
              : 'Could not consolidate family billing',
          type: 'error',
        });
      } finally {
        setFamilyBillingMigrationLoading(false);
      }
    },
    [
      editingStudent,
      formData.family_id,
      fetchFamilyConsolidatedBillingSummary,
      getAuthToken,
      isStripeConnected,
    ]
  );

  const canConsolidateIndividualBilling = (() => {
    if (
      !isConsolidateAllowed(
        CONSOLIDATE_ALLOWED_STUDENT_IDS,
        editingStudent?.id
      ) ||
      !isStripeConnected ||
      !editingStudent?.id ||
      formData.family_id ||
      formData.billing_mode === 'cash' ||
      formData.billing_mode === 'manual'
    ) {
      return false;
    }

    const liveSubscriptionIds = new Set(
      (editingStudent.subscription_details || [])
        .filter(
          (detail) =>
            !detail.isCanceling &&
            !detail.cancel_at_period_end &&
            ['active', 'trialing', 'past_due'].includes(detail.status)
        )
        .map((detail) => detail.id)
        .filter(Boolean)
    );
    if (liveSubscriptionIds.size >= 2) {
      return true;
    }

    const storedIds = Array.isArray(editingStudent.stripe_subscription_id)
      ? editingStudent.stripe_subscription_id.filter(Boolean)
      : editingStudent.stripe_subscription_id
        ? [editingStudent.stripe_subscription_id]
        : [];
    return (
      (!editingStudent.subscription_details ||
        editingStudent.subscription_details.length === 0) &&
      storedIds.length >= 2
    );
  })();

  const runIndividualBillingConsolidate = useCallback(async () => {
    if (!editingStudent?.id || !getAuthToken()) {
      return;
    }

    setIndividualBillingConsolidateLoading(true);
    try {
      const previewResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/consolidate-subscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ dry_run: true }),
        }
      );
      const preview = await previewResponse.json();
      if (!previewResponse.ok) {
        throw new Error(preview.error || 'Could not preview consolidation');
      }

      if (preview.already_consolidated) {
        setNotification({
          show: true,
          title: 'Already on one bill',
          message: preview.message,
          type: 'success',
        });
        return;
      }

      const alignmentCents = Number(preview.billing_alignment_charge_cents || 0);
      const alignmentCurrency = preview.billing_alignment_currency || 'usd';
      const formattedAlignment = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: alignmentCurrency.toUpperCase(),
      }).format(alignmentCents / 100);
      const renewalDate = preview.billing_cycle_anchor
        ? new Date(preview.billing_cycle_anchor * 1000).toLocaleDateString(
            'en-US',
            { month: 'short', day: 'numeric', year: 'numeric' }
          )
        : 'the current renewal date';
      const movingNames = (preview.plan || [])
        .filter((item: { on_keeper?: boolean }) => !item.on_keeper)
        .map((item: { membership_name: string }) => item.membership_name)
        .filter(Boolean)
        .join(', ');

      const confirmed = window.confirm(
        `${preview.message}\n\n` +
          `Move to one bill renewing ${renewalDate}` +
          (movingNames ? `: ${movingNames}` : '.') +
          `\n\nAmount owed to align billing dates: ${formattedAlignment}.` +
          (alignmentCents > 0
            ? ' Charge this as a One-Time Payment first if you want to collect it, then press this again.'
            : ' Nothing extra is owed.') +
          `\n\nThis does not change paused or scheduled-to-cancel subscriptions. Continue?`
      );
      if (!confirmed) {
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/consolidate-subscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            dry_run: false,
            expected_billing_alignment_charge_cents: alignmentCents,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Could not consolidate subscriptions');
      }

      refreshMembership(editingStudent.id);

      if (isStripeConnected) {
        const syncResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/sync-subscription-status`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );
        if (syncResponse.ok) {
          const syncResult = await syncResponse.json();
          setEditingStudent((prev) =>
            prev
              ? {
                  ...prev,
                  stripe_subscription_id:
                    syncResult.stripe_subscription_id ||
                    result.subscription_id ||
                    prev.stripe_subscription_id,
                  subscription_status:
                    syncResult.status ?? prev.subscription_status,
                  membership_type_ids:
                    syncResult.active_membership_ids || prev.membership_type_ids,
                  membership_type:
                    syncResult.membership_type ?? prev.membership_type,
                  subscription_details:
                    syncResult.subscription_details || prev.subscription_details,
                }
              : null
          );
        }
      }

      setNotification({
        show: true,
        title: result.already_consolidated
          ? 'Already on one bill'
          : 'Billing consolidated',
        message: result.message,
        type: 'success',
      });
    } catch (error) {
      setNotification({
        show: true,
        title: 'Consolidation failed',
        message:
          error instanceof Error
            ? error.message
            : 'Could not consolidate subscriptions',
        type: 'error',
      });
    } finally {
      setIndividualBillingConsolidateLoading(false);
    }
  }, [editingStudent, getAuthToken, isStripeConnected, refreshMembership]);

  // Refresh family data when modal opens
  useEffect(() => {
    if (isModalOpen && !familyIdDirty && editingStudent?.family_id) {
        // Add a small delay to ensure the modal has fully loaded
  const timer = setTimeout(async () => {
    if (!familyIdDirty && editingStudent?.family_id) {
      fetchFamilyMembers(editingStudent.family_id);
    }
    
    // Auto-sync payment method status when modal opens
    if (editingStudent?.id && editingStudent?.stripe_customer_id) {
      try {
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/sync-payment-method-status`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          if (result.updated) {
                      // Refresh the student data to show updated status
          refreshPaymentStatus(editingStudent.id);
          }
        }
      } catch (error) {
        console.error('Auto-sync payment method status failed:', error);
      }
    }
  }, 100);
  return () => clearTimeout(timer);
    }
  }, [isModalOpen, editingStudent?.family_id, familyIdDirty, fetchFamilyMembers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (familyIdValidationTimeoutRef.current) {
        clearTimeout(familyIdValidationTimeoutRef.current);
      }
    };
  }, []);

  // Close family dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.family-dropdown-container')) {
        setShowFamilyDropdown(false);
      }
    };

    if (showFamilyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFamilyDropdown]);



  const cancelDelete = useCallback(() => {
    setStudentToDelete(null);
  }, []);

  const handleImportClick = useCallback(async () => {
    if (!importFile) return;

    try {
      // Parse the file using our utility
      const parsedData = await parseFile(importFile);

      if (parsedData.errors.length > 0) {
        setImportError(parsedData.errors.join(", "));
        return;
      }

      if (parsedData.rows && parsedData.rows.length > 0) {
        await handleImportSubmit(parsedData.rows);
      }
    } catch (error) {
      setImportError(
        error instanceof Error ? error.message : "Failed to process file"
      );
    }
  }, [importFile, handleImportSubmit]);

  const handleDownloadTemplate = useCallback(() => {
    downloadTemplate();
  }, [downloadTemplate]);

  const handleImportFromStripe = useCallback(async () => {
    if (!getAuthToken() || !studioUser?.studio_id) {
      setNotification({
        show: true,
        message: "Authentication required",
        title: "Error",
        type: "error",
      });
      return;
    }

    setIsImportingFromStripe(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      // Fetch customers from Stripe
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/customers`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch Stripe customers");
      }

      const {
        customers,
        total,
        total_customers_in_stripe,
        filtered_out_count,
      } = await response.json();

      if (total === 0) {
        setNotification({
          show: true,
          message: `No new customers found in Stripe to import. Found ${total_customers_in_stripe} total customers in Stripe, ${filtered_out_count} were filtered out (already exist as students).`,
          title: "No Customers",
          type: "general",
        });
        return;
      }

      // Transform customers to student format
      const studentsToImport = customers.map((customer: any) => ({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        status: "Active" as const,
        join_date: new Date(customer.created * 1000)
          .toISOString()
          .split("T")[0], // Convert Unix timestamp to date
        ai_drop_off_risk: 0,
        studio_id: studioUser.studio_id,
        stripe_customer_id: customer.id,
        has_payment_method: customer.has_payment_method || false,
        stripe_payment_method_id: customer.stripe_payment_method_id || null,
        notes: customer.has_active_subscription
          ? "Imported from Stripe - Has active subscription"
          : "Imported from Stripe",
      }));

      // Import students using the existing bulk endpoint
      const importResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ students: studentsToImport }),
        }
      );

      if (!importResponse.ok) {
        const errorData = await importResponse.json();
        throw new Error(
          errorData.message || "Failed to import students from Stripe"
        );
      }

      const result = await importResponse.json();
      refreshBulkImport();

      setNotification({
        show: true,
        message: `Successfully imported ${result.count} students from Stripe. These students are now linked to their existing Stripe customers and payment methods. Found ${total_customers_in_stripe} total customers, ${filtered_out_count} were already existing students.`,
        title: "Import Complete",
        type: "success",
      });

      setIsImportModalOpen(false);
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to import from Stripe",
        title: "Import Error",
        type: "error",
      });
    } finally {
      setIsImportingFromStripe(false);
    }
  }, [getAuthToken, studioUser, refreshData]);

  const handleCancelMembership = useCallback(async () => {
    if (!studentToDelete || !getAuthToken()) return;

    setIsCancelling(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${studentToDelete.id}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            immediate: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to cancel membership");
      }

      refreshMembership(studentToDelete.id);
      setStudentToDelete(null);
      setNotification({
        show: true,
        message: "Membership cancelled successfully",
        title: "Success",
        type: "success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to cancel membership",
        title: "Error",
        type: "error",
      });
    } finally {
      setIsCancelling(false);
    }
  }, [studentToDelete, getAuthToken, refreshData]);

  useEffect(() => {
    // Check payment methods based on current form billing mode (not saved billing mode)
    const currentBillingMode = formData.billing_mode || editingStudent?.billing_mode;
    const isCashBilling =
      !canUseStripeBilling ||
      currentBillingMode === 'cash' ||
      currentBillingMode === 'manual';
    
    if (editingStudent?.id && !isCashBilling && canUseStripeBilling) {
      const isFamilyBilled =
        (formData.billing_mode === "family" && !!formData.billing_guardian_id) ||
        (editingStudent.billing_mode === "family" &&
          !!editingStudent.billing_guardian_id);

      if (isFamilyBilled) {
        setStripeHasPaymentMethod(true);
      } else {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/payment-methods`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
        .then((res) => res.json())
        .then((data) => setStripeHasPaymentMethod(!!data.hasPaymentMethod))
        .catch(() => setStripeHasPaymentMethod(null));
      }
    } else {
      // For cash/manual billing or no student, clear payment method state
      setStripeHasPaymentMethod(null);
    }
    setLastPaymentMethodId(null);
  }, [editingStudent, formData.billing_mode, formData.billing_guardian_id, getAuthToken, canUseStripeBilling]);

  const handlePauseClick = (student: Student) => {
    setPausingStudent(student);
    setShowPauseModal(true);
  };

  const handleResumeClick = (student: Student) => {
    setPausingStudent(student);
    setShowPauseModal(true);
  };

  const syncEditingStudentSubscriptionStatus = useCallback(
    async (studentId: string) => {
      if (!isStripeConnected) {
        return null;
      }

      setLoadingSubscriptionSync(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${studentId}/sync-subscription-status`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!response.ok) {
          return null;
        }

        const result = await response.json();
        const syncedMembershipIds =
          Array.isArray(result.active_membership_ids) &&
          result.active_membership_ids.length > 0
            ? result.active_membership_ids
            : null;

        setEditingStudent((prev) =>
          prev && prev.id === studentId
            ? {
                ...prev,
                subscription_status: (() => {
                  const nextStatus = result.status ?? prev.subscription_status;
                  if (
                    prev.subscription_status === 'paused' &&
                    nextStatus !== 'paused' &&
                    nextStatus !== 'active'
                  ) {
                    return 'paused';
                  }
                  return nextStatus;
                })(),
                membership_type_ids:
                  syncedMembershipIds ?? prev.membership_type_ids ?? [],
                membership_type: result.membership_type ?? prev.membership_type,
                subscription_details:
                  result.subscription_details || prev.subscription_details || [],
              }
            : prev
        );

        if (
          editingStudentIdRef.current === studentId &&
          syncedMembershipIds &&
          !userMakingMembershipChanges
        ) {
          setFormData((prev) => ({
            ...prev,
            membership_type_ids: syncedMembershipIds,
            membership_type: result.membership_type ?? prev.membership_type,
          }));
        }

        return result;
      } catch (error) {
        console.error("Failed to sync subscription status after pause/resume:", error);
        return null;
      } finally {
        setLoadingSubscriptionSync(false);
      }
    },
    [getAuthToken, isStripeConnected, userMakingMembershipChanges]
  );

  const handlePauseMembership = async () => {
    if (!pausingStudent) return;
    setIsPausing(true);
    try {
      let endpoint = '';
      let successMsg = '';
      if (pausingStudent.subscription_status === 'paused') {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${pausingStudent.id}/resume`;
        successMsg = 'Membership resumed successfully.';
      } else {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${pausingStudent.id}/pause`;
        successMsg = 'Membership paused successfully.';
      }
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update membership status");
      }

      const pauseResult = await response.json().catch(() => ({}));
      if (pauseResult?.message) {
        successMsg = pauseResult.message;
      }

      if (editingStudent && editingStudent.id === pausingStudent.id) {
        const newStatus =
          pausingStudent.subscription_status === "paused" ? "active" : "paused";
        setEditingStudent((prev) =>
          prev
            ? {
                ...prev,
                subscription_status: newStatus,
              }
            : null
        );
      }

      await syncEditingStudentSubscriptionStatus(pausingStudent.id);

      const familyIdToRefresh =
        effectiveFamilyId || pausingStudent.family_id || editingStudent?.family_id;
      if (familyIdToRefresh) {
        await fetchFamilyConsolidatedBillingSummary(familyIdToRefresh);
        await fetchFamilyMembers(familyIdToRefresh);
      }

      await triggerAIRiskRecalculation(pausingStudent.id);

      setNotification({
        show: true,
        message: successMsg,
        title: "Success",
        type: "success",
      });
      setShowPauseModal(false);
      setPausingStudent(null);
      refreshMembership(pausingStudent.id);
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to update membership status",
        title: "Error",
        type: "error",
      });
    } finally {
      setIsPausing(false);
    }
  };

  // Function to trigger AI risk recalculation in Supabase
  const triggerAIRiskRecalculation = async (studentId: string) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studentId}/recalculate-risk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        // Refresh the student data to get updated risk score
        const updatedStudent = await response.json();
        // Use universal refresh system to update the student list with new risk score
        refreshAIRisk(studentId);
      }
    } catch (error) {
      console.error("Failed to trigger AI risk recalculation:", error);
    }
  };

  // Function to sync database status with actual attendance
  const syncDatabaseStatus = async (studentId: string, lastCheckedIn: string | null) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Calculate the correct status based on last check-in
      let correctStatus = "Inactive";
      if (lastCheckedIn) {
        const lastCheckInDate = new Date(lastCheckedIn);
        const now = new Date();
        const weeksSinceLastCheckIn =
          (now.getTime() - lastCheckInDate.getTime()) /
          (1000 * 60 * 60 * 24 * 7);
        correctStatus = weeksSinceLastCheckIn <= 2 ? "Active" : "Inactive";
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studentId}/sync-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            status: correctStatus,
            last_checked_in: lastCheckedIn
          }),
        }
      );

      if (response.ok) {
        refreshStatus(studentId);
      }
    } catch (error) {
      console.error("Failed to sync database status:", error);
    }
  };

  // Remove the loading state check and merge it into the main render
  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6 min-w-0 max-w-full">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          type={notification.type}
          autoClose={notification.type !== "error"}
          onClose={() =>
            setNotification({
              show: false,
              message: "",
              title: "",
              type: "success",
            })
          }
        />

        <PlanUpgradeModal
          capability={upgradeModalCapability}
          onClose={() => setUpgradeModalCapability(null)}
        />

        {/* Expiring Payments Filter Banner */}
        {showExpiringPaymentsOnly && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Showing students with expiring payment methods
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    {expiringPaymentStudents.length} student{expiringPaymentStudents.length === 1 ? '' : 's'} found
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowExpiringPaymentsOnly(false);
                  setExpiringPaymentStudents([]);
                  navigate('/app/students');
                }}
                className="text-sm text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200 underline"
              >
                Show all students
              </button>
            </div>
          </div>
        )}

        {/* Header — toolbar flows from the left (avoids controls stranded on the far right on wide screens) */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Student Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage students and their memberships
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
            {/* Total Students Count */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {totalFilteredCount} student{totalFilteredCount !== 1 ? 's' : ''}
              </span>
              {searchInput.trim() || genderFilter !== 'all' || statusFilter !== 'all' || waiverFilter !== 'all' ? (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  (filtered)
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 xl:space-x-3">
              <button
                type="button"
                onClick={() => {
                  setEditingStudent(null);
                  setIsModalOpen(true);
                  setImagePreview("");
                  resetForm();
                }}
                disabled={isLoading}
                className={`inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                <span className="pointer-events-none">Add Student</span>
              </button>
              <button
                type="button"
                onClick={() => setIsImportModalOpen(true)}
                disabled={isLoading}
                className={`inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <ArrowUpTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                <span className="pointer-events-none">Import Students</span>
              </button>
              <button
                type="button"
                onClick={() => setIsExportModalOpen(true)}
                disabled={isLoading}
                className={`inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                <span className="pointer-events-none">Export Students</span>
              </button>
              {showDropoffRisk && (
              <button
                type="button"
                onClick={() => setIsRiskSettingsModalOpen(true)}
                disabled={isLoading}
                className={`inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <AdjustmentsHorizontalIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                <span className="pointer-events-none">Retention Risk Settings</span>
              </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-4">
          <StudentFilters
            genderFilter={genderFilter}
            statusFilter={statusFilter}
            waiverFilter={waiverFilter}
            classFilter={classFilter}
            billingModeFilter={billingModeFilter}
            canUseStripeBilling={canUseStripeBilling}
            sortField={sortField}
            sortDirection={sortDirection}
            classes={classes}
            onGenderFilterChange={handleGenderFilterChange}
            onStatusFilterChange={handleStatusFilterChange}
            onWaiverFilterChange={handleWaiverFilterChange}
            onClassFilterChange={handleClassFilterChange}
            onBillingModeFilterChange={handleBillingModeFilterChange}
            onSort={handleSort}
            disabled={isLoading}
          />
          
          {/* Search */}
          <div className="w-full">
            <input
              type="text"
              className={getInputClassName(
                "block w-full rounded-md border-0 py-2 pl-4 pr-4 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-base"
              )}
              placeholder="Search students by name, email, or phone..."
              value={searchInput}
              onChange={handleSearch}
              disabled={isLoading}
              autoFocus
              ref={(input) => {
                if (input && document.activeElement === document.body) {
                  input.focus();
                }
              }}
            />
          </div>

          {/* Secondary filters + bulk/delete controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Membership Filter */}
            <select
              value={membershipFilter}
              onChange={handleMembershipFilterChange}
              disabled={isLoading}
              className={getInputClassName(
                "rounded-md pl-3 pr-10 py-2 text-sm font-medium shadow-sm border-0 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer"
              )}
            >
              <option value="all">Any Membership</option>
              <option value="with_membership">With Active Membership</option>
              <option value="without_membership">Without Active Membership</option>
              {memberships.length > 0 && (
                <optgroup label="Active on selected type">
                  {memberships.map((m: { id: string; name: string }) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {/* Belt: curriculum sort or filter to one belt (from Settings → Belts) */}
            <select
              value={beltSortMode}
              onChange={handleBeltSortChange}
              disabled={isLoading}
              aria-label="Sort or filter by belt"
              className={getInputClassName(
                "rounded-md pl-3 pr-10 py-2 text-sm font-medium shadow-sm border-0 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer min-w-[11rem] max-w-[min(100vw-2rem,20rem)]"
              )}
            >
              <option value={BELT_SORT_NONE}>Belt: default</option>
              <option value={BELT_SORT_CURRICULUM}>Belt: curriculum order</option>
              <option value={BELT_SORT_CURRICULUM_DESC}>
                Belt: reverse curriculum
              </option>
              {belts.length > 0 && (
                <optgroup label="Students on belt">
                  {belts.map((b: { id: string; name: string }) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {/* Birthday Month Filter */}
            <select
              value={birthdayMonthFilter}
              onChange={handleBirthdayMonthFilterChange}
              disabled={isLoading}
              className={getInputClassName(
                "rounded-md pl-3 pr-10 py-2 text-sm font-medium shadow-sm border-0 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer"
              )}
            >
              <option value="all">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>

            {/* Delete Students / Exit Select Mode */}
            <button
              type="button"
              onClick={toggleSelectMode}
              className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                isSelectMode
                  ? "bg-gray-600 text-white hover:bg-gray-700"
                  : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <span className="pointer-events-none">{isSelectMode ? "Exit Select Mode" : "Delete Students"}</span>
            </button>

            {/* Bulk action buttons (only in select mode) */}
            {isSelectMode && (
              <>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={selectedStudents.size === 0}
                  className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                    selectedStudents.size === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  <TrashIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                  <span className="pointer-events-none">Delete Selected ({selectedStudents.size})</span>
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={selectedStudents.size === 0}
                  className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                    selectedStudents.size === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gray-600 text-white hover:bg-gray-700"
                  }`}
                >
                  <span className="pointer-events-none">Clear Selection</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Loading Message */}
        {(isLoading || isRefreshing) && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {isLoading ? "Loading student information..." : "Refreshing data..."}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {!isLoading && !isRefreshing && (
          <div className="mt-8 relative">
            {!isStripeConnected && (
              <StripeBillingGate
                canUseStripeBilling={canUseStripeBilling}
                isStripeConnected={isStripeConnected}
                variant="banner"
                collapsibleUpgrade
                upgradeCollapseStorageKey="students:stripeBillingUpgrade:collapsed"
              />
            )}

            <div className="overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                    <thead>
                      <tr>
                        <th className="py-3.5 pl-0 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          {isSelectMode && (
                            <input
                              type="checkbox"
                              checked={selectedStudents.size === students.length && students.length > 0}
                              onChange={() => {
                                if (selectedStudents.size === students.length) {
                                  clearSelection();
                                } else {
                                  selectAllStudents();
                                }
                              }}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-3"
                            />
                          )}
                          Name
                        </th>
                        <th className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          Contact
                        </th>
                        <th className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          Age
                        </th>
                        <th className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          <Tooltip
                            text={
                              <div className="space-y-2.5">
                                <p className="text-gray-300">
                                  Color shows how recently the student checked in.
                                </p>
                                <dl className="space-y-2">
                                  <div>
                                    <dt className="font-semibold text-white">Green</dt>
                                    <dd className="text-gray-300">
                                      Checked in within the last 2 weeks.
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">Red</dt>
                                    <dd className="text-gray-300">
                                      No check-in for over 2 weeks.
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">Never</dt>
                                    <dd className="text-gray-300">
                                      Hasn’t checked in yet.
                                    </dd>
                                  </div>
                                </dl>
                              </div>
                            }
                          >
                            <span className="cursor-help">Last Checked In</span>
                          </Tooltip>
                        </th>
                        <th className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          <Tooltip
                            text={
                              <div className="space-y-2.5">
                                <p className="text-gray-300">
                                  Billing state of the student’s memberships.
                                </p>
                                <dl className="space-y-2">
                                  <div>
                                    <dt className="font-semibold text-white">Active</dt>
                                    <dd className="text-gray-300">Billing normally.</dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">Cancels (date)</dt>
                                    <dd className="text-gray-300">
                                      Ends on that date; still a member until then.
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">Paused</dt>
                                    <dd className="text-gray-300">
                                      Billing paused; no payments collected.
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">Past due</dt>
                                    <dd className="text-gray-300">A payment failed.</dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">Cash / Manual</dt>
                                    <dd className="text-gray-300">Billed outside Stripe.</dd>
                                  </div>
                                  <div>
                                    <dt className="font-semibold text-white">No membership</dt>
                                    <dd className="text-gray-300">No current membership.</dd>
                                  </div>
                                </dl>
                              </div>
                            }
                          >
                            <span className="cursor-help">
                              Membership Status
                            </span>
                          </Tooltip>
                        </th>

                        {/* Emergency Contact column removed */}
                        {showDropoffRisk && (
                        <th className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          <Tooltip
                            text={
                              <RetentionRiskTooltipContent
                                onOpenSettings={() =>
                                  setIsRiskSettingsModalOpen(true)
                                }
                              />
                            }
                          >
                            <span className="cursor-help">
                              Retention Risk
                            </span>
                          </Tooltip>
                        </th>
                        )}
                        <th className="relative pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {students
                        .filter(student => 
                          !showExpiringPaymentsOnly || 
                          expiringPaymentStudents.includes(student.id)
                        )
                        .map((student) => (
                        <StudentTableRow
                          key={student.id}
                          student={student}
                          lastCheckedIn={lastCheckedInMap[student.id]}
                          cardExpiration={cardExpirationsByStudent[student.id]}
                          onEdit={handleEditClick}
                          onDelete={(s) => setStudentToDelete(s)}
                          formatPhoneNumber={(phone) =>
                            formatPhoneNumber(phone || "")
                          }
                          riskThresholds={riskThresholds}
                          showDropoffRisk={showDropoffRisk}
                          isSelectMode={isSelectMode}
                          isSelected={selectedStudents.has(student.id)}
                          onToggleSelection={toggleStudentSelection}
                          onRecordPayment={handleRecordPayment}
                          onViewPaymentHistory={handleViewPaymentHistory}
                          onOpenCurriculum={handleOpenCurriculum}
                          onOpenAttendanceHistory={handleOpenAttendanceHistory}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 space-x-2">
                <button
                  className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <span className="pointer-events-none">Previous</span>
                </button>
                <div className="space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`px-3 py-1 rounded font-medium border transition-colors duration-150 ${
                        currentPage === i + 1
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      <span className="pointer-events-none">{i + 1}</span>
                    </button>
                  ))}
                </div>
                <button
                  className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="pointer-events-none">Next</span>
                </button>
              </div>
            )}
          </div>
        )}

      {/* Crop Image Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-8">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500"
                  onClick={() => {
                    setShowCropModal(false);
                    setCropPosition({ x: 0, y: 0 });
                    setZoom(1);
                  }}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white mb-4">
                    Position Your Image
                  </h3>
                  
                  {/* Zoom Controls */}
                  <div className="mb-4 flex items-center justify-center space-x-4">
                    <button
                      onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                      className="p-2 bg-gray-200 dark:bg-gray-600 rounded-full hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50"
                      disabled={zoom <= 0.5}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[60px] text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                      className="p-2 bg-gray-200 dark:bg-gray-600 rounded-full hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50"
                      disabled={zoom >= 3}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Crop Area */}
                  <div className="relative w-80 h-80 mx-auto mb-4">
                    {/* Circular mask */}
                    <div className="absolute inset-0 rounded-full border-4 border-indigo-500 bg-transparent pointer-events-none z-10" />
                    
                    {/* Draggable image */}
                    <div 
                      className="absolute inset-0 overflow-hidden rounded-full cursor-move"
                      // touchAction none is what stops the browser claiming the gesture to
                      // scroll the page. It only applies to this circle, so the rest of the
                      // modal still scrolls normally, and it has no effect on a mouse.
                      style={{ touchAction: 'none' }}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent browser drag
                        setIsDragging(true);
                        setDragStart({ x: e.clientX - cropPosition.x, y: e.clientY - cropPosition.y });
                      }}
                      onMouseMove={(e) => {
                        if (isDragging) {
                          e.preventDefault(); // Prevent browser drag
                          const newX = e.clientX - dragStart.x;
                          const newY = e.clientY - dragStart.y;
                          setCropPosition({ x: newX, y: newY });
                        }
                      }}
                      onMouseUp={() => setIsDragging(false)}
                      onMouseLeave={() => setIsDragging(false)}
                      onTouchStart={(e) => {
                        // Ignore multi-finger gestures so a pinch does not read as a drag.
                        if (e.touches.length !== 1) return;
                        const touch = e.touches[0];
                        setIsDragging(true);
                        setDragStart({ x: touch.clientX - cropPosition.x, y: touch.clientY - cropPosition.y });
                      }}
                      onTouchMove={(e) => {
                        if (!isDragging) return;
                        // A second finger landing mid-drag ends it rather than jumping the
                        // image when that finger lifts again.
                        if (e.touches.length !== 1) {
                          setIsDragging(false);
                          return;
                        }
                        const touch = e.touches[0];
                        setCropPosition({
                          x: touch.clientX - dragStart.x,
                          y: touch.clientY - dragStart.y,
                        });
                      }}
                      onTouchEnd={() => setIsDragging(false)}
                      onTouchCancel={() => setIsDragging(false)}
                    >
                      <img
                        src={cropImage}
                        alt="Crop preview"
                        className="w-full h-full object-contain"
                        draggable={false} // Prevent browser drag
                        style={{
                          transform: `translate(${cropPosition.x}px, ${cropPosition.y}px) scale(${zoom})`,
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                          userSelect: 'none', // Prevent text selection
                          pointerEvents: 'none' // Prevent image-specific events
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Drag to move • Zoom to adjust
                    </div>
                    {/* Action buttons */}
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCropModal(false);
                          setCropPosition({ x: 0, y: 0 });
                          setZoom(1);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <span className="pointer-events-none">Cancel</span>
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                    

                            
                            // Create circular crop
                            const croppedImage = await createCircularCrop(cropImage, cropPosition.x, cropPosition.y, 280, zoom);
                            
                            if (croppedImage.length > MAX_STUDENT_IMAGE_BASE64_LENGTH) {
                                setNotification({
                                    show: true,
                                    message: "Image is too large. Please try cropping a smaller area or use a different image.",
                                    title: "Image Too Large",
                                    type: "error",
                                });
                                return;
                            }
                            
                            setImagePreview(croppedImage);
                            setFormData(prev => ({ ...prev, student_image: croppedImage }));
                            setShowCropModal(false);
                            setCropPosition({ x: 0, y: 0 });
                            setZoom(1);
                          } catch (error) {
                            console.error('Crop error:', error);
                            setNotification({
                              show: true,
                              message: "Failed to crop image. Please try again.",
                              title: "Crop Error",
                              type: "error",
                            });
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                      >
                        <span className="pointer-events-none">Apply Crop</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Student Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4 flex items-center space-x-2">
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false, false, false)}
                  disabled={isSaving || membershipLoading || membershipRequiresPaymentMethod}
                  className="hidden xl:inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <span className="pointer-events-none">{isSaving ? "Saving..." : membershipLoading ? "Loading..." : editingStudent ? "Update Student" : "Add Student"}</span>
                </button>
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500"
                  onClick={handleModalDismiss}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    {editingStudent ? "Edit Student" : "Add New Student"}
                    {membershipLoading && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full">
                        <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin mr-1"></div>
                        Loading memberships...
                      </span>
                    )}
                  </h3>
                  <form 
                    onSubmit={handleSubmit} 
                    className="mt-4 space-y-4"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && membershipLoading) {
                        e.preventDefault();
                        setNotification({
                          show: true,
                          message: "Please wait for memberships to finish loading before saving.",
                          title: "Memberships Loading",
                          type: "error",
                        });
                      }
                    }}
                  >
                    {/* Student Image Upload - Moved to top */}
                    <div>
                      <label
                        htmlFor="student_image"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Student Image
                      </label>
                      <div className="mt-1 flex items-center justify-between space-x-4">
                        {/* Left side: Image Preview and Upload Button */}
                        <div className="flex items-center space-x-4">
                          {/* Image Preview */}
                          <div className="flex-shrink-0">
                            {imagePreview ? (
                              <div className="relative">
                                <img
                                  src={imagePreview}
                                  alt="Student preview"
                                  className="h-24 w-24 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                                  style={{
                                    objectPosition: 'center',
                                    objectFit: 'cover'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={removeImage}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  <span className="pointer-events-none">×</span>
                                </button>
                              </div>
                            ) : (
                              <div className="h-24 w-24 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <UserIcon className="h-12 w-12 text-gray-500 dark:text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          {/* Upload Button */}
                          <div className="flex-1">
                            <input
                              type="file"
                              id="student_image"
                              name="student_image"
                              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                            <label
                              htmlFor="student_image"
                              className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              <ArrowUpTrayIcon className="h-4 w-4 mr-2 pointer-events-none" />
                              <span className="pointer-events-none">{imagePreview ? "Change Image" : "Upload Image"}</span>
                            </label>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              JPG, PNG, WEBP, HEIC - automatically compressed
                            </p>
                          </div>
                        </div>
                        
                        {/* Right side: Student Number (only show when editing) */}
                        {editingStudent && editingStudent.student_number && (
                          <div className="flex-shrink-0 flex items-center gap-2">
                            <span className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full font-mono font-bold text-xl">
                              #{editingStudent.student_number}
                            </span>
                            <Tooltip text="This unique number is the student's kiosk login code for quick self-check-in.">
                              <span className="sr-only">Student number info</span>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      {showDropoffRisk && editingStudent ? (
                        <div>
                          <Tooltip
                            text={
                              <RetentionRiskTooltipContent
                                onOpenSettings={() =>
                                  setIsRiskSettingsModalOpen(true)
                                }
                              />
                            }
                          >
                            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-help">
                              Retention Risk
                            </span>
                          </Tooltip>
                          {(() => {
                            const risk = editingStudent.ai_drop_off_risk || 0;
                            const percent = Math.round(Math.min(Math.max(risk, 0), 1) * 100);
                            const level =
                              risk < riskThresholds.medium
                                ? { label: "Low", bar: "bg-green-500", text: "text-green-700 dark:text-green-400" }
                                : risk < riskThresholds.high
                                ? { label: "Medium", bar: "bg-yellow-500", text: "text-yellow-700 dark:text-yellow-400" }
                                : { label: "High", bar: "bg-red-500", text: "text-red-700 dark:text-red-400" };
                            return (
                              <div className="mt-1 flex h-[38px] items-center gap-3">
                                <div
                                  className="h-2.5 flex-1 rounded-full bg-gray-200 dark:bg-gray-600"
                                  role="meter"
                                  aria-label="Retention risk"
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-valuenow={percent}
                                >
                                  <div
                                    className={`h-2.5 rounded-full ${level.bar}`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                                <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
                                  {percent}%
                                </span>
                                <span className={`text-xs font-medium ${level.text}`}>
                                  {level.label}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        // Keeps the field pairs below aligned when there's no risk to show
                        // (new student, or the plan doesn't include drop-off risk).
                        <div className="hidden sm:block" aria-hidden="true" />
                      )}
                      <div>
                        <Tooltip
                          text={
                            canUseStripeBilling
                              ? "Required to create a customer in Stripe and receive receipts and billing notifications."
                              : "Required for waivers, messaging, and studio communications."
                          }
                        >
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Primary Email <span className="text-red-500">*</span>
                          </label>
                        </Tooltip>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          required
                          value={formData.email || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      <div>
                        <Tooltip text="Optional parent or guardian email for studio communications. Choose this separately when sending from Messaging.">
                          <label
                            htmlFor="alternative_email"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Alternative Email
                          </label>
                        </Tooltip>
                        <input
                          type="email"
                          name="alternative_email"
                          id="alternative_email"
                          value={formData.alternative_email || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          id="phone"
                          value={formData.phone || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="gender"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="gender"
                          id="gender"
                          required
                          value={formData.gender || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="emergency_contact_name"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Emergency Contact Name
                        </label>
                        <input
                          type="text"
                          name="emergency_contact_name"
                          id="emergency_contact_name"
                          value={formData.emergency_contact_name || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="emergency_contact_phone"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Emergency Contact Phone
                        </label>
                        <input
                          type="tel"
                          name="emergency_contact_phone"
                          id="emergency_contact_phone"
                          value={formData.emergency_contact_phone || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="relationship_to_student"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Relationship to Student
                        </label>
                        <select
                          name="relationship_to_student"
                          id="relationship_to_student"
                          value={formData.relationship_to_student || ""}
                          onChange={handleInputChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        >
                          <option value="">Select Relationship</option>
                          <option value="Mother">Mother</option>
                          <option value="Father">Father</option>
                          <option value="Grandparent">Grandparent</option>
                          <option value="Guardian">Guardian</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Aunt/Uncle">Aunt/Uncle</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Other Family">Other Family</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Tooltip text="This birthdate is used to automatically calculate the student's age and enables birthday email notifications to be sent from the Dashboard on their special day.">
                          <label
                            htmlFor="birthdate"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Birthdate (MM/DD/YYYY)
                          </label>
                        </Tooltip>
                        <input
                          type="text"
                          name="birthdate"
                          id="birthdate"
                          value={formData.birthdate || ""}
                          onChange={handleInputChange}
                          placeholder="MM/DD/YYYY"
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label
                            htmlFor="belt"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Belt
                          </label>
                          <label
                            htmlFor="belt_size"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Belt Size
                          </label>
                        </div>
                        <div className="mt-1 flex gap-2">
                          <select
                            name="belt"
                            id="belt"
                            value={formData.belt || ""}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "block w-full flex-[7] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          >
                            <option value="">Select Belt</option>
                            {belts
                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                              .map((belt) => (
                                <option key={belt.id} value={belt.name}>
                                  {belt.name}
                                </option>
                              ))}
                          </select>
                          <input
                            type="text"
                            name="belt_size"
                            id="belt_size"
                            value={formData.belt_size || ""}
                            onChange={handleInputChange}
                            placeholder="Size"
                            className={getInputClassName(
                              "block w-full flex-[3] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        {editingStudent &&
                          editingStudent.belt &&
                          editingStudent.belt_last_changed && (
                            <p className="mt-1 text-xs text-gray-500 italic">
                              Last Changed:{" "}
                              {(() => {
                                const ymd = getLocalYMDFromUTC(
                                  editingStudent.belt_last_changed,
                                  studioTimezone
                                );
                                // Convert YYYY-MM-DD -> MM-DD-YYYY
                                return ymd && ymd.length === 10
                                  ? `${ymd.slice(5, 7)}-${ymd.slice(8, 10)}-${ymd.slice(0, 4)}`
                                  : ymd;
                              })()}{" "}
                              at{" "}
                              {formatTimeTo12Hour(
                                new Date(
                                  editingStudent.belt_last_changed
                                ).toLocaleTimeString("en-US", {
                                  hour12: false,
                                  timeZone: studioTimezone,
                                })
                              )}
                            </p>
                          )}
                      </div>
                      <div>
                        <label
                          htmlFor="staff_role"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Instructor Role
                        </label>
                        <select
                          name="staff_role"
                          id="staff_role"
                          value={formData.staff_role || ""}
                          onChange={handleInputChange}
                          disabled={staffRoles.length === 0}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          )}
                        >
                          <option value="">
                            {staffRoles.length === 0 ? "Loading roles..." : "No Instructor Role"}
                          </option>
                          {staffRoles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Assigning an instructor role will create this student as a staff member
                        </p>
                      </div>
                      {canUseStripeBilling && (
                      <div>
                        <Tooltip text="Students with the same Family ID can share payment methods. You can type, paste, or select an existing family ID. Generated IDs are globally unique.">
                          <label
                            htmlFor="family_id"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Family ID
                          </label>
                        </Tooltip>
                        <div className="flex gap-2">
                          <div className="relative flex-1 family-dropdown-container">
                            <input
                              type="text"
                              name="family_id"
                              id="family_id"
                              value={formData.family_id || ""}
                              onChange={(e) => {
                                handleInputChange(e);
                                // Clear existing timeout
                                if (familyIdValidationTimeoutRef.current) {
                                  clearTimeout(familyIdValidationTimeoutRef.current);
                                }
                                // Set new timeout for validation and family discount fetch
                                familyIdValidationTimeoutRef.current = setTimeout(() => {
                                  
                                  validateFamilyId(e.target.value);
                                  
                                  if (e.target.value) {
                                    // For existing students who already have this family_id saved in DB, fetch their actual discount
                                    if (editingStudent?.id && editingStudent.family_id === e.target.value) {
                                      fetchFamilyDiscount(e.target.value, editingStudent.id);
                                    } 
                                    // For new students OR students changing/adding family_id, calculate discount based on position
                                    else {
                                      fetchFamilyMembers(e.target.value);
                                    }
                                  } else {
                                    setFamilyDiscountPercent(0);
                                  }
                                }, 500);
                              }}
                              onPaste={(e) => {
                                // Allow paste and trigger validation after a short delay
                                setTimeout(() => {
                                  const pastedValue = (e.target as HTMLInputElement).value;
                                  if (pastedValue) {
                                    validateFamilyId(pastedValue);
                                  }
                                }, 100);
                              }}
                              onFocus={() => setShowFamilyDropdown(true)}
                              placeholder="Type, paste, or select existing family ID"
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                                familyIdValidation.isValid 
                                  ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100' 
                                  : 'border-red-300 dark:border-red-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                              }`}
                            />
                            {/* Validation indicator */}
                            {familyIdValidation.isChecking && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                            {!familyIdValidation.isChecking && formData.family_id && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                {familyIdValidation.isValid ? (
                                  familyIdValidation.message.includes('already exists') ? (
                                    // Warning icon for existing family
                                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    // Check icon for new family
                                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )
                                ) : (
                                  // X icon for errors
                                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            )}
                            {showFamilyDropdown && existingFamilies.length > 0 && (
                              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg border border-gray-300 dark:border-gray-600 rounded-md max-h-60 overflow-y-auto">
                                {existingFamilies.map((family) => (
                                  <button
                                    key={family.family_id}
                                    type="button"
                                    onClick={() => {
                                      handleFamilyIdChange(family.family_id);
                                      setShowFamilyDropdown(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {family.family_id}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {family.student_count} student{family.student_count !== 1 ? 's' : ''}: {family.students.map(s => s.name).join(', ')}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {(formData.family_id ||
                            (!familyIdDirty && editingStudent?.family_id)) && (
                            <button
                              type="button"
                              onClick={handleRemoveFromFamily}
                              className="mt-1 inline-flex items-center px-3 py-2 border border-red-300 dark:border-red-700 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Remove
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const response = await fetch(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/supabase/families/generate-id`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      Authorization: `Bearer ${getAuthToken()}`,
                                    },
                                  }
                                );

                                if (!response.ok) {
                                  const errorData = await response.json();
                                  throw new Error(errorData.error || "Failed to generate family ID");
                                }

                                const result = await response.json();
                                handleFamilyIdChange(result.family_id);
                                
                                setNotification({
                                  show: true,
                                  message: "Unique family ID generated successfully",
                                  title: "Success",
                                  type: "success",
                                });
                              } catch (error) {
                                setNotification({
                                  show: true,
                                  message: error instanceof Error ? error.message : "Failed to generate family ID",
                                  title: "Error",
                                  type: "error",
                                });
                              }
                            }}
                            className="mt-1 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Generate
                          </button>
                        </div>
                        {/* Validation message */}
                        {familyIdValidation.message && (
                          <p className={`mt-1 text-xs ${
                            familyIdValidation.isValid 
                              ? familyIdValidation.message.includes('already exists')
                                ? 'text-amber-600 dark:text-amber-400' // Amber for existing family (valid but warning)
                                : 'text-green-600 dark:text-green-400' // Green for new family
                              : 'text-red-600 dark:text-red-400' // Red for errors
                          }`}>
                            {familyIdValidation.message}
                          </p>
                        )}
                      </div>
                      )}

                      {/* Billing Mode Selector */}
                      <div>
                        <label
                          htmlFor="billing_mode"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Billing Mode
                        </label>
                        <select
                          name="billing_mode"
                          id="billing_mode"
                          value={
                            canUseStripeBilling
                              ? formData.billing_mode || "auto"
                              : "cash"
                          }
                          onChange={handleBillingModeChange}
                          className={getInputClassName(
                            "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        >
                          {canUseStripeBilling ? (
                            <option value="auto">💳 Auto Billing (Stripe)</option>
                          ) : (
                            <option value={UPGRADE_REQUIRED_OPTION_VALUE} disabled>
                              💳 {AUTO_BILLING_UPGRADE_OPTION_LABEL}
                            </option>
                          )}
                          <option value="cash">💵 Cash/Manual Billing</option>
                        </select>
                        {!canUseStripeBilling && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Cash/manual billing is included on your plan. Auto Billing with Stripe is shown above but requires a paid plan — upgrade from Billing settings to enable it.
                        </p>
                        )}
                        {canUseStripeBilling &&
                         editingStudent && 
                         (editingStudent.billing_mode !== 'cash' && editingStudent.billing_mode !== 'manual') &&
                         (formData.billing_mode === 'cash' || formData.billing_mode === 'manual') && (
                          <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                            <p className="text-xs font-semibold text-red-800 dark:text-red-200 flex items-start gap-2">
                              <span className="text-base">⚠️</span>
                              <span>
                                <strong>Important:</strong> If an active Stripe subscription exists, you MUST cancel their active Stripe subscriptions before switching to Cash/Manual Billing to avoid double charging. 
                                Uncheck all memberships above, save to cancel Stripe subscriptions, then switch to Cash/Manual Billing and re-add memberships.
                              </span>
                            </p>
                          </div>
                        )}
                      </div>

                    </div>
                    
                    {/* Assigned Classes and Notes in their own row */}
                    <div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <div className="flex items-center gap-1 mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Assigned Classes
                            </label>
                            <Tooltip text="Assigning students to classes helps you quickly identify regular attendees for each class. This enables bulk actions like sending emails or SMS to all students in a specific class, and makes it easier to manage class rosters and communications, as well as prevent students or leads from signing into the wrong class via kiosk mode.">
                              <div></div>
                            </Tooltip>
                          </div>
                          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-white dark:bg-gray-800">
                            {classes.length === 0 ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {classes.length === 0 && isLoading ? "Loading classes..." : "No classes available"}
                              </p>
                            ) : (
                              (() => {
                                // Function to get weekday order (Monday = 1, Sunday = 7)
                                const getWeekdayOrder = (dayName: string): number => {
                                  const dayMap: Record<string, number> = {
                                    'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
                                    'Friday': 5, 'Saturday': 6, 'Sunday': 7
                                  };
                                  return dayMap[dayName] || 8;
                                };

                                // Function to convert time to minutes for sorting
                                const timeToMinutes = (timeStr: string | null | undefined): number => {
                                  if (!timeStr) return 0;
                                  const [hours, minutes] = timeStr.split(':').map(Number);
                                  return hours * 60 + minutes;
                                };

                                // Function to format time for display
                                const formatTime = (timeStr: string | null | undefined): string => {
                                  if (!timeStr) return '';
                                  const [hours, minutes] = timeStr.split(':');
                                  const hour = parseInt(hours);
                                  const ampm = hour >= 12 ? 'PM' : 'AM';
                                  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                  return `${displayHour}:${minutes} ${ampm}`;
                                };

                                // Sort classes by weekday and time
                                const sortedClasses = [...classes].sort((a, b) => {
                                  // Get the first day for each class for sorting
                                  const aDay = a.days && a.days.length > 0 ? a.days[0] : '';
                                  const bDay = b.days && b.days.length > 0 ? b.days[0] : '';
                                  
                                  const aWeekday = getWeekdayOrder(aDay);
                                  const bWeekday = getWeekdayOrder(bDay);
                                  
                                  if (aWeekday !== bWeekday) {
                                    return aWeekday - bWeekday;
                                  }
                                  
                                  // If same day, sort by start time
                                  const aTime = timeToMinutes(a.start_time);
                                  const bTime = timeToMinutes(b.start_time);
                                  return aTime - bTime;
                                });

                                return sortedClasses.map((classItem) => {
                                  const isAssigned = formData.assigned_classes.includes(classItem.id);
                                  return (
                                    <label
                                      key={classItem.id}
                                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                                    >
                                      <div className="flex items-center space-x-2 flex-1">
                                        <input
                                          type="checkbox"
                                          checked={isAssigned}
                                          onChange={() => handleClassToggle(classItem.id)}
                                          disabled={isLoading}
                                          className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <div className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                                          <div className="font-medium">{classItem.class_name}</div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {classItem.days && classItem.days.length > 0 && (
                                              <span>
                                                {classItem.days.join(", ")}
                                                {(classItem.start_time || classItem.end_time) && (
                                                  <span className="ml-2">
                                                    {classItem.start_time && formatTime(classItem.start_time)}
                                                    {classItem.start_time && classItem.end_time && " - "}
                                                    {classItem.end_time && formatTime(classItem.end_time)}
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {isAssigned && (
                                        <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                          Assigned
                                        </span>
                                      )}
                                    </label>
                                  );
                                });
                              })()
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 mb-2">
                            <label
                              htmlFor="notes"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Notes
                            </label>
                            <Tooltip text={showDropoffRisk
                              ? "Write detailed notes about the student's behavior, attendance patterns, and engagement. The AI uses sentiment analysis to calculate a more accurate retention risk score based on your observations."
                              : "Optional notes about this student."}>
                              <div></div>
                            </Tooltip>
                          </div>
                          <textarea
                            name="notes"
                            id="notes"
                            value={formData.notes || ""}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full h-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm resize-y"
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Memberships + billing (left) and Family Management (right) */}
                    <div
                      className={`grid grid-cols-1 gap-4 lg:items-start ${
                        canUseStripeBilling && editingStudent?.family_id
                          ? "lg:grid-cols-2"
                          : ""
                      }`}
                    >
                    <div className="min-w-0">
                      {/* Memberships multi-select - show only when editing */}
                      {editingStudent && (
                        <div className="relative">
                          <Tooltip
                            text={
                              isLocalMembershipBilling
                                ? 'Tap to select multiple. Uncheck a membership and press "Update Student" to remove it from this student.'
                                : 'Tap to select multiple. Unchecking a membership, and pressing "Update Student" will cancel it at the end of the current billing period.'
                            }
                          >
                            <label
                              htmlFor="membership_type_ids"
                              className={`block text-sm font-medium ${
                                needsStripeForMemberships
                                  ? "text-gray-400 dark:text-gray-500"
                                  : "text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              Memberships
                              {needsStripeForMemberships && (
                                <span className="text-xs text-red-500 ml-1">(Requires Stripe)</span>
                              )}
                            </label>
                          </Tooltip>
                          <div className="mt-1 space-y-2">
                            {/* Loading overlay for memberships only */}
                            {membershipLoading && (
                              <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 z-10 flex items-center justify-center rounded-lg">
                                <div className="text-center">
                                  <div className="w-6 h-6 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-2"></div>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">Loading...</p>
                                </div>
                              </div>
                            )}
                            {/* Loading skeleton for memberships */}
                            {(isLoading || membershipLoading) && (
                              <>
                                {[1, 2, 3].map((i) => (
                                  <div key={i} className="flex items-center justify-between gap-2 p-2 border border-gray-200 dark:border-gray-600 rounded-md animate-pulse">
                                    <div className="flex items-center gap-2">
                                      <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                      <div className="h-4 w-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="h-5 w-16 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                      <div className="h-5 w-20 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                            {/* Actual memberships when loaded */}
                            {!isLoading && !membershipLoading && memberships.length === 0 && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                No memberships available. Please create memberships in your studio settings.
                              </div>
                            )}
                            {!isLoading && !membershipLoading && memberships.length > 0 && membershipAdditionsPaused && (
                              <p className="text-xs text-yellow-800 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md px-2 py-1.5">
                                Memberships are paused. Resume them before adding another.
                              </p>
                            )}
                            {!isLoading && !membershipLoading && memberships.length > 0 && memberships.map((membership) => {
                              const selected = (formData.membership_type_ids || []).includes(membership.id);
                              // Only show cancellation logic if we have loaded subscription data
                              const hasLoadedSubscriptionData = editingStudent?.subscription_details && editingStudent.subscription_details.length > 0;
                              
                              // Check if this membership has an active subscription in Stripe
                              const subscriptionDetail = editingStudent?.subscription_details?.find(
                                sub => sub.membership_type_id === membership.id
                              );
                              const hasActiveStripeSubscription = subscriptionDetail && 
                                (subscriptionDetail.status === "active" || subscriptionDetail.status === "canceling" || subscriptionDetail.isCanceling);
                              
                              // For Stripe: Only consider it "previously selected" if it has an active Stripe subscription
                              // For Cash/Manual: Consider it "previously selected" if it's in the saved membership_type_ids
                              const isCashManualBilling = formData.billing_mode === 'cash' || formData.billing_mode === 'manual';
                              const wasPreviouslySavedCashManual = isCashManualBilling &&
                                editingStudent?.membership_type_ids?.includes(membership.id);
                              // This must match the diff the save handler actually
                              // submits (editingStudent.membership_type_ids vs. the
                              // form's current selection), not the broader
                              // pendingMembershipChange.previouslySelectedIds set
                              // (which also counts a membership still "canceling" in
                              // Stripe as previously selected, for swap/proration
                              // purposes). Otherwise a membership already scheduled
                              // to cancel — already dropped from membership_type_ids
                              // by the billing sync — shows a false "Cancels
                              // Immediately" badge even though this save won't touch
                              // it at all.
                              const wasPreviouslySelected =
                                !!editingStudent?.membership_type_ids?.includes(
                                  membership.id
                                );

                              const willBeCanceled = wasPreviouslySelected && !selected;
                              const isNewlySelected = selected && !wasPreviouslySelected;
                              const isPastDueMembership =
                                editingStudent?.subscription_status === "past_due" &&
                                selected &&
                                !willBeCanceled;
                              const hasActiveSubscription = editingStudent?.subscription_status && 
                                (editingStudent.subscription_status === "active" || editingStudent.subscription_status === "canceling");
                              // Pausing the family billing primary only pauses the primary's
                              // own memberships; other members keep billing on the family
                              // subscription, so only this student's own status counts.
                              const showMembershipPausedState =
                                selected &&
                                !isNewlySelected &&
                                !willBeCanceled &&
                                editingStudent?.subscription_status === "paused";
                              const isPausedMembership = showMembershipPausedState;
                              const awaitingIndividualBilling =
                                !isCashManualBilling &&
                                selected &&
                                !hasActiveSubscription &&
                                !isNewlySelected &&
                                !isPausedMembership &&
                                !willBeCanceled &&
                                !formData.family_id;
                              
                              return (
                                <label key={membership.id} className={`flex flex-col gap-2 p-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer sm:flex-row sm:items-start sm:justify-between ${
                                  willBeCanceled 
                                    ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' 
                                    : 'border-gray-200 dark:border-gray-600'
                                }`}>
                                  <div className="flex items-start gap-2 min-w-0 flex-1">
                                    <input
                                      type="checkbox"
                                      className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                      disabled={
                                        needsStripeForMemberships ||
                                        membershipLoading ||
                                        (familyMembershipChangesBlocked && !selected) ||
                                        (membershipAdditionsPaused && !wasPreviouslySelected)
                                      }
                                      checked={selected}
                                      onChange={(e) => {
                                        const current = new Set(formData.membership_type_ids || []);
                                        if (e.target.checked) current.add(membership.id); else current.delete(membership.id);
                                        const values = Array.from(current);
                                        // Ensure no duplicates (extra safety)
                                        const uniqueValues = Array.from(new Set(values));
                                        setFormData(prev => ({
                                          ...prev,
                                          membership_type_ids: uniqueValues,
                                          // Don't set membership_type here - let backend compute it from IDs
                                        }));
                                        // Mark that user is making membership changes
                                        setUserMakingMembershipChanges(true);
                                        // Don't trigger proration calculation on checkbox change
                                        // Proration will be calculated when the form is actually saved
                                        setProrationData(null);
                                      }}
                                    />
                                    <span className={`text-sm leading-snug ${willBeCanceled ? 'text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-gray-200'}`}>
                                      {membership.name} - ${membership.price}/month
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap items-center justify-end gap-1.5 w-full min-w-0 sm:ml-auto sm:w-auto sm:max-w-none">
                                    {willBeCanceled && (
                                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                        {isCashManualBilling
                                          ? "Will Remove"
                                          : pendingMembershipChange.removalsAreImmediate
                                            ? "Cancels Immediately"
                                            : subscriptionDetail?.current_period_end
                                              ? `Active Until ${new Date(
                                                  subscriptionDetail.current_period_end * 1000
                                                ).toLocaleDateString(undefined, {
                                                  month: "short",
                                                  day: "numeric",
                                                })}`
                                              : "Active Until Period End"}
                                      </span>
                                    )}

                                    {/* For Cash/Manual billing with previously saved membership, show Active */}
                                    {isCashManualBilling && wasPreviouslySavedCashManual && selected && (
                                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                        Active
                                      </span>
                                    )}
                                    
                                    {/* For newly selected memberships (both Stripe and Cash/Manual) */}
                                    {isNewlySelected && (
                                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                                        Will Add
                                      </span>
                                    )}
                                    
                                    {/* For paused consolidated/per-student memberships */}
                                    {isPausedMembership && (
                                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400">
                                        Paused
                                      </span>
                                    )}
                                    
                                    {/* Membership assigned but no Stripe subscription yet */}
                                    {awaitingIndividualBilling && (
                                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                                        {membershipRequiresPaymentMethod
                                          ? "Payment method required"
                                          : "Awaiting billing setup"}
                                      </span>
                                    )}

                                    {/* Family consolidated payment failed */}
                                    {isPastDueMembership && !isNewlySelected && !isPausedMembership && (
                                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                        Payment failed
                                      </span>
                                    )}

                                    {/* For Stripe with active subscription */}
                                    {!isCashManualBilling && hasActiveSubscription && hasActiveStripeSubscription && editingStudent?.subscription_status && !willBeCanceled && !isNewlySelected && !isPausedMembership && !awaitingIndividualBilling && !isPastDueMembership && (() => {
                                      // Find subscription details for this membership
                                      const subscriptionDetail = editingStudent.subscription_details?.find(
                                        sub => sub.membership_type_id === membership.id
                                      );

                                      // Check if this subscription is scheduled to cancel
                                      const isScheduledToCancel = subscriptionDetail?.isCanceling;

                                      // Unchecked memberships only reach here when they're
                                      // already scheduled to cancel in Stripe but this save
                                      // isn't touching them — show the read-only "Cancels on"
                                      // badge (plus the uncancel action below) rather than
                                      // nothing, so staff can see/undo a pending cancellation
                                      // that predates this edit session.
                                      if (!selected && !isScheduledToCancel) {
                                        return null;
                                      }
                                      
                                      return (
                                        <>
                                          {loadingSubscriptionSync ? (
                                            <span className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-gray-200 dark:bg-gray-600 animate-pulse">
                                              <div className="w-24 h-4 bg-gray-300 dark:bg-gray-500 rounded"></div>
                                            </span>
                                          ) : subscriptionDetail ? (
                                            <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug ${
                                              isScheduledToCancel
                                                ? "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300"
                                                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                            }`}>
                                              {formatBillingPeriod(subscriptionDetail.calculated_billing_period_end, subscriptionDetail.isCanceling)}
                                            </span>
                                          ) : null}
                                          {!isScheduledToCancel && (
                                            <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                              {editingStudent.subscription_status.charAt(0).toUpperCase() + editingStudent.subscription_status.slice(1)}
                                            </span>
                                          )}
                                          {/* Already taken off the Stripe subscription before
                                              renewal: nothing left to uncancel. Staff can add
                                              the membership again as a new one if needed. */}
                                          {isScheduledToCancel && !subscriptionDetail?.removed_from_billing && (
                                            <button
                                              type="button"
                                              disabled={uncancelingMembershipId === membership.id}
                                              onClick={async () => {
                                                if (!editingStudent?.id) return;
                                                
                                                setUncancelingMembershipId(membership.id);
                                                
                                                try {
                                                  const response = await fetch(
                                                    `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${editingStudent.id}/uncancel`,
                                                    {
                                                      method: "POST",
                                                      headers: {
                                                        "Content-Type": "application/json",
                                                        Authorization: `Bearer ${getAuthToken()}`,
                                                      },
                                                      body: JSON.stringify({
                                                        membership_type_id: membership.id,
                                                      }),
                                                    }
                                                  );
                                                  
                                                  if (!response.ok) {
                                                    const errorData = await response.json();
                                                    throw new Error(errorData.error || "Failed to uncancel subscription");
                                                  }
                                                  
                                                  const result = await response.json();
                                                  
                                                  setNotification({
                                                    show: true,
                                                    message: result.message,
                                                    title: "Subscription Uncancelled",
                                                    type: "success",
                                                  });
                                                  
                                                  if (isStripeConnected && editingStudent?.id) {
                                                    try {
                                                      const syncResponse = await fetch(
                                                        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${editingStudent.id}/sync-subscription-status`,
                                                        {
                                                          method: "POST",
                                                          headers: {
                                                            "Content-Type": "application/json",
                                                            Authorization: `Bearer ${getAuthToken()}`,
                                                          },
                                                        }
                                                      );
                                                      if (syncResponse.ok) {
                                                        const syncResult = await syncResponse.json();
                                                        setEditingStudent(prev => prev ? {
                                                          ...prev,
                                                          subscription_status: syncResult.status ?? prev.subscription_status,
                                                          membership_type_ids:
                                                            Array.isArray(syncResult.active_membership_ids) &&
                                                            syncResult.active_membership_ids.length > 0
                                                              ? syncResult.active_membership_ids
                                                              : prev.membership_type_ids ?? [],
                                                          membership_type: syncResult.membership_type ?? prev.membership_type,
                                                          subscription_details: syncResult.subscription_details || prev.subscription_details || [],
                                                          subscription_cancel_at: undefined,
                                                        } : null);
                                                      }
                                                    } catch (syncError) {
                                                      console.error("Failed to sync after uncancel:", syncError);
                                                    }
                                                  } else {
                                                    setEditingStudent(prev => {
                                                      if (!prev) return prev;
                                                      return {
                                                        ...prev,
                                                        subscription_details: prev.subscription_details?.map(sub => 
                                                          sub.membership_type_id === membership.id 
                                                            ? { ...sub, isCanceling: false, cancel_at_period_end: false }
                                                            : sub
                                                        ) || [],
                                                        subscription_cancel_at: undefined,
                                                      };
                                                    });
                                                  }
                                                  
                                                } catch (error) {
                                                  setNotification({
                                                    show: true,
                                                    message: error instanceof Error ? error.message : "Failed to uncancel subscription",
                                                    title: "Error",
                                                    type: "error",
                                                  });
                                                } finally {
                                                  setUncancelingMembershipId(null);
                                                }
                                              }}
                                              className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug ${
                                                uncancelingMembershipId === membership.id
                                                  ? "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                                  : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 cursor-pointer"
                                              }`}
                                            >
                                              {uncancelingMembershipId === membership.id ? (
                                                <>
                                                  <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin mr-1"></div>
                                                  Uncancel...
                                                </>
                                              ) : (
                                                "Uncancel"
                                              )}
                                            </button>
                                          )}
                                        </>
                                      );
                                    })()}
                                    
                                    {/* Show reactivation option for cancelled memberships */}
                                    {!selected && wasPreviouslySelected && editingStudent?.subscription_status === "canceled" && (() => {
                                      return (
                                        <>
                                          <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
                                            Cancelled
                                          </span>
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              if (!editingStudent?.id) return;
                                              
                                              try {
                                                const response = await fetch(
                                                  `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions/${editingStudent.id}/reactivate`,
                                                  {
                                                    method: "POST",
                                                    headers: {
                                                      "Content-Type": "application/json",
                                                      Authorization: `Bearer ${getAuthToken()}`,
                                                    },
                                                    body: JSON.stringify({
                                                      membership_type_id: membership.id,
                                                    }),
                                                  }
                                                );
                                                
                                                if (!response.ok) {
                                                  const errorData = await response.json();
                                                  throw new Error(errorData.error || "Failed to reactivate subscription");
                                                }
                                                
                                                const result = await response.json();
                                                
                                                setNotification({
                                                  show: true,
                                                  message: result.message,
                                                  title: "Subscription Reactivated",
                                                  type: "success",
                                                });
                                                
                                                // Refresh the student data
                                                refreshMembership(editingStudent.id);
                                                
                                                // Update the form to show the membership as selected
                                                setFormData(prev => ({
                                                  ...prev,
                                                  membership_type_ids: [...(prev.membership_type_ids || []), membership.id]
                                                }));
                                                
                                              } catch (error) {
                                                setNotification({
                                                  show: true,
                                                  message: error instanceof Error ? error.message : "Failed to reactivate subscription",
                                                  title: "Error",
                                                  type: "error",
                                                });
                                              }
                                            }}
                                            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-snug bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 cursor-pointer"
                                          >
                                            Reactivate
                                          </button>
                                        </>
                                      );
                                    })()}
                                    

                                  </div>
                                </label>
                              );
                            })}
                            {canConsolidateIndividualBilling && (
                              <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-2 dark:border-blue-700 dark:bg-blue-900/20">
                                <p className="mb-1.5 text-[10px] leading-snug text-blue-700 dark:text-blue-300">
                                  This student has more than one live Stripe subscription.
                                  Consolidate to one monthly bill. If they owe a gap to align
                                  dates, charge it as a One-Time Payment first.
                                </p>
                                <button
                                  type="button"
                                  disabled={individualBillingConsolidateLoading}
                                  onClick={() => runIndividualBillingConsolidate()}
                                  className="rounded-md border border-blue-300 bg-white px-2 py-1 text-[10px] font-medium text-blue-800 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-100"
                                >
                                  {individualBillingConsolidateLoading
                                    ? 'Checking...'
                                    : 'Consolidate billing'}
                                </button>
                              </div>
                            )}
                            {lastPaymentDateForStudent != null && (
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1">
                                Last payment: {new Date(lastPaymentDateForStudent + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                            {lastPaymentDateFetched && lastPaymentDateForStudent === null && (
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1">
                                Last payment: Never
                              </p>
                            )}
                            {/* Membership Change Summary */}
                            {editingStudent && isStripeConnected && (
                              (() => {
                                const previousMembershipIds = editingStudent?.membership_type_ids || [];
                                const newMembershipIds = formData.membership_type_ids || [];
                                const addedMemberships = newMembershipIds.filter(id => !previousMembershipIds.includes(id));
                                const removedMemberships = previousMembershipIds.filter(id => !newMembershipIds.includes(id));
                                
                                if (addedMemberships.length === 0 && removedMemberships.length === 0) {
                                  return null;
                                }
                                
                                return (
                                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
                                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                                      Membership Changes Summary
                                    </h4>
                                    <div className="space-y-1 text-xs text-blue-700 dark:text-blue-300">
                                    {pendingMembershipChange.isOneForOneSwap ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-blue-600 dark:text-blue-400">→</span>
                                        <span>
                                          Change:{' '}
                                          {memberships.find((m) => m.id === pendingMembershipChange.removals[0])?.name}
                                          {' '}to{' '}
                                          {memberships.find((m) => m.id === pendingMembershipChange.additions[0])?.name}
                                        </span>
                                      </div>
                                    ) : (
                                      <>
                                      {addedMemberships.length > 0 && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-green-600 dark:text-green-400">✓</span>
                                          <span>Add: {addedMemberships.map(id => memberships.find(m => m.id === id)?.name).filter(Boolean).join(', ')}</span>
                                        </div>
                                      )}
                                      {removedMemberships.length > 0 && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-red-600 dark:text-red-400">✗</span>
                                          <span>
                                            Cancel: {removedMemberships.map(id => memberships.find(m => m.id === id)?.name).filter(Boolean).join(', ')}
                                            {pendingMembershipChange.removalsAreImmediate
                                              ? ' (immediately)'
                                              : ' (stays active until the end of the paid period)'}
                                          </span>
                                        </div>
                                      )}
                                      </>
                                    )}
                                    </div>
                                  </div>
                                );
                              })()
                            )}

                            {/* Cancellation Type Selection (Stripe auto-billing only) */}
                            {!isLocalMembershipBilling && (() => {
                              const previousMembershipIds = editingStudent?.membership_type_ids || [];
                              const newMembershipIds = formData.membership_type_ids || [];
                              const removedMemberships = previousMembershipIds.filter(id => !newMembershipIds.includes(id));
                              
                              if (removedMemberships.length === 0) return null;
                              
                              return (
                                <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                                  {pendingMembershipChange.isOneForOneSwap ? (
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                      The new rate starts on the next invoice. Nothing is charged when you save.
                                      To cancel and start a new membership with a charge, save the cancellation first, then add the new one.
                                    </p>
                                  ) : pendingMembershipChange.additions.length > 0 ? (
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                      Because a new membership is replacing it, the cancelled
                                      membership ends immediately so the student is not billed
                                      for both at once.
                                    </p>
                                  ) : (
                                    <label className="flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-yellow-300 rounded"
                                        checked={formData.cancelImmediately || false}
                                        onChange={(e) => setFormData(prev => ({ ...prev, cancelImmediately: e.target.checked }))}
                                      />
                                      Cancel all selected memberships immediately (instead of at period end)
                                    </label>
                                  )}
                                  {pendingMembershipChange.isOneForOneSwap ? null : (
                                  <p className="text-xs text-yellow-600 dark:text-yellow-300 mt-1">
                                    Immediate cancellation will stop billing right away for all cancelled memberships but may not provide refunds. To issue a refund to a student, please log into your Stripe dashboard and process the refund there.
                                  </p>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          {needsStripeForMemberships && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {canUseStripeBilling
                                ? 'Connect your Stripe account in Settings → Integrations to enable automatic membership billing.'
                                : 'Upgrade to a paid plan to enable automatic Stripe membership billing.'}
                            </p>
                          )}
                          
                          {/* Cash Billing Pricing Summary - for both new and existing students */}
                          {(formData.billing_mode === 'cash' || formData.billing_mode === 'manual') && (
                            (() => {
                              const selectedMembershipIds = formData.membership_type_ids || [];
                              
                              
                              if (selectedMembershipIds.length === 0) {
                                return null;
                              }
                              
                              const selectedMemberships = selectedMembershipIds
                                .map(id => memberships.find(m => m.id === id))
                                .filter(Boolean);
                              
                              // Calculate prices with family discount if applicable
                              let membershipPrices = selectedMemberships.map(m => ({
                                ...m,
                                originalPrice: parseFloat(m.price || '0'),
                                discountedPrice: parseFloat(m.price || '0'),
                                discountApplied: false,
                                discountPercent: 0
                              }));
                              
                              // Apply family discount if student is in a family and has a position > 1
                              if (familyDiscountPercent > 0) {
                                membershipPrices = membershipPrices.map(mp => ({
                                  ...mp,
                                  discountedPrice: mp.originalPrice * (1 - familyDiscountPercent / 100),
                                  discountApplied: true,
                                  discountPercent: familyDiscountPercent
                                }));
                              }
                              
                              const totalPrice = membershipPrices.reduce((sum, mp) => sum + mp.discountedPrice, 0);
                              const hasDiscount = membershipPrices.some(mp => mp.discountApplied);
                              
                              return (
                                <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md">
                                  <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center gap-2">
                                    <span>💵</span>
                                    <span>Cash/Manual Billing - Pricing Summary</span>
                                    {hasDiscount && (
                                      <span className="ml-auto px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                                        {familyDiscountPercent}% Family Discount
                                      </span>
                                    )}
                                  </h4>
                                  <div className="space-y-2">
                                    {membershipPrices.map((mp: any) => (
                                      <div key={mp.id} className="flex justify-between items-center text-sm text-green-900 dark:text-green-100">
                                        <span>{mp.name}</span>
                                        <div className="flex items-center gap-2">
                                          {mp.discountApplied && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 line-through">
                                              ${mp.originalPrice.toFixed(2)}
                                            </span>
                                          )}
                                          <span className="font-medium">${mp.discountedPrice.toFixed(2)}/month</span>
                                        </div>
                                      </div>
                                    ))}
                                    {selectedMemberships.length > 1 && (
                                      <div className="pt-2 mt-2 border-t border-green-200 dark:border-green-700 flex justify-between items-center font-semibold text-green-900 dark:text-green-100">
                                        <span>Total Monthly Amount</span>
                                        <span className="text-lg">${totalPrice.toFixed(2)}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700 text-xs text-green-700 dark:text-green-300">
                                    <p className="flex items-start gap-2">
                                      <span>ℹ️</span>
                                      <span>This is the monthly amount to collect from the student. Use the "💵 Record Cash Membership Payment" button to log payments when received.</span>
                                    </p>
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      )}
                      
                      {/* Payment Status Display - moved to replace Current Memberships */}
                      <div className="mt-2">
                                            {editingStudent &&
                        (loadingFailedPayments || failedPayments !== null) && (
                          <div>
                            {loadingFailedPayments ? (
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <div className="h-4 w-4 bg-gray-400 rounded-full animate-spin"></div>
                                Loading payment information...
                              </div>
                            ) : (failedPayments?.count || 0) > 0 ||
                              editingStudent.subscription_status === "past_due" ? (
                              <div className="mt-2">
                                {/* Customer Portal Link for Outstanding Payments */}
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <div className="ml-3 flex-1">
                                      <h3 className="text-sm font-medium text-red-600 dark:text-red-400">
                                        Outstanding Payments Detected
                                      </h3>
                                      <div className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                                        {hasFamilyBillingPastDue
                                          ? "The family consolidated payment failed. Membership access is suspended until payment is resolved on the billing primary's account."
                                          : "This student has failed payments that need attention."}
                                      </div>
                                      <div className="mt-2">
                                      {formData.billing_mode === "family" &&
                                      formData.billing_guardian_id ? (
                                        <p className="text-xs text-amber-700 dark:text-amber-300">
                                          Billed through{" "}
                                          {familyMembers.find(
                                            (member) =>
                                              member.id === formData.billing_guardian_id
                                          )?.name || "the billing primary"}
                                          . Use their payment portal to resolve outstanding
                                          family payments.
                                        </p>
                                      ) : hasFamilyBillingPastDue ? (
                                        <button
                                          type="button"
                                          onClick={() => handleGenerateCustomerPortalLink(editingStudent)}
                                          disabled={portalLoading}
                                          className="text-sm bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-3 py-1 rounded border border-amber-600 dark:border-amber-400 hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {portalLoading ? (
                                            <>
                                              <div className="w-3 h-3 border border-amber-600 border-t-transparent rounded-full animate-spin mr-1"></div>
                                              Opening...
                                            </>
                                          ) : (
                                            "Open Customer Portal"
                                          )}
                                        </button>
                                      ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleGenerateCustomerPortalLink(editingStudent)}
                                        disabled={portalLoading}
                                        className="text-sm bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-3 py-1 rounded border border-amber-600 dark:border-amber-400 hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {portalLoading ? (
                                          <>
                                            <div className="w-3 h-3 border border-amber-600 border-t-transparent rounded-full animate-spin mr-1"></div>
                                            Opening...
                                          </>
                                        ) : (
                                          "Open Customer Portal"
                                        )}
                                      </button>
                                      )}
                                      </div>
                                      <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                        The customer portal allows secure payment method updates and retry of failed payments.
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="text-sm text-green-700 dark:text-green-400">
                                  ✓ No outstanding payments - all payments are current!
                                </div>
                                {/* Pause/Resume Membership Button */}
                                {editingStudent &&
                                  editingStudent.membership_type_ids &&
                                  editingStudent.membership_type_ids.length > 0 && (
                                  editingStudent.subscription_status === "active" ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPausingStudent(editingStudent);
                                        setShowPauseModal(true);
                                      }}
                                      className="inline-flex shrink-0 items-center self-start px-3 py-1.5 border border-yellow-400 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-600 rounded text-sm font-medium transition-colors sm:self-auto"
                                      title="Pause Membership(s)"
                                    >
                                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                                      </svg>
                                      Pause
                                    </button>
                                  ) : editingStudent.subscription_status === "paused" ? (
                                    <button
                                      type="button"
                                      onClick={() => handleResumeClick(editingStudent)}
                                      className="inline-flex shrink-0 items-center self-start px-3 py-1.5 border border-green-400 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-100 dark:hover:bg-green-600 rounded text-sm font-medium transition-colors sm:self-auto"
                                      title="Resume Membership(s)"
                                    >
                                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <polygon points="8,5 19,12 8,19" />
                                      </svg>
                                      Resume
                                    </button>
                                  ) : null
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>



                      {/* Proration Display - Only for auto-billing students */}
                      {/* Show proration and credit balance information */}
                      {formData.billing_mode !== 'cash' && formData.billing_mode !== 'manual' && (
                        <div className="mt-2">
                          <ProrationSummary
                            creditBalance={creditBalance}
                            prorationData={prorationData}
                            multiProrationData={multiProrationData}
                            // Only spin for an actual membership-change preview; the credit
                            // balance loads on every card open and has its own loading state
                            // in the Account Balance panel.
                            isCalculating={isCalculatingProration || isCalculatingMultiProration}
                            showCreditBalance={false}
                            showProration={false}
                            showCancellationCredit={false}
                            showMultiProration={true}
                            chargeTiming={
                              hasMidCycleMembershipProration
                                ? 'immediate'
                                : 'next_invoice'
                            }
                            familyBillingPrimaryName={familyBillingPrimaryDisplayName}
                          />
                        
                        {/* Balance Adjustment Button */}
                        {/* Wait for the family billing summary so a non-primary member
                            doesn't briefly see the balance controls before the notice. */}
                        {editingStudent && editingStudent.id && hasPaymentMethod &&
                          !(editingStudent.family_id && familyBillingSummaryLoading) && (
                          restrictBalanceAdjustmentToPrimary ? (
                            <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-md dark:bg-indigo-900/20 dark:border-indigo-800">
                              <p className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                                Account balance
                              </p>
                              <p className="mt-1 text-sm text-indigo-800 dark:text-indigo-200">
                                Credits and debits apply to the family invoice and must be
                                adjusted on{" "}
                                {familyBillingPrimaryName ? (
                                  <>
                                    <span className="font-medium">
                                      {familyBillingPrimaryName}
                                    </span>
                                    &apos;s account (billing primary).
                                  </>
                                ) : (
                                  <>the billing primary&apos;s account.</>
                                )}
                              </p>
                            </div>
                          ) : (
                          <div className="mt-3 flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Account Balance</p>
                              <p className="text-lg font-semibold">
                                {isLoadingCreditBalance ? (
                                  <span className="text-gray-400">Loading...</span>
                                ) : creditBalance && creditBalance.raw_balance < 0 ? (
                                  <span className="text-green-600">
                                    ${(creditBalance.balance / 100).toFixed(2)} credit
                                  </span>
                                ) : creditBalance && creditBalance.raw_balance > 0 ? (
                                  <span className="text-red-600">
                                    ${(creditBalance.raw_balance / 100).toFixed(2)} owed
                                  </span>
                                ) : (
                                  <span className="text-gray-600">$0.00</span>
                                )}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowBalanceAdjustmentModal(true)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                            >
                              Adjust Balance
                            </button>
                          </div>
                          )
                        )}
                        </div>
                      )}

                      {/* Loading indicator for proration calculation (kept for backwards compatibility) */}
                      {false && isCalculatingProration && (
                        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="h-5 w-5 bg-gray-200 rounded-full animate-spin"></div>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-gray-600">
                                Calculating proration...
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Payment Method Required Warning (only for auto-billing students) */}
                      {membershipRequiresPaymentMethod && !billsThroughFamilyPrimary && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-5 w-5 text-blue-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">
                                Payment Method Required
                              </h3>
                              <div className="mt-2 text-sm text-blue-700">
                                <p>
                                  A payment method is required to activate
                                  this membership. Please add a payment method
                                  below before saving the student.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {billsThroughFamilyPrimary &&
                        hasSelectedMembership &&
                        familyBillingPrimaryDisplayName && (
                        <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                          <p className="text-sm text-indigo-800">
                            Membership billing will use{" "}
                            <span className="font-medium">
                              {familyBillingPrimaryDisplayName}&apos;s
                            </span>{" "}
                            payment method. No card is required for this student.
                          </p>
                          {familyConsolidatedMidCycleImmediateCharge &&
                            hasMidCycleMembershipProration && (
                              <p className="mt-2 text-xs text-indigo-700">
                                New memberships charge the partial period immediately on{" "}
                                {familyBillingPrimaryDisplayName}&apos;s card. The next family
                                renewal will only include the regular monthly total.
                              </p>
                            )}
                        </div>
                      )}
                    </div>

                    {/* Family Management Column */}
                    <div className="min-w-0">
                      {canUseStripeBilling && editingStudent?.family_id && (
                        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                          {familyConsolidatedBillingEnabled ? (
                            <Tooltip text="One bill, one renewal date: all family memberships renew together on the billing primary's date. Mid-cycle adds charge the partial period immediately; renewals stay predictable.">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                Family Management
                              </label>
                            </Tooltip>
                          ) : (
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Family Management
                            </label>
                          )}
                          <div className="space-y-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Family ID: {editingStudent.family_id}
                            </div>
                            
                            {familyDataLoading ? (
                              <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                                <div className="space-y-2">
                                  {[...Array(3)].map((_, i) => (
                                    <div key={i} className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <>
                                {familyMembers.length > 0 && (
                                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Family Members:
                                    </div>
                                    <div className="space-y-1">
                                      {familyMembers.map((member) => (
                                        <div key={member.id} className="text-xs text-gray-600 dark:text-gray-400">
                                          <span>{member.name}</span>
                                          {isFamilyBillingPrimaryMember(member) && (
                                            <span className="ml-2 text-indigo-600 dark:text-indigo-400">
                                              Billing primary
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {formData.billing_mode === "family" &&
                                  formData.billing_guardian_id &&
                                  familyBillingPrimaryName && (
                                    <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-200">
                                      Billed through {familyBillingPrimaryName}
                                    </div>
                                  )}

                                {formData.family_id &&
                                  isFamilyBillingPrimaryMember(formData) && (
                                    <div className="mt-3 flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
                                      <span>This student is the family billing primary.</span>
                                      {familyConsolidatedBillingEnabled && (
                                        <Tooltip text="If this billing primary leaves the family, another member becomes the billing primary. This student's memberships end on their profile. Everyone else stays on one family bill with the same renewal date—billing does not restart.">
                                          <span className="inline-flex items-center" aria-label="Billing primary departure info" />
                                        </Tooltip>
                                      )}
                                    </div>
                                  )}

                                {(familyBillingSummaryLoading ||
                                  familyConsolidatedBillingSummary) && (
                                  <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-900/20">
                                    <div className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-2">
                                      Consolidated Family Billing
                                      {familyConsolidatedBillingSummary?.subscription_status ===
                                        "past_due" && (
                                        <span className="ml-2 inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                          Payment failed
                                        </span>
                                      )}
                                    </div>
                                    {familyBillingSummaryLoading ? (
                                      <div className="text-xs text-blue-700 dark:text-blue-200">
                                        Loading billing breakdown...
                                      </div>
                                    ) : familyConsolidatedBillingSummary ? (
                                      <div className="space-y-2">
                                        {familyConsolidatedBillingSummary.line_items.map(
                                          (lineItem) => (
                                            <div
                                              key={`${lineItem.student_id}-${lineItem.membership_type_id}`}
                                              className="flex items-start justify-between gap-2 text-xs text-blue-800 dark:text-blue-200"
                                            >
                                              <span>
                                                {lineItem.student_name}: {lineItem.membership_name}
                                                {lineItem.is_canceling && lineItem.cancel_at && (
                                                  <span className="ml-1 text-amber-700 dark:text-amber-300">
                                                    (cancels{" "}
                                                    {new Date(
                                                      lineItem.cancel_at * 1000
                                                    ).toLocaleDateString("en-US", {
                                                      month: "short",
                                                      day: "numeric",
                                                      year: "numeric",
                                                    })}
                                                    )
                                                  </span>
                                                )}
                                              </span>
                                              <span className="whitespace-nowrap">
                                                {lineItem.discount_cents > 0 ? (
                                                  <>
                                                    <span className="line-through opacity-70">
                                                      $
                                                      {(
                                                        lineItem.unit_amount_cents / 100
                                                      ).toFixed(2)}
                                                    </span>{" "}
                                                    $
                                                    {(
                                                      lineItem.net_amount_cents / 100
                                                    ).toFixed(2)}
                                                  </>
                                                ) : (
                                                  <>${(lineItem.net_amount_cents / 100).toFixed(2)}</>
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        {(familyConsolidatedBillingSummary.paused_members || []).map(
                                          (pausedMember) => (
                                            <div
                                              key={`paused-${pausedMember.student_id}`}
                                              className="flex items-start justify-between gap-2 text-xs text-blue-800 dark:text-blue-200"
                                            >
                                              <span>
                                                {pausedMember.student_name}:{" "}
                                                <span className="text-amber-700 dark:text-amber-300">
                                                  Memberships paused
                                                </span>
                                              </span>
                                              <span className="whitespace-nowrap opacity-70">$0.00</span>
                                            </div>
                                          )
                                        )}
                                        <div className="border-t border-blue-200 pt-2 space-y-1 text-xs font-medium text-blue-900 dark:border-blue-700 dark:text-blue-100">
                                          <div>
                                            Monthly recurring total: $
                                            {(
                                              (familyConsolidatedBillingSummary.monthly_recurring_total_cents ??
                                                familyConsolidatedBillingSummary.line_items.reduce(
                                                  (sum, item) =>
                                                    sum + item.net_amount_cents,
                                                  0
                                                )) / 100
                                            ).toFixed(2)}
                                          </div>
                                          {familyConsolidatedBillingSummary.current_period_end !=
                                            null &&
                                            familyConsolidatedBillingSummary.current_period_end >
                                              0 && (
                                              <div className="font-normal text-blue-800 dark:text-blue-200">
                                                Next family renewal:{" "}
                                                {new Date(
                                                  familyConsolidatedBillingSummary.current_period_end *
                                                    1000
                                                ).toLocaleDateString("en-US", {
                                                  month: "short",
                                                  day: "numeric",
                                                  year: "numeric",
                                                })}
                                              </div>
                                            )}
                                          <p className="text-[10px] font-normal leading-snug text-blue-700 dark:text-blue-300">
                                            Mid-cycle membership adds charge the partial period
                                            immediately on the billing primary&apos;s card. Each
                                            renewal uses the monthly recurring total above.
                                          </p>
                                          {familyConsolidatedBillingSummary.upcoming_total_cents !=
                                            null &&
                                            familyConsolidatedBillingSummary.upcoming_includes_prorations && (
                                              <div className="font-normal text-amber-800 dark:text-amber-200">
                                                Upcoming invoice preview: $
                                                {(
                                                  familyConsolidatedBillingSummary.upcoming_total_cents /
                                                  100
                                                ).toFixed(2)}
                                                <span className="ml-1">
                                                  (includes pending proration — should clear after
                                                  the next immediate charge)
                                                </span>
                                              </div>
                                            )}
                                        </div>
                                        {isViewingFamilyBillingPrimary &&
                                          isConsolidateAllowed(
                                            CONSOLIDATE_ALLOWED_FAMILY_IDS,
                                            editingStudent?.family_id || formData.family_id
                                          ) && (
                                          <div className="border-t border-blue-200 pt-2 dark:border-blue-700">
                                            <p className="text-[10px] text-blue-700 dark:text-blue-300 mb-1.5">
                                              Migrate legacy per-student Stripe subscriptions onto
                                              one consolidated family bill.
                                            </p>
                                            <button
                                              type="button"
                                              disabled={familyBillingMigrationLoading}
                                              onClick={() => runFamilyBillingMigration()}
                                              className="rounded-md border border-blue-300 bg-white px-2 py-1 text-[10px] font-medium text-blue-800 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-100"
                                            >
                                              {familyBillingMigrationLoading
                                                ? 'Checking...'
                                                : 'Consolidate family billing'}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                )}

                                {/* Family Discount Display */}
                                {hasCapability('familyDiscounts') && (
                                <div className="mt-4">
                                  <FamilyDiscountDisplay 
                                    guardianId={editingStudent.family_id} 
                                    refreshTrigger={familyDiscountRefreshTrigger}
                                  />
                                </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Waiver Status
                      </label>
                      <div
                        className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border transition-colors duration-150 cursor-pointer select-none ${
                          formData.waiver_signed
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                        onClick={() => {
                          handleInputChange({
                            target: {
                              name: "waiver_signed",
                              value: "",
                              type: "checkbox",
                              checked: !Boolean(formData.waiver_signed),
                            },
                          } as any);
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={!!formData.waiver_signed}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            handleInputChange({
                              target: {
                                name: "waiver_signed",
                                value: "",
                                type: "checkbox",
                                checked: !Boolean(formData.waiver_signed),
                              },
                            } as any);
                        }}
                      >
                        {formData.waiver_signed ? (
                          <>
                            <span className="text-green-600 mr-2">✔️</span>
                            Waiver Signed
                          </>
                        ) : (
                          <>
                            <span className="text-gray-400 mr-2">🚫</span>
                            No Waiver Signed
                          </>
                        )}
                      </div>
                    </div>
                  </form>

                  {/* Inline Payment Form */}
                  {editingStudent && showInlinePaymentForm && (
                    <div className="mt-4">
                      <StudentPaymentMethodModal
                        open={true}
                        onClose={() => setShowInlinePaymentForm(null)}
                        studentId={editingStudent.id}
                        studioId={studioId || ""}
                        paymentMethodStudentId={
                          restrictOneTimePaymentToPrimaryCard
                            ? familyBillingPrimaryStudentId || editingStudent.id
                            : editingStudent.id
                        }
                        paymentMethodOwnerName={
                          restrictOneTimePaymentToPrimaryCard
                            ? familyBillingPrimaryName ?? undefined
                            : undefined
                        }
                        restrictToPrimaryPaymentMethod={
                          restrictOneTimePaymentToPrimaryCard
                        }
                        onSuccess={handlePaymentSuccess}
                        oneTimePayment={
                          showInlinePaymentForm === "one-time" ||
                          (typeof showInlinePaymentForm === "object" &&
                            showInlinePaymentForm.isRetry)
                        }
                        productDescriptions={productDescriptions}
                        inline={true}
                        isRetryMode={false}
                        // Pre-populate retry data
                        {...(typeof showInlinePaymentForm === "object" &&
                          showInlinePaymentForm.isRetry && {
                            retryData: {
                              amount: showInlinePaymentForm.amount,
                              description: showInlinePaymentForm.description,
                              originalPaymentId:
                                showInlinePaymentForm.originalPaymentId,
                            },
                          })}
                      />
                    </div>
                  )}

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:items-center sm:justify-between">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                        onClick={handleModalDismiss}
                      >
                        <span className="pointer-events-none">Cancel</span>
                      </button>
                      <button
                        type="button"
                        disabled={
                          isSaving || 
                          membershipRequiresPaymentMethod ||
                          (!!formData.family_id && !familyIdValidation.isValid && !familyIdValidation.isChecking)
                        }
                        className={`inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                          isSaving || 
                          membershipRequiresPaymentMethod ||
                          (formData.family_id && !familyIdValidation.isValid && !familyIdValidation.isChecking)
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-indigo-600 text-white hover:bg-indigo-500"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubmit(e as any);
                        }}
                      >
                        <span className="pointer-events-none">
                          {isSaving
                            ? "Saving..."
                            : membershipRequiresPaymentMethod
                            ? "Payment Method Required"
                            : !!formData.family_id && !familyIdValidation.isValid && !familyIdValidation.isChecking
                            ? "Invalid Family ID"
                            : editingStudent
                            ? "Update Student"
                            : "Add Student"}
                        </span>
                      </button>
                    </div>

                    {/* Payment Method Buttons (paid plans only) */}
                    {editingStudent && canUseStripeBilling && (
                      <div className="flex gap-2 mt-4 xl:mt-0">
                        {!isFamilyBilledMember ? (
                        <button
                          type="button"
                          className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                            membershipRequiresPaymentMethod
                              ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 border border-red-300 dark:border-red-700"
                              : hasPaymentMethod
                              ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-800/50"
                              : "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-800/50"
                          }`}
                          onClick={async () => {
                            // Check if Stripe is connected
                            if (!isStripeConnected) {
                              setNotification({
                                show: true,
                                message: "Please connect your account to Stripe to enable payment processing.",
                                title: "Stripe Required",
                                type: "error",
                              });
                              return;
                            }

                            // Open Stripe customer portal for this individual student (regardless of family status)
                            if (!editingStudent?.id) return;
                            try {
                              const pmRequestBody: any = {
                                student_id: editingStudent.id, // Always use individual student's ID
                                return_url: window.location.href,
                              };
                              


                              const response = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/payment/link`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${getAuthToken()}`,
                                  },
                                  body: JSON.stringify({ student_id: editingStudent.id }),
                                }
                              );
                              
                              if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.error || "Failed to open customer portal");
                              }
                              
                              const { url } = await response.json();
                              window.open(url, "_blank", "noopener,noreferrer");
                              
                              // Show notification about the portal
                              setNotification({
                                show: true,
                                message: `Opening ${editingStudent.name}'s individual payment portal. You can send this to the student to update their payment method.`,
                                title: "Payment Portal Opened",
                                type: "success",
                              });
                            } catch (error) {
                              setNotification({
                                show: true,
                                message: error instanceof Error ? error.message : "Failed to open Stripe customer portal.",
                                title: "Error",
                                type: "error",
                              });
                            }
                          }}
                        >
                          Manage Payment Method
                        </button>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 text-sm text-indigo-800 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800">
                              Billed through{" "}
                              {familyBillingPrimaryName || "the billing primary"}
                            </span>
                            <button
                              type="button"
                              className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/30 px-3 py-2 text-sm font-semibold text-indigo-700 dark:text-indigo-400 shadow-sm hover:bg-indigo-200 dark:hover:bg-indigo-800/50"
                              title={`Save a card on ${editingStudent.name}'s own profile so they can take over billing if ${
                                familyBillingPrimaryName || "the billing primary"
                              } leaves automatic billing.`}
                              onClick={async () => {
                                if (!isStripeConnected) {
                                  setNotification({
                                    show: true,
                                    message: "Please connect your account to Stripe to enable payment processing.",
                                    title: "Stripe Required",
                                    type: "error",
                                  });
                                  return;
                                }

                                if (!editingStudent?.id) return;

                                try {
                                  const response = await fetch(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/payment/link`,
                                    {
                                      method: "POST",
                                      headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${getAuthToken()}`,
                                      },
                                      body: JSON.stringify({
                                        student_id: editingStudent.id,
                                        for_family_billing_setup: true,
                                      }),
                                    }
                                  );

                                  if (!response.ok) {
                                    const errorData = await response.json().catch(() => null);
                                    throw new Error(
                                      errorData?.error || "Failed to open customer portal"
                                    );
                                  }

                                  const { url } = await response.json();
                                  window.open(url, "_blank", "noopener,noreferrer");

                                  setNotification({
                                    show: true,
                                    message: `Opening a payment portal for ${editingStudent.name}. Saving a card here lets them take over family billing, so the current billing primary can move off automatic billing.`,
                                    title: "Payment Portal Opened",
                                    type: "success",
                                  });
                                } catch (error) {
                                  setNotification({
                                    show: true,
                                    message:
                                      error instanceof Error
                                        ? error.message
                                        : "Failed to open Stripe customer portal.",
                                    title: "Error",
                                    type: "error",
                                  });
                                }
                              }}
                            >
                              Add Own Payment Method
                            </button>
                          </div>
                        )}

                        <button
                          type="button"
                          className="inline-flex items-center rounded-md bg-green-100 dark:bg-green-900/30 px-3 py-2 text-sm font-semibold text-green-700 dark:text-green-400 shadow-sm hover:bg-green-200 dark:hover:bg-green-800/50"
                          onClick={() => {
                            setPaymentStudent(editingStudent);
                            setShowPaymentTypeSelection(true);
                          }}
                        >
                          One-Time Payment
                        </button>

                        {!isFamilyBilledMember && (
                        <>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md bg-blue-100 dark:bg-blue-900/30 px-3 py-2 text-sm font-semibold text-blue-700 dark:text-blue-400 shadow-sm hover:bg-blue-200 dark:hover:bg-blue-800/50"
                          onClick={async () => {
                            if (!editingStudent?.id || !editingStudent?.email) {
                              setNotification({
                                show: true,
                                message: "Student must have an email address to send payment link.",
                                title: "Email Required",
                                type: "error",
                              });
                              return;
                            }

                            const authToken = getAuthToken();
                            if (!authToken) {
                              setNotification({
                                show: true,
                                message: "Authentication required",
                                title: "Error",
                                type: "error",
                              });
                              return;
                            }

                            const isGmailConnected = await checkGmailConfigured(authToken);
                            if (!isGmailConnected) {
                              setNotification({
                                show: true,
                                message: EMAIL_INTEGRATION_REQUIRED_MESSAGE,
                                title: "Connect Email",
                                type: "error",
                              });
                              return;
                            }

                            try {
                              const response = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/payment/link`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${getAuthToken()}`,
                                  },
                                  body: JSON.stringify({ student_id: editingStudent.id }),
                                }
                              );

                              if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.error || "Failed to get payment link");
                              }

                              const { url } = await response.json();
                              
                              // Send email with payment link
                              const emailResponse = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/emails/send`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${getAuthToken()}`,
                                  },
                                  body: JSON.stringify({
                                    studio_id: studioId,
                                    subject: "Payment Method Update Request",
                                    content: `Hello, this is ${studioName}. Can you add/update your payment method, please: ${url}`,
                                    recipients: [editingStudent.email],
                                  }),
                                }
                              );

                              if (!emailResponse.ok) {
                                const errorData = await emailResponse.json();
                                throw new Error(
                                  getPaymentLinkSendErrorMessage(errorData, 'email')
                                );
                              }

                              setNotification({
                                show: true,
                                message: `Payment link sent to ${editingStudent.email}`,
                                title: "Email Sent",
                                type: "success",
                              });
                            } catch (error) {
                              setNotification({
                                show: true,
                                message:
                                  error instanceof Error
                                    ? error.message
                                    : "Failed to send payment link email.",
                                title: "Error",
                                type: "error",
                              });
                            }
                          }}
                        >
                          Email Payment Link
                        </button>

                        <button
                          type="button"
                          className="inline-flex items-center rounded-md bg-purple-100 dark:bg-purple-900/30 px-3 py-2 text-sm font-semibold text-purple-700 dark:text-purple-400 shadow-sm hover:bg-purple-200 dark:hover:bg-purple-800/50"
                          onClick={async () => {
                            if (!editingStudent?.id || !editingStudent?.phone) {
                              setNotification({
                                show: true,
                                message: "Student must have a phone number to send payment link.",
                                title: "Phone Required",
                                type: "error",
                              });
                              return;
                            }

                            const authToken = getAuthToken();
                            if (!authToken || !studioId) {
                              setNotification({
                                show: true,
                                message: "Authentication required",
                                title: "Error",
                                type: "error",
                              });
                              return;
                            }

                            const isSmsConfigured = await checkSmsConfigured(
                              authToken,
                              studioId
                            );
                            if (!isSmsConfigured) {
                              setNotification({
                                show: true,
                                message: SMS_INTEGRATION_REQUIRED_MESSAGE,
                                title: "Connect SMS",
                                type: "error",
                              });
                              return;
                            }

                            try {
                              const response = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/payment/link`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${getAuthToken()}`,
                                  },
                                  body: JSON.stringify({ student_id: editingStudent.id }),
                                }
                              );

                              if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.error || "Failed to get payment link");
                              }

                              const { url } = await response.json();
                              
                              // Send SMS with payment link
                              const smsResponse = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/send-sms`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${getAuthToken()}`,
                                  },
                                  body: JSON.stringify({
                                    studio_id: studioId,
                                    message: `Hello, this is ${studioName}. Can you add/update your payment method, please: ${url}`,
                                    recipients: [editingStudent.id],
                                  }),
                                }
                              );

                              if (!smsResponse.ok) {
                                const errorData = await smsResponse.json();
                                throw new Error(
                                  getPaymentLinkSendErrorMessage(errorData, 'sms')
                                );
                              }

                              setNotification({
                                show: true,
                                message: `Payment link sent to ${editingStudent.phone}`,
                                title: "SMS Sent",
                                type: "success",
                              });
                            } catch (error) {
                              setNotification({
                                show: true,
                                message:
                                  error instanceof Error
                                    ? error.message
                                    : "Failed to send payment link SMS.",
                                title: "Error",
                                type: "error",
                              });
                            }
                          }}
                        >
                          SMS Payment Link
                        </button>
                        </>
                        )}

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-8">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500"
                  onClick={() => setIsExportModalOpen(false)}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Export Students
                  </h3>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Select the fields you want to include in the export:
                    </p>
                    <div className="space-y-2">
                      {exportFields.map((field, index) => (
                        <div key={field.key} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`export-${field.key}`}
                            checked={field.selected}
                            onChange={() => toggleExportField(index)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          />
                          <label
                            htmlFor={`export-${field.key}`}
                            className="ml-1 text-sm text-gray-700 dark:text-gray-300"
                          >
                            {field.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleExport}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                    >
                      <span className="pointer-events-none">Export</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsExportModalOpen(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-8">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500"
                  onClick={() => setIsImportModalOpen(false)}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Import Students
                  </h3>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Upload a CSV, Excel (.xlsx, .xls), or JSON file with your
                      student data{canUseStripeBilling ? ", or import existing customers from your Stripe account" : ""}.
                      Make sure the column headers match the fields below.
                    </p>
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Import Methods:</strong>
                        <br />• <strong>File Import:</strong>{" "}
                        {canUseStripeBilling
                          ? "Creates new Stripe customers for students with email addresses"
                          : "Adds students from your CSV, Excel, or JSON file"}
                        {canUseStripeBilling && (
                          <>
                            <br />• <strong>Stripe Import:</strong> Links existing
                            Stripe customers and their payment methods as students
                          </>
                        )}
                      </p>
                    </div>
                    <div className="mb-4 space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Instructions:
                        </h4>
                        <ul className="mt-2 list-disc list-inside text-sm text-gray-500 dark:text-gray-400 space-y-1">
                          <li>
                            Download the template file to see the required
                            format
                          </li>
                          <li>
                            Fill in your student data following the template
                          </li>
                          <li>
                            Map your file columns to the system fields below
                          </li>
                          <li>
                            Upload your file (CSV, Excel, or JSON) to import the
                            students
                          </li>
                          {canUseStripeBilling && (
                          <li>
                            Or click "Import from Stripe" to import existing
                            customers from your Stripe account as students
                          </li>
                          )}
                        </ul>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={handleDownloadTemplate}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                        >
                          <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                          <span className="pointer-events-none">Download Template</span>
                        </button>
                        {canUseStripeBilling && (
                        <button
                          type="button"
                          onClick={handleImportFromStripe}
                          disabled={isImportingFromStripe}
                          className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowUpTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                          <span className="pointer-events-none">
                            {isImportingFromStripe
                              ? "Importing..."
                              : "Import from Stripe"}
                          </span>
                        </button>
                        )}
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Upload File
                      </label>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls,.json"
                        onChange={handleFileUpload}
                        className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-indigo-50 dark:file:bg-indigo-900/30 file:text-indigo-700 dark:file:text-indigo-400
                          hover:file:bg-indigo-100 dark:hover:file:bg-indigo-800/50"
                      />
                    </div>
                    {importError && (
                      <div className="mb-4 text-sm text-red-600 dark:text-red-400">
                        {importError}
                      </div>
                    )}
                    {importSuccess !== null && (
                      <div className="mb-4 text-sm text-green-600 dark:text-green-400">
                        Successfully imported {importSuccess} students
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Field Mapping
                      </label>
                      <div className="mt-2 mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          <strong>Data Preparation Note:</strong> Data manipulation with AI tools (such as ChatGPT, Grok, or Claude) or manual data manipulation may be necessary to prepare your data for import. For example, you may need to combine separate "First Name" and "Last Name" columns into a single "Name" field. Use the "Download Template" button to get a template to use to prepare your data properly. Feel free to use AI to manipulate the data from your previous CRM with the template to ensure it can upload successfully. Use the Field Mapping section below to compare your existing data (downloaded or exported from your previous CRM) and map it correctly to DojoTrack's data mapping fields.
                        </p>
                      </div>
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          <strong>Note:</strong> Age is automatically calculated based on Date of Birth. You only need to provide the Date of Birth field.
                        </p>
                      </div>
                      <div className="mt-2 space-y-2">
                        {importFields.map((field, index) => (
                          <div key={field.key} className="flex items-center">
                            <label className="block w-1/3 text-sm text-gray-700 dark:text-gray-300">
                              {field.label}
                            </label>
                            <input
                              type="text"
                              value={field.csvColumn}
                              onChange={(e) =>
                                handleImportFieldChange(index, e.target.value)
                              }
                              placeholder="File Column Header"
                              className="block w-2/3 rounded-md border-0 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleImportClick}
                      disabled={isImporting || !importFile}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="pointer-events-none">{isImporting ? "Importing..." : "Import"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsImportModalOpen(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {studentToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Student
                  </h3>
                  <div className="mt-2">
                    {hasSubscriptionId(studentToDelete) &&
                    studentToDelete.subscription_status !== "canceled" ? (
                      <div>
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
                          Cannot delete student with active membership
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {studentToDelete.name} has an active membership that
                          must be cancelled before deletion. Would you like to
                          cancel their membership immediately?
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete {studentToDelete.name}?
                        This action cannot be undone.
                      </p>
                    )}
                  </div>
                </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                {hasSubscriptionId(studentToDelete) &&
                studentToDelete.subscription_status !== "canceled" ? (
                  <>
                    <button
                      type="button"
                      onClick={cancelDelete}
                      className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelMembership}
                      disabled
                      className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-md shadow-sm cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      <span className="pointer-events-none">Cancel Membership</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={cancelDelete}
                      className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                    <button
                      type="button"
                      onClick={confirmDelete}
                      className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <span className="pointer-events-none">Delete</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}



      {/* AI Risk Settings Modal */}
      {showDropoffRisk && isRiskSettingsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500"
                  onClick={() => setIsRiskSettingsModalOpen(false)}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Retention Risk Thresholds
                  </h3>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Customize the color coding for the Retention Risk
                      metric.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="mediumRisk"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Yellow risk starts at
                        </label>
                        <div className="relative mt-1">
                          <input
                            type="number"
                            id="mediumRisk"
                            step="1"
                            min="0"
                            max="100"
                            value={Math.round(riskThresholds.medium * 100)}
                            onChange={(e) => {
                              const newMedium =
                                parseInt(e.target.value, 10) / 100;
                              const newHigh = Math.max(
                                newMedium,
                                riskThresholds.high
                              );
                              setRiskThresholds({
                                medium: newMedium,
                                high: newHigh,
                              });
                            }}
                            className={getInputClassName(
                              "block w-full rounded-md border-gray-300 shadow-sm sm:text-sm pl-3 pr-8"
                            )}
                          />
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <span className="text-gray-500 sm:text-sm">%</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="highRisk"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Red risk starts at
                        </label>
                        <div className="relative mt-1">
                          <input
                            type="number"
                            id="highRisk"
                            step="1"
                            min="0"
                            max="100"
                            value={Math.round(riskThresholds.high * 100)}
                            onChange={(e) => {
                              const newHigh =
                                parseInt(e.target.value, 10) / 100;
                              const newMedium = Math.min(
                                newHigh,
                                riskThresholds.medium
                              );
                              setRiskThresholds({
                                medium: newMedium,
                                high: newHigh,
                              });
                            }}
                            className={getInputClassName(
                              "block w-full rounded-md border-gray-300 shadow-sm sm:text-sm pl-3 pr-8"
                            )}
                          />
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <span className="text-gray-500 sm:text-sm">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={() => setIsRiskSettingsModalOpen(false)}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                    >
                      <span className="pointer-events-none">Done</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pause/Resume Confirmation Modal */}
      {showPauseModal && pausingStudent && (() => {
        const isResuming = pausingStudent.subscription_status === 'paused';
        return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  isResuming
                    ? 'bg-green-100 dark:bg-green-900'
                    : 'bg-yellow-100 dark:bg-yellow-900'
                }`}>
                  {isResuming ? (
                    <svg className="h-6 w-6 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.25 5.653v12.694c0 .856.917 1.398 1.667.986l11.54-6.347a1.125 1.125 0 000-1.972l-11.54-6.347A1.125 1.125 0 005.25 5.653z" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                    </svg>
                  )}
                </div>
                <div className="mt-3 text-left sm:ml-4">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    {isResuming ? 'Resume Membership' : 'Pause Membership'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isResuming
                        ? `Are you sure you want to resume billing for ${pausingStudent.name}? Their membership will be active again on the normal billing schedule.`
                        : `Are you sure you want to pause the membership for ${pausingStudent.name}? The student will not be billed until you resume their membership.`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handlePauseMembership}
                  disabled={isPausing}
                  className="inline-flex w-full justify-center rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-700 sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  <span className="pointer-events-none">
                    {isPausing ? (isResuming ? 'Resuming...' : 'Pausing...') : (isResuming ? 'Resume' : 'Pause')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowPauseModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Family billing action required modal */}
      {showFamilyBillingAlertModal && editingStudent?.family_id && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-black/85 backdrop-blur-sm transition-opacity"
              onClick={dismissFamilyBillingAlertModal}
              aria-hidden="true"
            />
            <div className="relative w-full max-w-lg transform overflow-hidden rounded-lg border border-amber-300 bg-amber-50 px-4 pb-4 pt-5 text-left shadow-2xl transition-all dark:border-amber-700 dark:bg-amber-950 sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon
                    className="h-6 w-6 text-amber-600 dark:text-amber-300"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-left sm:ml-4 sm:mt-0">
                  <h3 className="text-base font-semibold leading-6 text-amber-950 dark:text-amber-50">
                    Action required: family billing needs a payment method
                  </h3>
                  <div className="mt-2 space-y-2 text-sm text-amber-900 dark:text-amber-100">
                    <p>
                      {familyConsolidatedBillingSummary?.subscription_transfer_pending ? (
                        <>The previous billing primary left this family. </>
                      ) : null}
                      {isViewingFamilyBillingPrimary ? (
                        <>
                          Add a payment method for this student before the next billing cycle so
                          the consolidated family subscription can bill correctly.
                        </>
                      ) : familyBillingPrimaryDisplayName ? (
                        <>
                          <span className="font-medium">
                            {familyBillingPrimaryDisplayName}
                          </span>{" "}
                          is the billing primary and needs a payment method before the next
                          billing cycle so the consolidated family subscription can bill
                          correctly.
                        </>
                      ) : (
                        <>
                          This family needs a billing primary with a payment method before the
                          next billing cycle so the consolidated family subscription can bill
                          correctly.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-2">
                {isViewingFamilyBillingPrimary && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!editingStudent) return;
                      dismissFamilyBillingAlertModal();
                      if (!isStripeConnected) {
                        setNotification({
                          show: true,
                          message:
                            "Please connect your account to Stripe to enable payment processing.",
                          title: "Stripe Required",
                          type: "error",
                        });
                        return;
                      }
                      await handleUpdatePaymentMethod(editingStudent);
                    }}
                    className="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 sm:w-auto"
                  >
                    Add payment method
                  </button>
                )}
                <button
                  type="button"
                  onClick={dismissFamilyBillingAlertModal}
                  className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:w-auto ${
                    isViewingFamilyBillingPrimary
                      ? "bg-white text-amber-900 ring-1 ring-inset ring-amber-300 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-50 dark:ring-amber-700 dark:hover:bg-amber-800"
                      : "bg-amber-600 text-white hover:bg-amber-700"
                  }`}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove from Family Confirmation Modal */}
      {showRemoveFromFamilyModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowRemoveFromFamilyModal(false)}
              aria-hidden="true"
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon
                    className="h-6 w-6 text-red-600 dark:text-red-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Remove from family
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Remove{" "}
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        {editingStudent?.name || "this student"}
                      </span>{" "}
                      from family{" "}
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        {(formData.family_id && formData.family_id.trim()) ||
                          editingStudent?.family_id}
                      </span>
                      ?
                    </p>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      They will switch to individual billing. Click{" "}
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        Update Student
                      </span>{" "}
                      to save this change.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-2">
                <button
                  type="button"
                  onClick={confirmRemoveFromFamily}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 sm:w-auto"
                >
                  Remove from family
                </button>
                <button
                  type="button"
                  onClick={() => setShowRemoveFromFamilyModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">Bulk Delete Students</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete {selectedStudents.size} selected student{selectedStudents.size !== 1 ? 's' : ''}? This action cannot be undone.
                    </p>
                    <div className="mt-3 max-h-32 overflow-y-auto">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Students to be deleted:</p>
                      <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                        {students
                          .filter(student => selectedStudents.has(student.id))
                          .slice(0, 10)
                          .map(student => (
                            <li key={student.id} className="flex items-center gap-2">
                              <span>• {student.name}</span>
                              {hasSubscriptionId(student) && student.subscription_status !== "canceled" && (
                                <span className="text-red-500">(Active membership)</span>
                              )}
                            </li>
                          ))}
                        {selectedStudents.size > 10 && (
                          <li className="text-gray-500">... and {selectedStudents.size - 10} more</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  type="button"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isBulkDeleting}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  <span className="pointer-events-none">{isBulkDeleting ? "Deleting..." : "Delete Students"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Adjustment Modal */}
      {showBalanceAdjustmentModal && editingStudent && (
        <BalanceAdjustmentModal
          isOpen={showBalanceAdjustmentModal}
          onClose={() => setShowBalanceAdjustmentModal(false)}
          studentId={editingStudent.id}
          studentName={editingStudent.name}
          currentBalance={creditBalance?.raw_balance || 0}
          onAdjustmentComplete={() => {
            // Refresh credit balance after adjustment
            fetchCreditBalance(editingStudent.id);
          }}
        />
      )}

      {/* Manual Payment Recording Modal */}
      {showPaymentModal && paymentStudent && (
        <RecordPaymentModal
          isOpen={showPaymentModal}
          onClose={handlePaymentModalClose}
          student={paymentStudent}
          memberships={memberships}
          getAuthToken={getAuthToken}
          onSuccess={(message) => {
            setNotification({
              show: true,
              title: 'Success',
              message: message,
              type: 'success',
            });
          }}
        />
      )}

      {/* Payment History Modal */}
      {showPaymentHistoryModal && paymentHistoryStudent && (
        <PaymentHistoryModal
          isOpen={showPaymentHistoryModal}
          onClose={handlePaymentHistoryModalClose}
          student={paymentHistoryStudent}
          getAuthToken={getAuthToken}
        />
      )}

      {/* Curriculum Progress Modal */}
      {showCurriculumModal && curriculumStudent && (
        <CurriculumProgressModal
          open={showCurriculumModal}
          onClose={() => {
            setShowCurriculumModal(false);
            setCurriculumStudent(null);
          }}
          studentId={curriculumStudent.id}
          studentName={curriculumStudent.name}
          studentBelt={curriculumStudent.belt}
        />
      )}

      {attendanceHistoryStudent && (
        <AttendanceHistoryModal
          isOpen={showAttendanceHistoryModal}
          onClose={() => {
            setShowAttendanceHistoryModal(false);
            setAttendanceHistoryStudent(null);
          }}
          studentId={attendanceHistoryStudent.id}
          studentName={attendanceHistoryStudent.name}
        />
      )}

      {/* One-Time Payment Type Selection Modal */}
      <OneTimePaymentTypeModal
        open={showPaymentTypeSelection}
        onClose={() => {
          setShowPaymentTypeSelection(false);
          setPaymentStudent(null);
        }}
        onSelectCash={() => {
          setShowPaymentTypeSelection(false);
          if (paymentStudent) {
            setOneTimeCashPaymentStudent(paymentStudent);
            setShowOneTimeCashPaymentModal(true);
          }
        }}
        onSelectCard={() => {
          setShowPaymentTypeSelection(false);
          setShowInlinePaymentForm("one-time");
        }}
      />

      {/* One-Time Cash Payment Modal */}
      {showOneTimeCashPaymentModal && oneTimeCashPaymentStudent && (
        <OneTimeCashPaymentModal
          isOpen={showOneTimeCashPaymentModal}
          onClose={() => {
            setShowOneTimeCashPaymentModal(false);
            setOneTimeCashPaymentStudent(null);
            refreshData();
          }}
          student={oneTimeCashPaymentStudent}
          productDescriptions={productDescriptions}
          getAuthToken={getAuthToken}
          onSuccess={(message) => {
            setNotification({
              show: true,
              message: message,
              title: "Payment Recorded",
              type: "success",
            });
          }}
        />
      )}

      </div>
    </div>
  );
}
